import React, { useState, useEffect, useCallback, useRef} from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { requiresCitizenship } from '../../util/citizenshipRequirement';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { userTypeOf } from '../../util/userType';
import { teamRoleOf } from '../../util/teamRole';
import { hasVerifiedCategory, hasVerifiedType, isCompanyVerified, isVerifiedRecord } from '../../util/verifiedStatus';
import { confirmationOf, currentEmployerConfirmationState } from '../../util/currentEmployerConfirmation';
import { isInvitedToPosting } from '../../util/postingInvitation';
import { publicRecommendationsOf, publicRecommendationsGivenOf } from '../../util/endorsements';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { handleGetSubscriptionPrice, initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSideNavigation,
  ResponsiveImage,
  InlineTextButton,
  LayoutSingleColumn,
  ConsultantSelectionModal,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import AddToPreferredButton, { canAddToPreferred } from '../../components/AddToPreferredButton/AddToPreferredButton';
import TellConsultantsButton from '../PreferredNetwork/TellConsultantsButton';
import TellSubSuppliersButton from '../PreferredNetwork/TellSubSuppliersButton';
import ForwardedPostingNotice from './ForwardedPostingNotice';
import { PostingVisibilityLabel } from '../PreferredNetwork/PostingVisibilityField';
import { managesNetwork } from '../PreferredNetwork/networkPages';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import cover from './img/cover.jpg';
import unverified from './img/unverified.png';

import ListingPageSkeleton from './ListingPageSkeleton';

import {
  renderKYBBusinessDetails,
  renderKYBBeneficialOwners,
  renderKYBAuthorizedPersons,
  renderKYBInsuranceAndReports,
  consolidateAddressFieldsForDisplay,
} from '../../util/kybHelpers';
import { generatePreviewSasToken, getKybPersonaChecks, getKycPersonaChecks, decryptInviteToken } from '../../util/api';
import {
  CompanyDocumentFile,
  canSeeCompanyDocuments,
  useCompanyCredentialDetails,
} from './companyCredentialAccess';
import { keepShareVisit, readShareVisit, forgetShareVisit } from './shareVisitStore';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  initiateApplyTransaction,
  initiateManualTransaction,
  requestDocumentPresentation,
  requestShareOtpDuck,
  verifyShareOtpDuck,
  getSharedProfileDuck,
  fetchShareVisitorPersonaRecords,
  fetchJobConsent,
  updateTransactionDocuments,
  payForRFPDuck,
  saveRFPPayments,
  requestProfileAccessDuck,
  shareCredentialDuck,
  requestCredentialAccessDuck,
  getCredentialAccessRequestsDuck,
  approveCredentialAccessDuck,
  rejectCredentialAccessDuck,
  resendCredentialAccessEmailDuck,
  fetchListingLinkPage,
} from './ListingPage.duck';
import { getMessagePacksOfUser, getUserProfileListing, fetchSmsBalance } from '../../ducks/user.duck';
import {
  submitCredentialShare,
  NoSmsCreditsHint,
  SharePhoneInput,
  isSharePhoneValid,
} from './credentialShareSms';
import {
  requestedCompanyFiles,
  approvalWouldShare,
  decisionErrorMessage,
  CredentialRequestFileChoices,
  CredentialRequestDetails,
  CredentialRequestActions,
  ApprovalEmailResend,
  approvalEmailFailed,
} from './credentialAccessApproval';
import ApprovalShareConsent from './ApprovalShareConsent';
import { CREDENTIAL_APPROVAL_WORDING, approvalRequesterLabel } from '../../util/consentWordings';
import { reportCredentialFailure } from './credentialTrustNotice';
import {
  heldCompanyFiles,
  offeredCompanyFiles,
  toggleCompanyFile,
  withCompanyFiles,
  dialogCanSubmit,
  CompanyFileRows,
} from './companyFileChoices';

import {
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  LoadingPage,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import verified from '../../assets/verified.png';
import preferenceNav from './img/preference.svg';
import skillNav from './img/skill.svg';
import verificationNav from './img/verification.svg';
import experiencesNav from './img/experiences.svg';
import educationsNav from './img/educations.svg';
import certificationNav from './img/certification.svg';
import rolesNav from './img/roles.svg';
import aboutNav from './img/about.svg';
import vendorshipsNav from './img/vendorships.svg';
import partnershipsNav from './img/partnerships.svg';
import servicesNav from './img/services.svg';
import projectsNav from './img/projects.svg';
import consultantsNav from './img/consultants.svg';
import registrationNav from './img/registration.svg';
import infoNav from './img/info.svg';
import insuranceNav from './img/insurance.svg';
import staffingNav from './img/staffing.svg';
import implementNav from './img/implement.svg';
import identityIcon from './img/identity.svg';
import workIcon from './img/work.svg';
import experienceIcon from './img/experience.svg';
import educationIcon from './img/education.svg';
import educationLogo from '../ProfileSettingsPage/img/college-svgrepo-com.svg';
import Profile1 from './img/profile-01.jpg';
import Profile2 from './img/profile-02.jpg';
import Profile3 from './img/profile-03.jpg';
import Profile4 from './img/profile-04.jpg';
import Profile5 from './img/profile-05.jpg';
import Profile6 from './img/profile-06.jpg';
import Profile7 from './img/profile-07.png';
import Profile8 from './img/profile-08.png';
import Profile9 from './img/profile-09.png';
import Profile10 from './img/profile-10.png';
import partnerImg1 from './img/work.svg';
import verifyIcon from './img/verified.png';
import unveriffiedIcon from './img/unverified.png';
import { getAgencyTypeDisplayLabel, getCustomerTypeDisplayLabel } from '../../config/fieldOptions';

import {
  Row,
  Col,
  Avatar,
  Tabs,
  Flex,
  Button,
  Rate,
  Anchor,
  Dropdown,
  Tooltip,
  List,
  Timeline,
  Modal,
  Space,
  Badge,
  Tag,
  message,
  Spin,
  FloatButton,
  Input,
  Card,
  Typography,
  Divider,
  Affix,
  Skeleton,
  Collapse,
  Progress,
  Pagination,
  Select,
  Avatar as AntAvatar,
  Table,
  Popconfirm,
  Checkbox,
} from 'antd';

import {
  FormOutlined,
  ContactsOutlined,
  AuditOutlined,
  FileTextOutlined,
  GlobalOutlined,
  MoreOutlined,
  ArrowRightOutlined,
  EditOutlined,
  EllipsisOutlined,
  TrophyOutlined,
  FacebookOutlined,
  InstagramOutlined,
  XOutlined,
  BehanceOutlined,
  PlusOutlined,
  ExportOutlined,
  EnvironmentOutlined,
  ProductOutlined,
  MailOutlined,
  SendOutlined,
  CommentOutlined,
  FileSearchOutlined,
  LinkOutlined,
  InfoCircleOutlined,
  ToolOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DollarCircleOutlined,
  SafetyCertificateOutlined,
  FilePdfOutlined,
  LinkedinOutlined,
  IdcardOutlined,
  LockOutlined,
  UserAddOutlined,
  TagOutlined,
  VerifiedOutlined,
  SolutionOutlined,
  DatabaseOutlined,
  UsergroupAddOutlined,
  CheckCircleOutlined,
  SecurityScanOutlined,
  BlockOutlined,
  HistoryOutlined,
  ShopOutlined,
  EyeOutlined,
  BankOutlined,
  FolderOutlined,
  TeamOutlined,
  NodeIndexOutlined,
  ProjectOutlined,
  StarOutlined,
  GiftOutlined,
  DollarOutlined,
  CarOutlined,
  EnvironmentFilled,
  CalendarOutlined,
  MobileOutlined,
  PercentageOutlined,
  PhoneOutlined,
  SettingOutlined,
  SafetyOutlined,
  FileProtectOutlined,
  DownloadOutlined,
  DownOutlined,
  UpOutlined,
  FileOutlined,
  OneToOneOutlined,
  PaperClipOutlined,
  TagsOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  MessageOutlined,
  AppstoreOutlined,
  HarmonyOSOutlined,
  CheckOutlined,
  UnorderedListOutlined,
  ShareAltOutlined,
  CloseCircleOutlined,
  NumberOutlined,
  LinkedinFilled,
  CopyOutlined,
  UnlockOutlined,
} from '@ant-design/icons';

import css from './ListingPage.module.css';
import { formatMoney } from '../../util/currency.js';
import InquiryForm from './InquiryForm/InquiryForm.js';
import { workTicksFor } from './workTicks';
import { createResourceLocatorString } from '../../util/routes.js';

import Spinner from '../../components/IconSpinner/IconSpinner.js';
import { sendInvitationToUser } from '../JobPostsPage/JobPostsPage.duck.js';
import { getAllApplyTransactions } from '../../ducks/user.duck.js';
import { searchAcceptedConsultants } from '../../ducks/linkedContacts.duck';
import { uploadBlobFile, uploadResumeBlobFile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import UploadDocumentButton from '../../components/UploadDocumentButton/UploadDocumentButton.js';
import ApplicationResumeModal from '../../components/ApplicationResumeModal/ApplicationResumeModal.js';
import {
  saveConsentFor,
  ConsentSaveFailedError,
  isConsentRequiredRefusal,
  CONSENT_SAVE_FAILED_MESSAGE_ID,
} from '../../util/actionConsent';
import { agencyApplyWithConsultant, NO_ACCEPTED_CONSULTANT } from '../../util/linkedConsultantLists';
import { documentTicks, companyTicksFor, isCompanyFieldTicked } from '../../util/fieldTicks';

import LoginPopupModal from '../../components/LoginPopupModal/LoginPopupModal';
import { verificationTagText } from '../../util/verificationTagText';
import { eadCategoriesAcceptedBy, workDocumentCodeOfName } from '../../util/workDocumentRules';
import { workProfileOfPublicData } from '../../util/workProfile';
import { offeredJobRequirements } from '../../util/unbuiltVerifications';

const { Text } = Typography;
const { Paragraph } = Typography;
const { Panel } = Collapse;

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

// Identity documents are recorded under their document name as well as their type.
const hasVerifiedDocument = (records, name) =>
  hasVerifiedType(records, name) ||
  (Array.isArray(records) && records.some(record => record?.documentName === name && isVerifiedRecord(record)));

const { UUID } = sdkTypes;
const { Money } = sdkTypes;

/**
 * Helper function to format field names for display
 * Converts field names like 'business-name' or 'business_name' to 'Business Name'
 * @param {string} fieldName - The field name to format
 * @returns {string} Formatted field name
 */
const formatFieldName = fieldName => {
  // Custom field name mappings
  const customMappings = {
    'business-tax-identification-number': 'Tax Identification number',
    'business-registration-number-type': 'Registration number type',
    documentein: 'Employee Identification number',
    docformationdate: 'Formation date',
    docdateincorporated: 'Date of Incorporation',
    docfederaltaxclassification: 'Federal tax classification',
    docissuedate: 'Issued date',
    docformid: 'Form ID',
    policy_expiry_date: 'Policy expire date',
    policy_number: 'Policy number',
    type_of_insurance: 'Type of Insurance',
    insurer_company_name: 'Insurer company name',
    docnumberofemployees: 'Employee count',
    docannualrevenue: 'Annual Revenue',
    docaddress: 'Address',
  };

  // Check if there's a custom mapping first
  if (customMappings[fieldName]) {
    return customMappings[fieldName];
  }

  // Otherwise, apply generic formatting
  return fieldName
    .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Helper function to create collapse items from personaChecks data
 * Handles both flat structure (for non-KYB) and categorized structure (for KYB)
 * Consolidates address component fields into single "Address" entries
 * @param {Object} personaChecksForDocument - PersonaChecks data for a specific document type
 * @param {Object} verified - Verified icon
 * @param {Object} unverified - Unverified icon
 * @returns {Array} Array of field items with verification status
 */
const createPersonaFieldItems = (personaChecksForDocument, verified, unverified) => {
  if (!personaChecksForDocument || typeof personaChecksForDocument !== 'object') {
    return [];
  }

  // Check if this is a categorized KYB structure
  const isCategorizedKYB =
    personaChecksForDocument.businessDetails !== undefined ||
    personaChecksForDocument.beneficialOwners !== undefined ||
    personaChecksForDocument.authorizedPersons !== undefined;

  if (isCategorizedKYB) {
    // For categorized KYB, only show business details in the flat list
    if (personaChecksForDocument.businessDetails) {
      // Consolidate address fields before creating field items
      const consolidatedFields = consolidateAddressFieldsForDisplay(personaChecksForDocument.businessDetails);

      return consolidatedFields.map(field => ({
        fieldName: field.fieldName, // Keep original field name
        displayName: formatFieldName(field.fieldName), // Formatted name for display
        isVerified: Boolean(field.isVerified),
        icon: field.isVerified ? verified : unverified,
      }));
    }
    return [];
  }

  // For non-KYB documents, use the flat structure with consolidation
  const consolidatedFields = consolidateAddressFieldsForDisplay(personaChecksForDocument);

  return consolidatedFields.map(field => ({
    fieldName: field.fieldName, // Keep original field name
    displayName: formatFieldName(field.fieldName), // Formatted name for display
    isVerified: Boolean(field.isVerified),
    icon: field.isVerified ? verified : unverified,
  }));
};

/**
 * Mapping of UI field identifiers to personaChecks field names
 * Maps the visibility settings keys to their corresponding persona check field names
 */
const FIELD_TO_PERSONA_CHECK_MAPPING = {
  // Agency/Customer Profile Name
  agencyProfileName: 'business-name',
  customerProfileName: 'business-name',

  // DBA/Alternate Name
  agencyAlternateName: 'dba-name',
  customerAlternateName: 'dba-name',

  // Country of Formation - not in persona checks, will use default
  countryOfFormation: null,

  // State of Incorporation - not in persona checks, will use default
  stateOfIncorp: null,
  statesIncorpForeign: null,

  // Address
  agencyAddress: 'docaddress',
  customerAddress: 'docaddress',

  // Date of Formation
  dateOfFormation: 'docformationdate',

  // Business Type
  typeOfBusiness: 'business-type',

  // Tax Identification
  // 'identificationType': 'business-registration-number-type',
  identificationNumber: 'business-tax-identification-number',
  // 'taxClassification': 'docfederaltaxclassification',

  // Website
  agencyWebsite: 'business-url',
  customerWebsite: 'business-url',

  // Employee Count
  employeeCount: 'docnumberofemployees',

  // Annual Revenue
  annualRevenue: 'docannualrevenue',

  // Contact Email
  agencyMail: null,
  customerMail: null,

  // Documents
  'w9FormUrl': 'documentein',
  'insuranceCertificateUrl': null, // Insurance has its own section
};

/**
 * Whether a company detail shows a verified tick. It ticks only when the business check still
 * counts as verified and the provider matched the value prefilled for that detail; a detail with
 * nothing prefilled behind it (mapped to null, or not mapped) never ticks.
 * @param {Object} personaChecks - The personaChecks, including the company's business check
 * @param {string} fieldKey - The field key (visibility settings key)
 * @param {Array} personaRecords - The company's verification records
 * @returns {boolean} Whether the field is verified
 */
const getFieldVerificationStatus = (personaChecks, fieldKey, personaRecords) => {
  if (!personaChecks || !FIELD_TO_PERSONA_CHECK_MAPPING.hasOwnProperty(fieldKey)) {
    return false;
  }

  // A field manually marked as unverified never ticks
  if (personaChecks.manualOverrides && personaChecks.manualOverrides[fieldKey] === false) {
    return false;
  }

  return isCompanyFieldTicked(
    personaRecords,
    personaChecks['Business Registry Verification (KYB)'],
    FIELD_TO_PERSONA_CHECK_MAPPING[fieldKey]
  );
};

const numOfProcessingApplications = applyTxns => {
  const activeApplications = applyTxns?.filter(
    t => t.status !== 'rejected' && t.status !== 'scheduled'
  );

  return activeApplications?.length;
};

const formatPrice = price => {
  if (!price || !price.unit_amount) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price?.currency?.toUpperCase(),
  }).format(price?.unit_amount / 100);
};

/**
 * Reusable verification tag component for displaying verification status
 * @param {boolean} isVerified - Whether the field is verified or not
 * @param {boolean} selfDeclared - Whether the field is self-declared (takes precedence over isVerified)
 * @param {string} [verifiedText='Verified'] - Custom text for verified state
 * @param {string} [notVerifiedText='Not verified'] - Custom text for not verified state
 * @param {string} [selfDeclaredText='Self declared'] - Custom text for self-declared state
 * @returns {JSX.Element} Tag component with appropriate styling
 */
const VerificationTag = ({
  isVerified,
  selfDeclared = false,
  verifiedText = 'Verified',
  notVerifiedText = 'Not verified',
  selfDeclaredText = 'Self declared',
}) => {
  if (selfDeclared) {
    return (
      <Tag icon={<UserOutlined />} variant="filled" color="cyan">
        {selfDeclaredText}
      </Tag>
    );
  }

  return isVerified ? (
    // <Tag icon={<SafetyOutlined />} color="success">
    <Tag icon={<SafetyOutlined />} color="blue">
      {verifiedText}
    </Tag>
  ) : (
    <Tag icon={<CloseCircleOutlined />} color="orange">
      {notVerifiedText}
    </Tag>
  );
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [mounted, setMounted] = useState(false);

  // Experience and Education expand/collapse states
  const [expandedExperiences, setExpandedExperiences] = useState({});
  const [expandedEducations, setExpandedEducations] = useState({});
  const [hoveredExperience, setHoveredExperience] = useState(null);
  const [hoveredEducation, setHoveredEducation] = useState(null);

  // Share Profile OTP states
  const [showShareOtpModal, setShowShareOtpModal] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareOtp, setShareOtp] = useState('');
  const [shareOtpLoading, setShareOtpLoading] = useState(false);
  const [shareOtpError, setShareOtpError] = useState(null);
  const [shareOtpVerified, setShareOtpVerified] = useState(false);
  const [sharedProfileData, setSharedProfileData] = useState(null);
  // The share visit ({ token, session }) this tab was given when its visitor entered the code.
  const [shareVisit, setShareVisit] = useState(null);

  // Request Access states
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [requestAccessData, setRequestAccessData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    message: '',
  });
  const [requestAccessLoading, setRequestAccessLoading] = useState(false);
  const [requestAccessError, setRequestAccessError] = useState(null);
  const [requestAccessType, setRequestAccessType] = useState('profile'); // 'profile' | 'vault'
  const [shareAccessType, setShareAccessType] = useState('profile'); // 'profile' | 'vault'
  const [shareRequestType, setShareRequestType] = useState('profile'); // 'profile' | 'vault'

  useEffect(() => {
    const setMountedFunc = () => {
      setMounted(true);
    };
    setMountedFunc();
  }, []);

  // Check for credentialRequest query param in URL — shows popup to profile owner
  // Server enforces that only users with the same profileListingId (or matching userId) can access
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!props?.currentUser?.id?.uuid) return; // Wait for auth to load

    const searchParams = new URLSearchParams(window.location.search);
    const credentialRequestId = searchParams.get('credentialRequest');

    if (credentialRequestId && onGetCredentialAccessRequests) {
      // Fetch the specific credential access request details
      onGetCredentialAccessRequests({ requestId: credentialRequestId })
        .then(response => {
          const requestData = response?.data;
          if (requestData && requestData.status === 'pending') {
            setCredentialAccessTickedFiles([]);
            setApprovalConsentChecked(false);
            setCredentialAccessRequest(requestData);
            setShowCredentialAccessModal(true);
          }
        })
        .catch(err => {
          console.error('Error fetching credential access request:', err);
          // Show user-friendly message if unauthorized
          if (err?.status === 403 || err?.data?.error === 'unauthorized') {
            message.error('This credential request link is not intended for your account.');
          } else if (err?.status === 401) {
            message.error('Please log in to review this credential request.');
          }
        });
    }
  }, [props?.currentUser?.id?.uuid]);

  // Check for share token in URL and auto-request OTP
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('share');

    if (token && !shareToken && !shareOtpVerified) {
      setShareToken(token);

      const requestShareCode = () => props
        .onRequestShareOtp(token)
        .then(res => {
          if (res.requestType === 'vault' && !currentUser) {
            history.push(
              createResourceLocatorString(
                'LoginPage',
                routeConfiguration,
                {},
                { from: `${location.pathname}${location.search}` }
              )
            );
            return;
          }
          setShareRequestType(res.requestType || 'profile');
          setShowShareOtpModal(true);
          message.success(`Secure Verification Code sent to ${res.email}`);
        })
         .catch(error => {
          const errorMessage = error?.error || error?.message || 'Failed to send Secure Verification Code';
          // If OTP was already sent and is still valid (e.g. user just logged in and was redirected back)
          // still show the modal so they can enter the code they already received
          if (error?.otpAlreadyActive) {
            setShareRequestType(error?.requestType || 'profile');
            setShowShareOtpModal(true);
            message.info('A Secure Verification Code was already sent to your email. Please enter it below.');
            return;
          }
          message.error(errorMessage);
          setShareOtpError(errorMessage);
        });

      // A tab that already entered this link's code keeps its visit, so a refresh needs no new code.
      // If the visit is no longer good (the link expired), it is forgotten and a code is asked for.
      const kept = readShareVisit(token);
      if (kept) {
        props
          .onGetSharedProfile(token, { shareVisit: kept.shareVisit })
          .then(profileRes => {
            setShareVisit(kept.shareVisit);
            setShareRequestType(kept.requestType);
            setShareOtpVerified(true);
            setSharedProfileData(profileRes);
          })
          .catch(() => {
            forgetShareVisit(token);
            requestShareCode();
          });
      } else {
        requestShareCode();
      }
    }
  }, [shareToken, shareOtpVerified]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    messageTxnId,
    applyTxnId,
    onInitiateApplyTransaction,
    onSaveRFPPayment,
    onInitiateManualTransaction,
    onSendInvitation,
    // applied = false,
    // applying = false,
    mainUserId,
    profileListing,
    candidatesListings = [],
    enableMessageButton,
    jobRfpListingsOfCus = [],
    currentListingAdmin,
    postingForward = null,
    onRequestDocumentPresentation,
    requestRecords = [],
    listingLinkPages = {},
    onFetchListingLinkPage,
    loading,
    acceptedConsultantsExist,
    onSearchAcceptedConsultants,
    onFetchAllApplyTxns,
    allApplyTransactions = [],
    allApplyTransactionsInProgress = false,
    subscriptionsOfUser = [],
    onPayForRFP,
    subscriptionPrices,
    getSubscriptionPrices,
    authorEmail,
    hasConsentForJob,
    fetchJobConsentInProgress,
    fetchJobConsentError,
    personaRecords = [],
    currentUserPersonaRecords = [],
    messagePacksOfUser = [],
    userProfileListing,
    integrityCheckStatus,
    fetchMessagePacksOfUser,
    fetchProfileListing,
    smsBalance,
    onFetchSmsBalance,
    onUploadBlobFile,
    onUploadResumeBlobFile,
    onFetchJobConsent,
    onUpdateTransactionDocuments,
    onRequestCredentialAccess,
    onGetCredentialAccessRequests,
    onApproveCredentialAccess,
    onRejectCredentialAccess,
    onResendCredentialAccessEmail,
    onShareCredential,
    ...restOfProps
  } = props;

  const VisibilityChip = ({ isPublic }) => (
    <Tag color={isPublic ? 'green' : ''} style={{ margin: 0 }}>
      {isPublic ? 'Public' : 'Private'}
    </Tag>
  );

  const currentUserCustomerType =
    currentUser?.attributes?.profile?.publicData?.customerProfile?.customerType ||
    profileListing?.attributes?.publicData?.customerProfile?.customerType ||
    '';

  const currentUserMainListingId = currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId;
  const isProfileComplete = currentUserMainListingId !== undefined;

  const currentUserType = userTypeOf(currentUser);
  const currentUserIsCandidate = currentUserType === 'candidate';
  const currentUserIsAgency = currentUserType === 'agency';
  const currentUserIsCustomer = currentUserType === 'customer';
  const currentUserRole = teamRoleOf(currentUser);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCase, setActiveCase] = useState('');
  const [cardDetails, setCardDetails] = useState(null);

  const [decryptedData, setDecryptedData] = useState(null);

  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedJobForInvitation, setSelectedJobForInvitation] = useState(null);
  const [invitedTo, setInvitedTo] = useState([]);
  const [inviteSending, setInviteSending] = useState(false);

  const [isUserInvited, setIsUserInvited] = useState(false);

  const [alreadyWarned, setAlreadyWarned] = useState(false);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [toSendMessage, setToSendMessage] = useState('');

  const [showRequestDocumentModal, setShowRequestDocumentModal] = useState(false);
  // Read the SMS balance when the credential dialog opens, so its SMS option can say when none is left.
  useEffect(() => {
    if (showRequestDocumentModal && onFetchSmsBalance) onFetchSmsBalance();
  }, [showRequestDocumentModal]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentName, setSelectedDocumentName] = useState('');
  const [availableFields, setAvailableFields] = useState([]); // All fields to display
  const [selectedVerifiedFields, setSelectedVerifiedFields] = useState([]); // User-selected fields
  const [offeredFiles, setOfferedFiles] = useState([]); // Company files the dialog offers
  const [tickedFiles, setTickedFiles] = useState([]); // Company files the user ticked
  const [responseByMail, setResponseByMail] = useState(false);
  const [responseByPhone, setResponseByPhone] = useState(false);
  const [consentToRequest, setConsentToRequest] = useState(false);
  const [isShareMode, setIsShareMode] = useState(false); // Track if in Share mode vs Request mode
  const [sharePhone, setSharePhone] = useState(''); // Phone number for sharing
  const [shareEmail, setShareEmail] = useState(''); // Email for sharing
  const [shareRecipientName, setShareRecipientName] = useState(''); // Recipient name for sharing
  const [showConsultantModal, setShowConsultantModal] = useState(false);
  const [localAppliedConsultants, setLocalAppliedConsultants] = useState([]);

  // Credential access request modal states (credentialVault-based flow)
  const [showCredentialAccessModal, setShowCredentialAccessModal] = useState(false);
  const [credentialAccessRequest, setCredentialAccessRequest] = useState(null);
  const [credentialAccessLoading, setCredentialAccessLoading] = useState(false);
  // The approved request whose email to the requester did not send, while the dialog offers a resend.
  const [credentialAccessEmailFailedId, setCredentialAccessEmailFailedId] = useState(null);
  const [credentialAccessTickedFiles, setCredentialAccessTickedFiles] = useState([]);
  // Whether the approver ticked the share consent in the approval dialog; unticked each time it opens.
  const [approvalConsentChecked, setApprovalConsentChecked] = useState(false);

  const [applying, setApplying] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalAgencyOnly, setLoginModalAgencyOnly] = useState(false);
  const [loginModalShowRoleSelect, setLoginModalShowRoleSelect] = useState(true);

  // Document upload state for candidate apply
  const [applyIdentityDocs, setApplyIdentityDocs] = useState([]);
  const [applyWorkAuthDocs, setApplyWorkAuthDocs] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [applicationResume, setApplicationResume] = useState(null);

  // Post-apply document upload state (separate from apply-time uploads)
  const [postApplyIdentityDocs, setPostApplyIdentityDocs] = useState([]);
  const [postApplyWorkDocs, setPostApplyWorkDocs] = useState([]);
  const [postApplyIdentityModalOpen, setPostApplyIdentityModalOpen] = useState(false);
  const [postApplyWorkModalOpen, setPostApplyWorkModalOpen] = useState(false);
  const [postApplySubmitInProgress, setPostApplySubmitInProgress] = useState({
    identity: false,
    work_auth: false,
  });

  // Document preview modal state
  const [docPreviewModal, setDocPreviewModal] = useState({
    visible: false,
    url: '',
    name: '',
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [docListModal, setDocListModal] = useState({
    visible: false,
    title: '',
    docs: [],
  });

  // Document consent modal states
  const [showDocConsentModal, setShowDocConsentModal] = useState(false);
  const [docConsentChecked, setDocConsentChecked] = useState(false);
  const [pendingApplyIsInvited, setPendingApplyIsInvited] = useState(false);
  const [pendingApplyResume, setPendingApplyResume] = useState(null);
  const [docConsentContext, setDocConsentContext] = useState({ mode: 'apply', docType: '' });
  // The upload consent an agency saved before picking a consultant; one application uses it up.
  const [agencyUploadConsentId, setAgencyUploadConsentId] = useState(null);

  const [applied, setApplied] = useState(false);
  const [hasRequestedApplyTransactions, setHasRequestedApplyTransactions] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rfpPrice, setRfpPrice] = useState(null);

  // Verification status states
  const [isIdentityVerified, setIsIdentityVerified] = useState(false);
  const [identityProgressValue, setIdentityProgressValue] = useState(0); // 0, 30, 60, or 100
  const [isWorkVerified, setIsWorkVerified] = useState(false);
  const [isExperienceVerified, setIsExperienceVerified] = useState(false);
  const [isEducationVerified, setIsEducationVerified] = useState(false);
  // A company's only evidence is its business verification, shown as one Company verified badge.
  const [isBusinessIdentityVerified, setIsBusinessIdentityVerified] = useState(false);
  // Fallback personaChecks fetched from credentialVault when listing publicData is missing them
  const [vaultPersonaChecks, setVaultPersonaChecks] = useState({});

  const [isCurrentUserIdentityVerified, setIsCurrentUserIdentityVerified] = useState(false);
  const [currentUserIdentityProgressValue, setCurrentUserIdentityProgressValue] = useState(0); // 0, 60, or 100
  const [isCurrentUserWorkVerified, setIsCurrentUserWorkVerified] = useState(false);
  const [isCurrentUserEducationVerified, setIsCurrentUserEducationVerified] = useState(false);
  const [isCurrentUserExperienceVerified, setIsCurrentUserExperienceVerified] = useState(false);

  const locationPay = useLocation();
  const searchParamsPay = new URLSearchParams(locationPay.search);

  const paymentSuccess = searchParamsPay.get('paymentSuccess');
  const type = searchParamsPay.get('type');
  const sessionId = searchParamsPay.get('session_id');

  useEffect(() => {
    getSubscriptionPrices();
  }, [getSubscriptionPrices]);

  const [expandIconPosition, setExpandIconPosition] = useState('end');
  const onPositionChange = newExpandIconPosition => {
    setExpandIconPosition(newExpandIconPosition);
  };

  // Keep expandedSections hook with other hooks so it's always called
  // (was previously declared after early returns which caused hooks order mismatch)

  const formatDate = dateString => {
    if (!dateString) return '-';
    // Handle both ISO timestamps and plain dates from listing metadata.
    const date = new Date(dateString);
    const fallbackDate = isNaN(date.getTime()) ? new Date(dateString.replace(/-/g, '/')) : date;
    if (isNaN(fallbackDate.getTime())) {
      return dateString; // Return original string if invalid
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(fallbackDate);
  };

  // Résumé-imported education/experience/current-employer dates carry a day-of-month the parser
  // invented (the source résumé only ever has month/year). Manually-entered dates for these same
  // fields do have a real day, but storage doesn't distinguish the two (both are just
  // `{ date: <ISO string> }`), so this formats every entry in these sections as month/year only,
  // accepting that real days are hidden too rather than guessing which records are which.
  const formatMonthYear = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const fallbackDate = isNaN(date.getTime()) ? new Date(dateString.replace(/-/g, '/')) : date;
    if (isNaN(fallbackDate.getTime())) {
      return dateString;
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      year: 'numeric',
    }).format(fallbackDate);
  };

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  // The company's owners, tax ID, W-9 and insurance, present only when the viewer is the company.
  const {
    listing: companyCredentialListing,
    credentialsLoaded: companyCredentialsLoaded,
  } = useCompanyCredentialDetails({ listing: currentListing, currentUser });
  const companyCredentials = companyCredentialListing?.attributes?.publicData || {};

  const hasApplyTransactionForCurrentListing =
    Array.isArray(allApplyTransactions) &&
    allApplyTransactions.some(tx => tx?.listingId === currentListing?.id?.uuid);
  const isApplyTransactionsReady =
    hasRequestedApplyTransactions && !allApplyTransactionsInProgress;
  const isApplyStatePending =
    hasRequestedApplyTransactions &&
    allApplyTransactionsInProgress &&
    !applied &&
    !applying &&
    !applyTxnId;

  const isAppliedToListing = Boolean(
    applied ||
    applying ||
    applyTxnId ||
    (isApplyTransactionsReady && hasApplyTransactionForCurrentListing)
  );

  const hasApplyTransactionForListingId = listingUuid =>
    Boolean(
      isApplyTransactionsReady &&
      listingUuid &&
      Array.isArray(allApplyTransactions) &&
      allApplyTransactions.some(tx => tx?.listingId === listingUuid)
    );

  const isProfileAppliedToListing = (listing, profileListingId) => {
    if (!profileListingId) {
      return false;
    }

    const listingUuid = listing?.id?.uuid;
    const isCurrentUserProfile = profileListingId === currentUserMainListingId;

    return isCurrentUserProfile && hasApplyTransactionForListingId(listingUuid);
  };

  const currentListingAdminProfileListingId =
    currentListingAdmin?.attributes?.profile?.publicData?.profileListingId;
  const hasCurrentListingAdminAppliedTo = listing =>
    isProfileAppliedToListing(listing, currentListingAdminProfileListingId);

  const getApplyTransactionTimestamp = transaction => {
    const timestamp = transaction?.updatedAt || transaction?.shortlistedAt || transaction?.scheduledAt || transaction?.rejectedAt || transaction?.createdAt;
    const parsed = new Date(timestamp || 0).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const relevantTransaction = Array.isArray(allApplyTransactions)
    ? allApplyTransactions
      .filter(tx => tx?.listingId === currentListing?.id?.uuid)
      .sort((a, b) => getApplyTransactionTimestamp(b) - getApplyTransactionTimestamp(a))[0] || null
    : null;

  const rawCandidateStatus = relevantTransaction?.status;
  let candidateApplicationStatus = null;
  if (['rejected', 'scheduled', 'shortlisted', 'pending'].includes(rawCandidateStatus)) {
    candidateApplicationStatus = rawCandidateStatus;
  } else if (relevantTransaction || isAppliedToListing || isApplyStatePending) {
    candidateApplicationStatus = 'pending';
  }

  useEffect(() => {
    if (!currentUserIsCandidate || !isAppliedToListing || !currentListing?.id?.uuid) {
      return;
    }

    onFetchJobConsent('IdWorkAuthDocUpload', currentListing.id.uuid);
  }, [currentUserIsCandidate, isAppliedToListing, currentListing?.id?.uuid, onFetchJobConsent]);

  const checkSubscriptionStatus = () => {
    if (!subscriptionsOfUser || !Array.isArray(subscriptionsOfUser) || subscriptionsOfUser.length === 0) {
      return { hasSubscription: false, isExpired: false, subscription: null };
    }

    const subscription = subscriptionsOfUser[0]; // Get the first subscription
    if (!subscription) {
      return { hasSubscription: false, isExpired: false, subscription: null };
    }
    const expiryDate = new Date(subscription?.expiry);
    const currentDate = new Date();
    // Check both subscriptionData.status and top-level status
    const status = subscription?.subscriptionData?.status || subscription?.status;
    const isExpired = status === 'canceled' || status === 'incomplete' || status === 'incomplete_expired' || status === 'past_due' || status === 'unpaid';
    const isCanceled = status === 'canceled';

    return {
      hasSubscription: isExpired ? false : true,
      isExpired,
      isCanceled,
      subscription,
      planType: isExpired
        ? 'n/a'
        : subscription?.type === 'monthly'
          ? 'Verified Plus Member Monthly'
          : subscription?.type === 'yearly'
            ? 'Verified Plus Member Yearly'
            : subscription?.type === 'verified-delivery-vendor-monthly'
              ? 'Verified Delivery Vendor Monthly'
              : subscription?.type === 'verified-delivery-vendor-yearly'
                ? 'Verified Delivery Vendor Yearly'
                : subscription?.type === 'verified-plus-delivery-vendor-monthly'
                  ? 'Verified Plus Delivery Vendor Monthly'
                  : 'Verified Plus Delivery Vendor Yearly',
    };
  };

  const subscriptionStatus = checkSubscriptionStatus();

  // const [rfpFreeCountStartedOneYearAgo, setRFPFreeCountStartedOneYearAgo] = useState(false);
  let rfpFreeCountPeriodExpired = false;
  let rfpFreeApplicationsCount =
    profileListing?.attributes?.publicData?.freeRFPApplicationsUsed?.count || 0;
  const rfpFreeApplicationsStartedAt = profileListing?.attributes?.publicData
    ?.freeRFPApplicationsUsed?.startedAt
    ? new Date(profileListing?.attributes?.publicData?.freeRFPApplicationsUsed?.startedAt)
    : null;

  // Determine free application limit based on subscription type
  const isMonthlyPlan = subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly';
  const isYearlyPlan = subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Yearly';
  const freeApplicationLimit = isMonthlyPlan ? 1 : 12;

  // const checkFreeRFPApplicationsStatus = (rfpFreeApplicationsStartedAt) => {
  //   if (rfpFreeApplicationsStartedAt) {
  //     const startedDate = new Date(rfpFreeApplicationsStartedAt);
  //     const now = new Date();

  //     // Calculate difference in milliseconds
  //     const diffInMs = now - startedDate;

  //     // Convert milliseconds to years (approx 365.25 days)
  //     const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25);

  //     if (diffInYears >= 1) {
  //       setRFPFreeCountStartedOneYearAgo(true);
  //     } else {
  //       setRFPFreeCountStartedOneYearAgo(false);
  //     }
  //   } else {
  //     setRFPFreeCountStartedOneYearAgo(false);
  //   }
  // };

  // checkFreeRFPApplicationsStatus(rfpFreeApplicationsStartedAt);

  if (rfpFreeApplicationsStartedAt) {
    const startedDate = rfpFreeApplicationsStartedAt;
    const now = new Date();

    // Calculate difference in milliseconds
    const diffInMs = now - startedDate;

    if (isMonthlyPlan) {
      // For monthly plans: check if 30 days have passed
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      if (diffInDays >= 30) {
        rfpFreeCountPeriodExpired = true;
      }
    } else if (isYearlyPlan) {
      // For yearly plans: check if 1 year has passed
      const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365);
      if (diffInYears >= 1) {
        rfpFreeCountPeriodExpired = true;
      }
    }
  }

  if (rfpFreeCountPeriodExpired) {
    rfpFreeApplicationsCount = 0;
  }

  const listingPublicData = currentListing?.attributes?.publicData;
  const visibilitySettings = listingPublicData?.visibilitySettings;

  // The owner's linked contacts, one page of each list at a time as read from the server: 6 per
  // page for cards and linked partners, 10 for the partner tables.
  const pageSize = 6;
  const partnerPageSize = 10;
  const linkPageOf = list => listingLinkPages?.[list] || { status: 'idle', items: [], total: 0, page: 1 };
  const linkPageFailed = list => linkPageOf(list).status === 'failed';
  const linkItems = list => (linkPageFailed(list) ? [] : linkPageOf(list).items || []);
  const showLinkPage = (list, page) =>
    onFetchListingLinkPage({
      list,
      page,
      ownerId: currentListing?.author?.id?.uuid,
      listingId: currentListing?.id?.uuid,
    });
  const linkPageFailure = list =>
    linkPageFailed(list) ? <Text type="secondary">These links could not be loaded.</Text> : null;
  const linkPager = list => {
    const shown = linkPageOf(list);
    return shown.total > pageSize ? (
      <div style={{ paddingTop: 20 }}>
        <Pagination
          align="center"
          current={shown.page}
          total={shown.total}
          pageSize={pageSize}
          onChange={page => showLinkPage(list, page)}
          showSizeChanger={false}
        />
      </div>
    ) : null;
  };
  const partnerTablePaging = list => ({
    current: linkPageOf(list).page,
    total: linkPageOf(list).total,
    pageSize: partnerPageSize,
    showSizeChanger: false,
    onChange: page => showLinkPage(list, page),
  });

  useEffect(() => {
    setHasRequestedApplyTransactions(true);
    onFetchAllApplyTxns();
  }, [onFetchAllApplyTxns]);

  useEffect(() => {
    const handleRFPApplication = async () => {
      try {
        let listingId = '';
        let isUserInvited = false;

        if (typeof localStorage !== 'undefined') {
          listingId = localStorage.getItem('rfpId');
          // Stored as text across the trip to checkout; only 'true' means invited.
          isUserInvited = localStorage.getItem('isUserInvited') === 'true';
          localStorage.removeItem('rfpId');
          localStorage.removeItem('isUserInvited');
        }

        await onSaveRFPPayment(sessionId);
        setApplied(true);
        await onInitiateApplyTransaction(listingId, isUserInvited, '', '', false, [], [], null, 'pending');
      } catch (error) {
        setApplied(false);
        message.error('Failed to send application. Please try again.');
      }
    };

    const handleRFPApplicationComplete = async (paymentSuccess, type, sessionId) => {
      await handleRFPApplication();

      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        url.search = '';
        window.location.href = url.toString();
      }
      message.success('Application sent successfully!');
    };

    if (paymentSuccess && type && sessionId) {
      handleRFPApplicationComplete(paymentSuccess, type, sessionId);

      return;
    }
  }, [paymentSuccess, type, sessionId]);

  useEffect(() => {
    const updateRFPPrice = () => {
      const subscriptionType =
        subscriptionStatus?.planType === 'Verified Delivery Vendor Monthly'
          ? 'Verified delivery vendor RFP bid price'
          : subscriptionStatus?.planType === 'Verified Delivery Vendor Yearly'
            ? 'Verified delivery vendor RFP bid price'
            : subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly'
              ? 'Verified plus delivery vendor RFP bid price'
              : 'Verified plus delivery vendor RFP bid price';
      // : subscriptionStatus?.planType === 'verified-plus-delivery-vendor-yearly' ? 'Verified plus delivery vendor yearly plan'

      setRfpPrice(
        subscriptionPrices?.prices?.find(price => price?.product?.name === subscriptionType)
      );
    };
    if (subscriptionPrices && currentUser && subscriptionStatus) {
      if (userTypeOf(currentUser) === 'agency') {
        updateRFPPrice();
      }
    }
  }, [subscriptionPrices, currentUser, subscriptionStatus]);

  useEffect(() => {
    if (
      currentUserPersonaRecords &&
      Array.isArray(currentUserPersonaRecords) &&
      currentUserPersonaRecords.length > 0
    ) {
      const verifiedCategory = categoryPrefix =>
        hasVerifiedCategory(currentUserPersonaRecords, categoryPrefix);
      const verifiedDocument = documentType =>
        hasVerifiedDocument(currentUserPersonaRecords, documentType);

      // Check verification status for each category
      // For identity, check if Driver's License OR State ID is verified (matching other users' logic)
      const hasDriversLicense = verifiedDocument("Driver's license");
      const hasStateId = verifiedDocument("State ID");
      const hasMandatoryIdentityDoc = hasDriversLicense || hasStateId;

      // Check Foreign Passport for non-US citizens
      const countryOfCitizenship = currentUser?.attributes?.profile?.publicData?.identityAndWorkAuthorizationDetails?.countryOfCitizenship;
      // Default to US citizen if country is not specified, or check if explicitly US
      const isUSCitizen = !countryOfCitizenship || countryOfCitizenship?.match(/^(United States|US|USA|United States of America)$/i);
      const hasForeignPassport = verifiedDocument("Foreign Passport");

      // Determine identity verification status (same logic as for viewing other users)
      let hasIdentityDoc = false;
      let identityVerificationProgress = 0;

      if (isUSCitizen) {
        // US Citizens: Only need Driver's License OR State ID
        hasIdentityDoc = hasMandatoryIdentityDoc;
        identityVerificationProgress = hasIdentityDoc ? 100 : 0;
      } else {
        // Non-US Citizens: Need (Driver's License OR State ID) AND Foreign Passport
        if (hasMandatoryIdentityDoc && hasForeignPassport) {
          hasIdentityDoc = true;
          identityVerificationProgress = 100;
        } else if (hasMandatoryIdentityDoc || hasForeignPassport) {
          hasIdentityDoc = false; // Partial completion doesn't count as verified
          identityVerificationProgress = 60; // Show progress
        } else {
          hasIdentityDoc = false;
          identityVerificationProgress = 0;
        }
      }

      // Set progress value state
      setCurrentUserIdentityProgressValue(identityVerificationProgress);

      // Set boolean for backward compatibility
      setIsCurrentUserIdentityVerified(hasIdentityDoc);
      setIsCurrentUserWorkVerified(verifiedCategory('work') || verifiedCategory('both'));
      setIsCurrentUserExperienceVerified(verifiedCategory('experience'));
      setIsCurrentUserEducationVerified(verifiedCategory('education'));
    }
  }, [currentUserPersonaRecords, currentUser]);

  const rfppostingitems = [
    {
      key: 'skills',
      label: 'Skills required',
      description: currentListing?.attributes?.publicData?.skills_required || '-',
      icon: <ToolOutlined />,
    },
    {
      key: 'experience',
      label: 'Experience required',
      description: currentListing?.attributes?.publicData?.experience_required || '-',
      icon: <UserOutlined />,
    },
    {
      key: 'duration',
      label: 'Expected project duration',
      description: currentListing?.attributes?.publicData?.expected_project_duration || '-',
      icon: <ClockCircleOutlined />,
    },
    {
      key: 'budget',
      label: 'Estimated Project value',
      description: currentListing?.attributes?.publicData?.estimated_project_value || '-',
      icon: <DollarCircleOutlined />,
    },
    {
      key: 'verifications',
      label: 'Supplier Verifications required',
      description: `${currentListing?.attributes?.publicData?.agency_verifications_required === 'yes'
        ? 'Yes'
        : 'No'
        }`,
      // (
      //   <div className="flex flex-wrap gap-2">
      //     <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
      //       <SafetyCertificateOutlined /> GSTIN
      //     </span>
      //     <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
      //       <SafetyCertificateOutlined /> CIN/Udyam
      //     </span>
      //     <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
      //       <SafetyCertificateOutlined /> Portfolio & References
      //     </span>
      //   </div>
      // ),
      icon: <SafetyCertificateOutlined />,
    },
    // {
    //   key: "rfp",
    //   label: "Request For Proposal document",
    //   description: (
    //     <div>
    //       <a
    //         href="#"
    //         onClick={(e) => e.preventDefault()}
    //         className="inline-flex items-center gap-2 text-blue-600 hover:underline"
    //       >
    //         <FilePdfOutlined /> RFP_v1.0.pdf (download)
    //       </a>
    //       <Paragraph type="secondary" className="mt-1 mb-0">
    //         Include timelines, milestones, deliverables, and payment terms.
    //       </Paragraph>
    //     </div>
    //   ),
    //   icon: <FilePdfOutlined />,
    // },
  ];

  const suggestionlist = [
    {
      title: 'Dock & C Ltd.',
    },
    {
      title: 'Menroe Co.',
    },
    {
      title: 'Pat n Jack',
    },
    {
      title: 'Soso Systems',
    },
  ];

  const jobpostingitems = [
    {
      key: 'title',
      label: 'Job Title',
      description: currentListing?.attributes?.title || '-',
      icon: <FileTextOutlined />,
    },
    {
      key: 'description',
      label: 'Job Description',
      description: currentListing?.attributes?.description || '-',
      icon: <InfoCircleOutlined />,
    },
    {
      key: 'skills',
      label: 'Preferred skills for the job',
      description: currentListing?.attributes?.publicData?.preferred_skills || '-',
      icon: <ToolOutlined />,
    },
    {
      key: 'experience',
      label: 'Preferred experience',
      description: currentListing?.attributes?.publicData?.preferred_experience || '-',
      icon: <UserOutlined />,
    },
    {
      key: 'location',
      label: 'Job location or timezone',
      description: 'Remote – candidates in IST ±3 hours preferred.',
      icon: <EnvironmentOutlined />,
    },
    {
      key: 'start',
      label: 'Duration to start',
      description: currentListing?.attributes?.publicData?.duration_to_start
        ? `${currentListing.attributes.publicData.duration_to_start} months`
        : '-',
      icon: <ClockCircleOutlined />,
    },
    {
      key: 'compensation',
      label: 'Compensation level',
      description: currentListing?.attributes?.publicData?.compensation_level
        ? `$ ${currentListing.attributes.publicData.compensation_level}`
        : '-',
      icon: <DollarCircleOutlined />,
    },
    {
      key: 'verifications',
      label: 'Candidate Verification required',
      description:
        currentListing?.attributes?.publicData?.candidate_verifications_required === 'yes'
          ? 'Yes'
          : 'No',
      icon: <SafetyCertificateOutlined />,
    },
    // {
    //   key: "verifications",
    //   label: "Candidate Verifications required",
    //   description: (
    //     <div className="flex flex-wrap gap-2">
    //       <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
    //         <SafetyCertificateOutlined /> ID Proof
    //       </span>
    //       <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
    //         <SafetyCertificateOutlined /> Work Authorization
    //       </span>
    //       <span className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-3 py-1 text-sm">
    //         <SafetyCertificateOutlined /> References
    //       </span>
    //     </div>
    //   ),
    //   icon: <SafetyCertificateOutlined />,
    // },
  ];

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };
  const searchParams = new URLSearchParams(location.search);

  const [expandedSections, setExpandedSections] = useState({});
  const [activeKeys, setActiveKeys] = useState([]);
  const toggleSection = sectionKey => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  useEffect(() => {
    const encryptionRequired = searchParams.get('enc');
    if (!encryptionRequired) return undefined;

    let cancelled = false;

    // Decryption happens server-side (/api/decryptInviteToken) — the AES secret
    // that encrypted this token must never be shipped to the client bundle.
    decryptInviteToken(encryptionRequired)
      .then(response => {
        if (cancelled) return;
        if (response?.data) setDecryptedData(response.data);
      })
      .catch(() => {
        // Decryption failed - ignore invalid enc param
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  useEffect(() => {
    if (
      decryptedData !== null &&
      decryptedData !== '' &&
      decryptedData !== undefined &&
      currentListing &&
      currentListing?.id &&
      currentListing?.id?.uuid
    ) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('decryptedData', JSON.stringify(decryptedData));
      }

      setDecryptedData(null);

      history.push(
        createResourceLocatorString(
          'ListingPage',
          routeConfiguration,
          { slug: listingSlug, id: currentListing?.id?.uuid },
          {}
        )
      );
    }
  }, [decryptedData, currentListing?.id?.uuid]);

  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('decryptedData');
      if (raw) {
        try {
          const invitingUser = JSON.parse(raw);
        } catch (err) {
          // Invalid JSON in localStorage - will be cleaned up below
        }
      }
      localStorage.removeItem('decryptedData');
    }
  }, []);

  useEffect(() => {
    if (currentListing?.id?.uuid && currentUser) {
      setIsUserInvited(isInvitedToPosting(currentListing, { currentUser, profileListing, mainUserId }));
    }
  }, [currentListing, currentUser, profileListing, mainUserId]);

  useEffect(() => {
    if (!alreadyWarned && mainUserId === currentListing?.attributes?.publicData?.adminId && userTypeOf(currentUser) !== 'candidate') {
      message.warning('This listing is posted by someone from your company!');
      setAlreadyWarned(true);
    }
  }, [mainUserId, currentListing, alreadyWarned]);

  const documentCategories = {
    identity: [
      {
        id: 'both-1',
        name: 'U.S. Passport',
        price: 90,
        description: 'Valid and unexpired',
      },
      {
        id: 'both-1-ppc',
        name: 'U.S. Passport Card',
        price: 90,
        description: 'Valid and unexpired',
      },
      {
        id: 'both-2',
        name: 'Permanent Resident Card (Form I-551)',
        price: 90,
        description: 'Green Card; current and older designs accepted',
      },
      {
        id: 'both-3',
        name: 'Foreign Passport + Form I-94',
        price: 90,
        description: 'I-94 must show status authorizing employment',
      },
      {
        id: 'both-4',
        name: 'Employment Authorization Document (Form I-766)',
        price: 90,
        description: 'Employment Authorization Document (EAD)',
      },
      {
        id: 'both-5',
        name: 'Passport from FSM or RMI with Form I-94',
        price: 90,
        description: 'FSM or RMI with valid I-94',
      },
      {
        id: 'identity-1',
        name: "Driver's license",
        price: 90,
        description: 'Must contain photo or identifying info',
      },
      {
        id: 'identity-2',
        name: 'State ID',
        price: 90,
        description: 'Must contain photo or identifying info',
      },
      {
        id: 'identity-4',
        name: 'Government-issued ID card (federal, state, local)',
        price: 90,
        description: 'Includes municipal IDs, government agencies',
      },
      {
        id: 'identity-5',
        name: 'School ID card',
        price: 90,
        description: 'Issued by accredited U.S. school',
      },
      {
        id: 'identity-6',
        name: "Voter's registration card",
        price: 90,
        description: 'Official voter card',
      },
      {
        id: 'identity-7',
        name: 'U.S. Military card or draft record',
        price: 90,
        description: 'Issued by U.S. Armed Forces',
      },
      {
        id: 'identity-8',
        name: "Military dependent's ID card",
        price: 90,
        description: 'For dependents of military personnel',
      },
      {
        id: 'identity-9',
        name: 'U.S. Coast Guard Merchant Mariner Card',
        price: 90,
        description: 'Merchant Marine ID',
      },
      {
        id: 'identity-10',
        name: 'Native American tribal document',
        price: 90,
        description: 'Federally recognized tribes',
      },
      {
        id: 'identity-11',
        name: "Driver's license from Canadian authority",
        price: 90,
        description: 'Issued by Canadian province or territory',
      },
      {
        id: 'identity-12',
        name: 'Under 18 alternatives (school, clinic, or daycare records)',
        price: 90,
        description: 'For minors with no other docs available',
      },
    ],
    work: [
      {
        id: 'both-1',
        name: 'U.S. Passport',
        price: 90,
        description: 'Valid and unexpired',
      },
      {
        id: 'both-1-ppc',
        name: 'U.S. Passport Card',
        price: 90,
        description: 'Valid and unexpired',
      },
      {
        id: 'both-2',
        name: 'Permanent Resident Card (Form I-551)',
        price: 90,
        description: 'Green Card; current and older designs accepted',
      },
      {
        id: 'both-3',
        name: 'Foreign Passport + Form I-94',
        price: 90,
        description: 'I-94 must show status authorizing employment',
      },
      {
        id: 'both-4',
        name: 'Employment Authorization Document (Form I-766)',
        price: 90,
        description: 'Employment Authorization Document (EAD)',
      },
      {
        id: 'both-5',
        name: 'Passport from FSM or RMI with Form I-94',
        price: 90,
        description: 'FSM or RMI with valid I-94',
      },
      {
        id: 'work-1',
        name: 'Social Security Card (unrestricted)',
        price: 90,
        description: "Not marked 'NOT VALID FOR EMPLOYMENT'",
      },
      {
        id: 'work-2',
        name: 'Certification of Birth Abroad (Forms DS-1350, FS-545, FS-240)',
        price: 90,
        description: 'Issued abroad by U.S. Department of State',
      },
      {
        id: 'work-3',
        name: 'U.S. Birth Certificate (certified copy)',
        price: 90,
        description: 'Certified by state or territory',
      },
      {
        id: 'work-4',
        name: 'Native American tribal document',
        price: 90,
        description: 'Federally recognized tribe',
      },
      {
        id: 'work-5',
        name: 'Form I-197 (U.S. Citizen Identification Card)',
        price: 90,
        description: 'Issued by DHS',
      },
      {
        id: 'work-6',
        name: 'Form I-179 (ID card for resident citizen)',
        price: 90,
        description: 'Issued by DHS',
      },
      {
        id: 'work-7',
        name: 'Employment Authorization Document issued by DHS (various visa types)',
        price: 90,
        description: 'Covers F-1, J-1, M-1, Asylees, Refugees, TPS, Parolees, H-1B, etc.',
      },
    ],
    experience: [
      { id: 'experience-1', name: 'Experience Certificate', price: 90 },
      { id: 'experience-2', name: 'Reference Letter', price: 90 },
      { id: 'experience-3', name: 'Portfolio Verification', price: 90 },
    ],
    education: [
      { id: 'education-1', name: 'Degree Certificate', price: 90 },
      { id: 'education-2', name: 'Transcript', price: 90 },
      { id: 'education-3', name: 'Academic Records', price: 90 },
    ],
  };

  // Verification status of the viewed profile, from its verification records only.
  useEffect(() => {
    const listingToUse = profileListing || currentListing;
    const hasPersonaRecords = personaRecords && Array.isArray(personaRecords) && personaRecords.length > 0;

    if (hasPersonaRecords) {
      const verifiedCategory = categoryPrefix => hasVerifiedCategory(personaRecords, categoryPrefix);
      const verifiedDocument = documentType => hasVerifiedDocument(personaRecords, documentType);

      // Check verification status for each category
      // For identity, check if Driver's License OR State ID is verified
      const hasDriversLicense = verifiedDocument("Driver's license");
      const hasStateId = verifiedDocument("State ID");
      const hasMandatoryIdentityDoc = hasDriversLicense || hasStateId;

      // Check Foreign Passport for non-US citizens
      // Get country of citizenship from the profile listing (the author's profile)
      const countryOfCitizenship = listingToUse?.attributes?.publicData?.identityAndWorkAuthorizationDetails?.countryOfCitizenship;
      // Default to US citizen if country is not specified, or check if explicitly US
      const isUSCitizen = !countryOfCitizenship || countryOfCitizenship?.match(/^(United States|US|USA|United States of America)$/i);
      const hasForeignPassport = verifiedDocument("Foreign Passport");

      // Determine identity verification status
      let hasIdentityDoc = false;
      let identityVerificationProgress = 0;

      if (isUSCitizen) {
        // US Citizens: Only need Driver's License OR State ID
        hasIdentityDoc = hasMandatoryIdentityDoc;
        identityVerificationProgress = hasIdentityDoc ? 100 : 0;
      } else {
        // Non-US Citizens: Need (Driver's License OR State ID) AND Foreign Passport
        if (hasMandatoryIdentityDoc && hasForeignPassport) {
          hasIdentityDoc = true;
          identityVerificationProgress = 100;
        } else if (hasMandatoryIdentityDoc || hasForeignPassport) {
          hasIdentityDoc = false; // Partial completion doesn't count as verified
          identityVerificationProgress = 60; // Show progress
        } else {
          hasIdentityDoc = false;
          identityVerificationProgress = 0;
        }
      }

      // Set progress value state
      setIdentityProgressValue(identityVerificationProgress);

      // Set boolean for backward compatibility
      setIsIdentityVerified(hasIdentityDoc);

      // For work authorization, at least ONE document from work category should be verified
      setIsWorkVerified(verifiedCategory('work') || verifiedCategory('both'));

      setIsExperienceVerified(verifiedCategory('experience'));
      setIsEducationVerified(verifiedCategory('education'));

      setIsBusinessIdentityVerified(isCompanyVerified(personaRecords));
    } else {
      setIsBusinessIdentityVerified(false);
    }
  }, [personaRecords, profileListing, userProfileListing, currentListing, currentUser]);

  // Work authorization field-level checks: the candidate's own record, shown to them alone. Worked
  // out on every render from the listing on screen, so ticks never carry over from another profile.
  const workChecks = workTicksFor(currentListing, userProfileListing || profileListing);

  // The candidate's type and EAD category, read the way the server reads them when it judges their
  // Integrity Check.
  const listingWorkProfile = workProfileOfPublicData(listingPublicData);
  const listingWorkAuthType = listingWorkProfile.workAuthType;

  // The EAD category is shown for every work-auth type that accepts an EAD. Where the candidate's
  // verified EAD is what their check rests on, and the integrity check status does not say the
  // card's category was read and matched, the category is only what the candidate picked, and is
  // marked as not confirmed by the card. A candidate whose check rests on another document, such
  // as a refugee's Social Security card, holds no card to confirm it.
  const showsEadCategory =
    eadCategoriesAcceptedBy(listingWorkAuthType) !== null ||
    listingWorkAuthType === 'Employment Authorization Document' ||
    !!listingWorkAuthType?.includes?.('EAD');
  const holdsVerifiedEad =
    Array.isArray(personaRecords) &&
    personaRecords.some(record => workDocumentCodeOfName(record?.documentName) === 'ead' && isVerifiedRecord(record));
  const eadCategoryNotConfirmed =
    showsEadCategory &&
    holdsVerifiedEad &&
    !!listingWorkProfile.eadCategory &&
    integrityCheckStatus?.eadCategoryConfirmed !== true;
  const eadCategoryNote = eadCategoryNotConfirmed ? (
    <FormattedMessage id="ListingPage.eadCategoryNotConfirmed" />
  ) : null;

  // Fetch the KYB personaChecks from the credentialVault whenever a kyb-1 record exists in
  // personaRecords (any status). The listing's public data holds only the verification flags,
  // not the business and person details, so the vault is always where those come from.
  useEffect(() => {
    const listingId = currentListing?.id?.uuid;
    if (!listingId) return;
    const hasKybRecord = personaRecords?.some(
      r => r.documentType === 'kyb-1' || r.documentName === 'kyb-1'
    );
    if (!hasKybRecord) return;
    getKybPersonaChecks({ profileListingId: listingId })
      .then(res => {
        if (res?.personaChecks) {
          setVaultPersonaChecks(prev => ({ ...prev, ...res.personaChecks }));
        }
      })
      .catch(() => { }); // Non-fatal
  }, [personaRecords, currentListing?.id?.uuid]);

  // Fetch KYC personaChecks from credentialVault when listing publicData is missing them.
  // Mirrors the KYB fallback above but covers all non-KYB credential documents
  // (identity, work, etc.). Triggers whenever a non-KYB record exists whose
  // personaChecks entry in publicData is absent or empty.
  useEffect(() => {
    const listingId = currentListing?.id?.uuid;
    if (!listingId) return;
    const currentPersonaChecks = currentListing?.attributes?.publicData?.personaChecks || {};
    const nonKybRecords = personaRecords?.filter(
      r => r.documentName && r.documentType !== 'kyb-1' && r.documentName !== 'kyb-1'
    );
    if (!nonKybRecords?.length) return;
    const hasMissingKycChecks = nonKybRecords.some(r => {
      const checks = currentPersonaChecks[r.documentName];
      return !checks || Object.keys(checks).length === 0;
    });
    if (!hasMissingKycChecks) return;
    const lookup = shareVisit
      ? getKycPersonaChecks({ profileListingId: listingId }, { shareVisit })
      : getKycPersonaChecks({ profileListingId: listingId });
    lookup
      .then(res => {
        if (res?.personaChecks) {
          setVaultPersonaChecks(prev => ({ ...prev, ...res.personaChecks }));
        }
      })
      .catch(() => { }); // Non-fatal
  }, [personaRecords, currentListing?.id?.uuid, shareVisit]);

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;
  const listingLoaded =
    currentListing && currentListing.attributes && currentListing.attributes.state;
  // Avoid rendering the full page until the component has mounted on the client.
  // This prevents an initial broken UI/hydration flash before client-side data and effects run.
  // Exception: if listing data is already loaded (SSR), render correct meta tags for social crawlers.
  if (!mounted) {
    if (listingLoaded) {
      const { title: ssrTitle = '', description: ssrDescriptionRaw = '', price: ssrPrice = null } = currentListing.attributes;
      const ssrDescription = ssrDescriptionRaw?.trim() || 'The trust layer for IT Hiring and Procurement';
      const { formattedPrice: ssrFormattedPrice } = priceData(ssrPrice, config.currency, intl);
      const ssrSchemaTitle = intl.formatMessage(
        { id: 'ListingPage.schemaTitle' },
        { title: ssrTitle, price: ssrFormattedPrice, marketplaceName: config.marketplaceName }
      );
      return (
        <Page title={ssrSchemaTitle} scrollingDisabled={scrollingDisabled} description={ssrDescription}>
          {topbar}
          <p className={css.loadingText}>
            <FormattedMessage id="ListingPage.loadingListingMessage" />
          </p>
        </Page>
      );
    }
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  if (loading || !listingLoaded) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (showListingError && showListingError.status === 404 && !loading) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError && !loading) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing = !currentUser
    ? null
    : userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const isOwnProfile =
    currentListing?.author?.id?.uuid == currentUser?.id?.uuid ||
    currentListing?.id?.uuid == currentUser?.attributes?.profile?.publicData?.firstListingId ||
    currentListing?.id?.uuid == currentUser?.attributes?.profile?.publicData?.profileListingId;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!listingType) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing === true && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const listingAuthorType = userTypeOf(ensuredAuthor);
  const listingIsCandidate = listingAuthorType === 'candidate';
  const listingIsAgency = listingAuthorType === 'agency';
  const listingIsCustomer = listingAuthorType === 'customer';
  const recommendationsReceived = publicRecommendationsOf(ensuredAuthor, currentListing);
  const recommendationsGiven = publicRecommendationsGivenOf(ensuredAuthor, currentListing);
  // Whether the employer the candidate names now confirmed it, as the server recorded it.
  const isCurrentEmployerConfirmed =
    currentEmployerConfirmationState(
      currentListing?.attributes?.publicData?.currentEmployer,
      confirmationOf(currentListing) || confirmationOf(ensuredAuthor)
    ) === 'confirmed';
  const currentEmployerConfirmationMessageId = isCurrentEmployerConfirmed
    ? 'CurrentEmployer.confirmed'
    : 'CurrentEmployer.unconfirmed';
  const listingIsJob = currentListing?.attributes?.publicData?.listingType === 'job_listing';
  const listingIsRFP = currentListing?.attributes?.publicData?.listingType === 'rfp_listing';

  const listingAgencyType =
    currentListingAdmin?.attributes?.profile?.publicData?.agencyProfile?.agencyType || '';

  const appliedTransactionsForListing = Array.isArray(allApplyTransactions)
    ? allApplyTransactions.filter(tx => tx?.listingId === currentListing?.id?.uuid)
    : [];
  const appliedTransactionById = applyTxnId
    ? appliedTransactionsForListing.find(tx => tx?.transactionId === applyTxnId)
    : null;
  const appliedTransaction = appliedTransactionById ||
    appliedTransactionsForListing
      .slice()
      .sort((a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0))
      .pop();
  const appliedTransactionId = applyTxnId || appliedTransaction?.transactionId || '';
  const appliedIdentityDocs = appliedTransaction?.metadata?.identityDocuments || [];
  const appliedWorkDocs = appliedTransaction?.metadata?.workAuthorizationDocuments || [];
  const appliedResume = appliedTransaction?.metadata?.applicationResume || null;
  const showPostApplyIdentityUpload =
    postApplyIdentityModalOpen || appliedIdentityDocs.length === 0;
  const showPostApplyWorkUpload = postApplyWorkModalOpen || appliedWorkDocs.length === 0;
  const postApplyIdentityHasUploads = postApplyIdentityDocs.some(doc => doc?.url);
  const postApplyIdentityHasPending = postApplyIdentityDocs.some(
    doc => doc?.status === 'uploading'
  );
  const postApplyWorkHasUploads = postApplyWorkDocs.some(doc => doc?.url);
  const postApplyWorkHasPending = postApplyWorkDocs.some(doc => doc?.status === 'uploading');
  const postApplyIdentitySubmitDisabled =
    postApplySubmitInProgress.identity ||
    !postApplyIdentityHasUploads ||
    postApplyIdentityHasPending;
  const postApplyWorkSubmitDisabled =
    postApplySubmitInProgress.work_auth || !postApplyWorkHasUploads || postApplyWorkHasPending;

  const formatResumeNameForDisplay = value => {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const firstUnderscoreIndex = value.indexOf('_');
    return firstUnderscoreIndex > -1 ? value.slice(firstUnderscoreIndex + 1) : value;
  };

  const formatDocumentNameForDisplay = value => {
    if (!value || typeof value !== 'string') {
      return 'Document';
    }

    const firstUnderscoreIndex = value.indexOf('_');
    return firstUnderscoreIndex > -1 ? value.slice(firstUnderscoreIndex + 1) : value;
  };

  const profileResume = (() => {
    const profilePublicData = currentUser?.attributes?.profile?.publicData || {};
    const profileListingPublicData = profileListing?.attributes?.publicData || {};
    const resumeUrl = profilePublicData?.resumeUrl || profileListingPublicData?.resumeUrl;

    if (!resumeUrl) {
      return null;
    }

    return {
      url: resumeUrl,
      name: profilePublicData?.resumeName || profileListingPublicData?.resumeName || 'Profile Resume',
      uploadedAt: profilePublicData?.resumeUploadedAt || profileListingPublicData?.resumeUploadedAt || null,
      source: 'profileResume',
    };
  })();

  const selectedResumeForApplication = applicationResume?.url
    ? { ...applicationResume, source: 'applicationUpload' }
    : profileResume;

  const selectedResumeDisplayName = formatResumeNameForDisplay(
    selectedResumeForApplication?.name || 'Resume'
  );

  const resumeToView = appliedResume?.url ? appliedResume : selectedResumeForApplication;
  const resumeViewName = resumeToView?.name || selectedResumeDisplayName || 'Resume';

  const handleApplyResumeUpload = async file => {
    const uploader = onUploadResumeBlobFile || onUploadBlobFile;
    if (typeof uploader !== 'function') {
      message.error('Resume upload is currently unavailable.');
      return false;
    }

    const allowedTypes = ['.pdf', '.txt'];
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

    if (!allowedTypes.includes(fileExtension)) {
      message.error('Only PDF and TXT files are allowed for resumes.');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Resume file must be smaller than 5MB.');
      return false;
    }

    setResumeUploading(true);
    try {
      const tempId = `${file.name}_${Date.now()}`;
      const responseData = await uploader({
        id: tempId,
        file,
        userId: currentUser?.id?.uuid,
        candidateListingId: currentUserMainListingId || '',
      });

      if (!responseData?.blobUrl) {
        throw new Error('No resume URL returned');
      }

      setApplicationResume({
        url: responseData.blobUrl,
        name: responseData.fileName || file.name,
        blobName: responseData.blobName || '',
        uploadedAt: new Date().toISOString(),
        source: 'applicationUpload',
      });

      message.success('Resume selected for this application.');
    } catch (error) {
      message.error('Resume upload failed. Please try again.');
    } finally {
      setResumeUploading(false);
    }

    return false;
  };

  const handlePostApplyUploadStart = docType => {
    const existingDocs =
      docType === 'identity'
        ? appliedIdentityDocs
        : docType === 'work_auth'
          ? appliedWorkDocs
          : [];

    if (existingDocs.length > 0) {
      message.info('A document is already attached for this category.');
      return false;
    }

    if (!currentListing?.id?.uuid) {
      message.error('Unable to start upload for this listing.');
      return false;
    }

    if (!appliedTransactionId) {
      message.error('No application transaction found for this listing.');
      return false;
    }

    if (fetchJobConsentInProgress) {
      message.info('Checking document consent status...');
      return false;
    }

    if (!hasConsentForJob) {
      setDocConsentChecked(false);
      setDocConsentContext({ mode: 'post-apply', docType });
      setShowDocConsentModal(true);
      return false;
    }

    return true;
  };

  const setPostApplyIdentityDocsWithRef = updater => {
    setPostApplyIdentityDocs(prev =>
      typeof updater === 'function' ? updater(prev) : updater
    );
  };

  const setPostApplyWorkDocsWithRef = updater => {
    setPostApplyWorkDocs(prev => (typeof updater === 'function' ? updater(prev) : updater));
  };

  const handlePostApplyIdentityModalChange = nextOpen => {
    if (!nextOpen) {
      setPostApplyIdentityDocs([]);
    }
    setPostApplyIdentityModalOpen(nextOpen);
  };

  const handlePostApplyWorkModalChange = nextOpen => {
    if (!nextOpen) {
      setPostApplyWorkDocs([]);
    }
    setPostApplyWorkModalOpen(nextOpen);
  };

  const createPostApplyUploader = docType => async ({ id, file }) => {
    if (!appliedTransactionId) {
      message.error('No application transaction found for this listing.');
      throw new Error('Missing application transaction');
    }

    const responseData = await onUploadBlobFile({ id, file });
    if (!responseData?.blobUrl) {
      throw new Error('No blobUrl returned');
    }

    const normalizedDoc = {
      url: responseData.blobUrl,
      name: responseData.fileName || file.name,
      blobName: responseData.blobName || '',
      uploadedAt: new Date().toISOString(),
    };

    const existingDocs =
      docType === 'identity'
        ? appliedIdentityDocs
        : docType === 'work_auth'
          ? appliedWorkDocs
          : [];

    const alreadyAttached = existingDocs.some(doc =>
      (doc?.blobName && doc.blobName === normalizedDoc.blobName) ||
      (doc?.url && doc.url === normalizedDoc.url)
    );
    if (alreadyAttached) {
      message.info('This document is already attached.');
      throw new Error('Document already attached');
    }

    return responseData;
  };

  const handlePostApplySubmit = async docType => {
    if (!appliedTransactionId) {
      message.error('No application transaction found for this listing.');
      return false;
    }

    const stagedDocs = docType === 'identity' ? postApplyIdentityDocs : postApplyWorkDocs;
    const docsToAttach = (stagedDocs || []).filter(doc => doc?.url);
    if (docsToAttach.length === 0) {
      message.error('Upload at least one document before submitting.');
      return false;
    }

    const existingDocs =
      docType === 'identity'
        ? appliedIdentityDocs
        : docType === 'work_auth'
          ? appliedWorkDocs
          : [];
    const newDocs = docsToAttach.filter(doc =>
      !existingDocs.some(existing =>
        (existing?.blobName && existing.blobName === doc?.blobName) ||
        (existing?.url && existing.url === doc?.url)
      )
    );

    if (newDocs.length === 0) {
      message.info('No new documents to submit.');
      return false;
    }

    setPostApplySubmitInProgress(prev => ({ ...prev, [docType]: true }));
    try {
      for (const doc of newDocs) {
        await onUpdateTransactionDocuments(
          appliedTransactionId,
          {},
          {
            manualTransaction: true,
            documentType: docType,
            document: {
              url: doc.url,
              name: doc.name,
              blobName: doc.blobName || '',
              uploadedAt: doc.uploadedAt || new Date().toISOString(),
            },
          }
        );
      }

      message.success('Documents submitted successfully.');
      onFetchAllApplyTxns();

      if (docType === 'identity') {
        setPostApplyIdentityDocs([]);
      } else if (docType === 'work_auth') {
        setPostApplyWorkDocs([]);
      }

      return true;
    } catch (error) {
      message.error('Failed to submit documents. Please try again.');
      return false;
    } finally {
      setPostApplySubmitInProgress(prev => ({ ...prev, [docType]: false }));
    }
  };

  const isBlobStorageUrl = (url = '') => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    try {
      const parsed = new URL(url, window.location.origin);
      return /blob\.core\.windows\.net$/i.test(parsed.hostname);
    } catch (error) {
      return false;
    }
  };

  const handleOpenPreview = async (url, name) => {
    setPreviewLoading(true);
    setDocPreviewModal({ visible: true, url: '', name });

    try {
      // The server signs a preview only for a link that already grants read, so send the link itself.
      const response = await generatePreviewSasToken({ url });
      const freshPreviewUrl = response?.previewUrl || url;

      setDocPreviewModal({ visible: true, url: freshPreviewUrl, name });
    } catch (error) {
      setDocPreviewModal({ visible: true, url, name });
    } finally {
      setPreviewLoading(false);
    }
  };

  const openDocListModal = (title, docs) => {
    setDocListModal({
      visible: true,
      title,
      docs: Array.isArray(docs) ? docs : [],
    });
  };

  // Determine if the current viewer is an external user (not logged in)
  // This is used to control field visibility - external users see only public fields,
  // while logged-in users see all fields with visibility tags
  // If OTP is verified, treat as non-external user to show private details
  const isExternalUser = !currentUser && !shareOtpVerified;

  // A company's preferred lists show on its profile only when their switch is on, to every visitor
  // signed in or not; the company itself always sees its own.
  const showsPreferredList = key => isOwnProfile || visibilitySettings?.[key] === true;

  // A verified share visitor sees the contact details the shared profile carries, whatever the
  // candidate set Private.
  const sharedContact = shareOtpVerified ? sharedProfileData?.contact : null;

  // Credential vault is visible only to the candidate themselves, a verified share visitor, or a
  // customer to whom the candidate's verified details are open: the customer holds a check of their
  // own on the candidate and a check on the candidate is Verified. A server that does not say so
  // reads as before: open once the customer's own check is Verified.
  const integrityDetailsOpen =
    typeof integrityCheckStatus?.detailsOpen === 'boolean'
      ? integrityCheckStatus.detailsOpen
      : integrityCheckStatus?.uiStatus === 'COMPLETED';
  const showCredentialVault =
    isOwnProfile || (currentUserIsCustomer && integrityDetailsOpen) || shareOtpVerified;

  const toEmail = currentListingAdmin?.attributes?.email;
  const toUserId = currentListingAdmin?.id?.uuid;
  const invitedUserName = currentListingAdmin?.attributes?.profile?.displayName;
  const toUserType = listingIsCandidate
    ? 'candidate'
    : listingAgencyType === 'Staffing Services'
      ? 'staffingProvider'
      : listingAgencyType === 'Implementation Service'
        ? 'implementationService'
        : listingAgencyType === 'Staffing and Implementation Services'
          ? 'staffingAndImplementationServices'
          : 'candidate';
  const toUserProfileListingId =
    currentListingAdmin?.attributes?.profile?.publicData?.profileListingId ||
    currentListingAdmin?.attributes?.profile?.publicData?.firstListingId;

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing === true || isCurrentlyClosed) {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const items = [
    {
      key: '1',
      label: <span>Manage Team</span>,
    },
    {
      key: '2',
      label: <span>Select Persona</span>,
    },
    {
      key: '3',
      label: <span>iParley Wallet</span>,
    },
    {
      key: '4',
      label: <span>Messages</span>,
    },
    {
      key: '5',
      label: <span>RFP Submissions</span>,
    },
  ];

  const showModal = caseId => {
    setActiveCase(caseId);
    setIsModalOpen(true);
  };

  const caseContentMap = {
    modalContact: {
      modalTitle: 'Contact Info',
      content: (
        <>
          <div className={css.formRow} style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Phone number
            </label>
            <span className={css.contactPhoneNumber} style={{ fontSize: '14px' }}>
              {currentListing?.attributes?.publicData?.phoneNumber || sharedContact?.phoneNumber || '-'}
            </span>
          </div>
          <div className={css.formRow}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Email
            </label>
            <span className={css.contactEmail} style={{ fontSize: '14px' }}>
              {authorEmail || sharedContact?.email || '-'}
            </span>
          </div>
        </>
      ),
    },
    modalCurrentEmp: {
      modalTitle: 'Current Employer',
      content: (
        <>
          {listingIsCandidate && (
            <div style={{ padding: '10px 0' }}>
              {currentListing?.attributes?.publicData?.currentEmployer?.company?.label ? (
                <>
                  <div className={css.formRow} style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '14px' }}>
                      <strong>Self-employed or 100% owned entity:</strong>{' '}
                      {currentListing?.attributes?.publicData?.currentEmployerSelfOrNot || '-'}
                    </span>
                  </div>

                  <div className={css.formRow} style={{ marginBottom: '10px' }}>
                    <span style={{ fontSize: '14px' }}>
                      <strong>Company:</strong>{' '}
                      {currentListing?.attributes?.publicData?.currentEmployer?.company?.id ? (
                        <a
                          href={`/l/${currentListing?.attributes?.publicData?.currentEmployer?.company?.listingName}/${currentListing?.attributes?.publicData?.currentEmployer?.company?.listingId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#0e9941' }}
                        >
                          {currentListing?.attributes?.publicData?.currentEmployer?.company?.label}
                        </a>
                      ) : (
                        currentListing?.attributes?.publicData?.currentEmployer?.company?.label
                      )}
                    </span>
                  </div>

                  {currentListing?.attributes?.publicData?.currentEmployer?.startDate?.date && (
                    <div className={css.formRow} style={{ marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px' }}>
                        <strong>Start Date:</strong>{' '}
                        {formatMonthYear(
                          currentListing?.attributes?.publicData?.currentEmployer?.startDate?.date
                        )}
                      </span>
                    </div>
                  )}

                  {/* Title, Description & Skills Used */}
                  {currentListing?.attributes?.publicData?.currentEmployer?.title && (
                    <div className={css.formRow} style={{ marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px' }}>
                        <strong>Title:</strong>{' '}
                        {currentListing?.attributes?.publicData?.currentEmployer?.title}
                      </span>
                    </div>
                  )}

                  {currentListing?.attributes?.publicData?.currentEmployer?.description && (
                    <div className={css.formRow} style={{ marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px' }}>
                        <strong>Description:</strong>{' '}
                        {currentListing?.attributes?.publicData?.currentEmployer?.description}
                      </span>
                    </div>
                  )}

                  {currentListing?.attributes?.publicData?.currentEmployer?.skillsUsed && (
                    <div className={css.formRow} style={{ marginBottom: '10px' }}>
                      <span style={{ fontSize: '14px' }}>
                        <strong>Skills Used:</strong>
                      </span>
                      <div
                        style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}
                      >
                        {Array.isArray(currentListing?.attributes?.publicData?.currentEmployer?.skillsUsed) &&
                          currentListing.attributes.publicData.currentEmployer.skillsUsed.map(
                            (skill, index, arr) => (
                              <React.Fragment key={index}>
                                <Tag style={{ fontSize: '11px', marginBottom: '4px' }}>
                                  {typeof skill === 'string' ? skill.trim() : String(skill || '')}
                                </Tag>
                                {/* {index < arr.length - 1 && ', '} */}
                              </React.Fragment>
                            )
                          )}
                      </div>
                    </div>
                  )}

                  {/* Show additional details if company is not registered */}
                  {!currentListing?.attributes?.publicData?.currentEmployer?.company?.id && (
                    <>
                      {currentListing?.attributes?.publicData?.currentEmployer?.website && (
                        <div className={css.formRow} style={{ marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px' }}>
                            <strong>Company Website:</strong>{' '}
                            <a
                              href={
                                currentListing?.attributes?.publicData?.currentEmployer?.website
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#0e9941' }}
                            >
                              {currentListing?.attributes?.publicData?.currentEmployer?.website}
                            </a>
                          </span>
                        </div>
                      )}

                      {currentListing?.attributes?.publicData?.currentEmployer?.poc && (
                        <div className={css.formRow} style={{ marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px' }}>
                            <strong>Point of Contact:</strong>{' '}
                            {currentListing?.attributes?.publicData?.currentEmployer?.poc}
                          </span>
                        </div>
                      )}

                      {currentListing?.attributes?.publicData?.currentEmployer?.pocEmail && (
                        <div className={css.formRow} style={{ marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px' }}>
                            <strong>POC Email:</strong>{' '}
                            {currentListing?.attributes?.publicData?.currentEmployer?.pocEmail}
                          </span>
                        </div>
                      )}

                      {currentListing?.attributes?.publicData?.currentEmployer?.pocPhoneNumber && (
                        <div className={css.formRow} style={{ marginBottom: '10px' }}>
                          <span style={{ fontSize: '14px' }}>
                            <strong>POC Phone Number:</strong>{' '}
                            {
                              currentListing?.attributes?.publicData?.currentEmployer
                                ?.pocPhoneNumber
                            }
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Linked Partners Section */}
                  {(linkItems('recruiters').length > 0 || linkPageFailed('recruiters')) && (
                    <div className={css.formRow} style={{ marginTop: '20px' }}>
                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
                        Linked Suppliers
                      </label>
                      {linkPageFailure('recruiters')}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {linkItems('recruiters').map((recruiter, index) => {
                          const recruiterId = recruiter?.recruiterId;
                          const recruiterName = recruiter?.recruiterName;
                          const recruiterEmail = recruiter?.recruiterEmail || '-';
                          const recruiterListingId = recruiter?.recruiterListingId;
                          const recruiterListingName = recruiter?.recruiterListingName || '';
                          const alternateName = recruiter?.recruiterAlternateName || '';
                          const profileImage = recruiter?.profileImage || null;

                          // Get initials for avatar
                          const getInitials = name => {
                            if (!name) return 'N/A';
                            return name
                              .split(' ')
                              .map(word => word[0])
                              .join('')
                              .toUpperCase()
                              .substring(0, 2);
                          };

                          return (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                border: '1px solid #e8e8e8',
                                borderRadius: '8px',
                                backgroundColor: '#fafafa',
                              }}
                            >
                              <AntAvatar
                                size={46}
                                src={profileImage}
                                style={
                                  !profileImage
                                    ? {
                                      backgroundColor: '#0e9941',
                                      fontSize: '20px',
                                      fontWeight: 600,
                                      marginRight: '12px',
                                    }
                                    : { marginRight: '12px' }
                                }
                              >
                                {!profileImage && getInitials(recruiterName)}
                              </AntAvatar>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 500, fontSize: '14px' }}>
                                  {recruiterName}
                                </div>
                                {alternateName && (
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {alternateName}
                                  </div>
                                )}
                              </div>
                              {recruiterListingId && (
                                <a
                                  // href={`/l/${recruiterListingName}/${recruiterListingId}`}
                                  href={`/l/${recruiterListingId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ marginLeft: '12px', color: '#0e9941', fontSize: '14px' }}
                                >
                                  <EyeOutlined /> View Profile
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {linkPager('recruiters')}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
                  <p>No current employer information available.</p>
                </div>
              )}
            </div>
          )}
        </>
      ),
    },
    modalResume: {
      modalTitle: 'Resume',
      content: (
        <>
          <div className={css.formRow}>
            {listingIsCandidate && ensuredAuthor?.attributes?.profile?.publicData?.resumeName && (
              <a href={ensuredAuthor?.attributes?.profile?.publicData?.resumeUrl} target="_blank">
                {ensuredAuthor?.attributes?.profile?.publicData?.resumeName
                  ?.split('_')
                  ?.slice(1)
                  ?.join('_') || ensuredAuthor?.attributes?.profile?.publicData?.resumeName}
              </a>
            )}
          </div>
        </>
      ),
    },
    showServiceCard: {
      modalTitle: <>{cardDetails?.name}</>,
      content: (
        <div>
          <p>{cardDetails?.description}</p>
        </div>
      ),
    },
  };

  const AVATAR_IMAGE_VARIANTS = [
    // 40x40
    'square-xsmall',

    // 80x80
    'square-xsmall2x',

    // 240x240
    'square-small',

    // 480x480
    'square-small2x',
  ];
  const AVATAR_SIZES = '40px';

  const { modalTitle, content } = caseContentMap[activeCase] || { modalTitle: '', content: '' };

  const personaChecks = currentListing?.attributes?.publicData?.personaChecks || {};
  // Merge: vault KYB data takes precedence (credentialVault is source of truth for KYB)
  const effectivePersonaChecks = { ...personaChecks, ...vaultPersonaChecks };
  // The company's checks as they may be shown: none unless the company still counts as verified,
  // and then only the fields prefilled into its check.
  const kybDisplayChecks = {
    ...effectivePersonaChecks,
    'Business Registry Verification (KYB)': companyTicksFor(
      personaRecords,
      effectivePersonaChecks['Business Registry Verification (KYB)']
    ),
  };
  // The business steps count as done only while the company's business check counts as verified.
  const isCompanyCheckVerified = isCompanyVerified(personaRecords);

  let identityDocuments = {};
  let workDocuments = {};

  // A candidate's identity and work documents come only from the verification records the server
  // holds. The listing's own public data also carries per-document match flags, but the candidate's
  // browser writes those, so they never make a document show as verified.
  if (
    listingIsCandidate &&
    personaRecords &&
    Array.isArray(personaRecords) &&
    personaRecords.length > 0
  ) {
    // Filter verified documents for identity category
    const walletIdentityDocs = personaRecords.filter(
      record =>
        isVerifiedRecord(record) &&
        (record.documentType?.startsWith('identity') || record.documentType?.startsWith('both'))
    );

    // Filter verified documents for work category
    const walletWorkDocs = personaRecords.filter(
      record =>
        isVerifiedRecord(record) &&
        (record.documentType?.startsWith('work') || record.documentType?.startsWith('both'))
    );

    // Convert wallet documents to the shape the document timelines read
    walletIdentityDocs.forEach(record => {
      if (record.documentName) {
        const documentType = record.documentType;
        const personaFieldsForDoc = documentTicks(record, effectivePersonaChecks[record.documentName]);

        identityDocuments[record.documentName] = {
          nameMatches: true,
          addressMatches: true,
          hasPhone: true,
          emailMatches: true,
          documentType: documentType,
          credentialId: record.credentialId,
          personaFields: personaFieldsForDoc,
        };
      }
    });

    walletWorkDocs.forEach(record => {
      if (record.documentName) {
        const documentType = record.documentType;
        const personaFieldsForDoc = documentTicks(record, effectivePersonaChecks[record.documentName]);

        workDocuments[record.documentName] = {
          nameMatches: true,
          addressMatches: true,
          hasPhone: true,
          emailMatches: true,
          documentType: documentType,
          credentialId: record.credentialId,
          personaFields: personaFieldsForDoc,
        };
      }
    });
  }

  // Generate Timeline items for Identity documents (matching ProfileSettingsForm structure)
  const identityTimelineItems = Object.entries(identityDocuments).map(([name, values], index) => {
    return {
      dot: (
        <img
          src={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? verified
              : unverified
          }
          alt={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? 'verified'
              : 'unverified'
          }
          width="22"
          title={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? 'Verified'
              : 'Unverified'
          }
        />
      ),
      children: (
        <div>
          <h6>
            {name}
            {isOwnListing !== null && (
              <Tooltip title={isOwnListing ? 'Share' : 'Request'}>
                <Button
                  color="default"
                  variant="filled"
                  shape="circle"
                  icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
                  className={css.verifyShare}
                  onClick={e => {
                    e.stopPropagation();
                    handleRequestDocument(
                      name,
                      values,
                      'identity',
                      isOwnListing,
                      values.personaFields
                    );
                  }}
                />
              </Tooltip>
            )}
          </h6>
          <Collapse
            ghost
            size="small"
            expandIconPosition="end"
            items={[
              {
                key: `identity-details-${index}`,
                label: <>View Details</>,
                children: (
                  <ul style={{ paddingLeft: '0', margin: '0' }}>
                    {values.personaFields && Object.keys(values.personaFields).length > 0 ? (
                      createPersonaFieldItems(
                        values.personaFields,
                        verifyIcon,
                        unveriffiedIcon
                      ).map((field, idx) => (
                        <li key={idx}>
                          <img
                            src={field.icon}
                            title={field.isVerified ? 'Verified' : 'Unverified'}
                            width="15px"
                          />{' '}
                          {field.displayName || field.fieldName}
                        </li>
                      ))
                    ) : (
                      <li style={{ color: '#999', fontSize: 12 }}>Field details not yet available</li>
                    )}
                  </ul>
                ),
              },
            ]}
          />
        </div>
      ),
    };
  });

  // Generate Timeline items for Work documents (matching ProfileSettingsForm structure)
  const workTimelineItems = Object.entries(workDocuments).map(([name, values], index) => {
    return {
      dot: (
        <img
          src={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? verified
              : unverified
          }
          alt={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? 'verified'
              : 'unverified'
          }
          width="22"
          title={
            values?.nameMatches &&
              values?.addressMatches &&
              values?.hasPhone &&
              values?.emailMatches
              ? 'Verified'
              : 'Unverified'
          }
        />
      ),
      children: (
        <div>
          <h6>
            {name}
            {isOwnListing !== null && (
              <Tooltip title={isOwnListing ? 'Share' : 'Request'}>
                <Button
                  color="default"
                  variant="filled"
                  shape="circle"
                  icon={isOwnListing ? <ShareAltOutlined /> : <SendOutlined />}
                  className={css.verifyShare}
                  onClick={e => {
                    e.stopPropagation();
                    handleRequestDocument(
                      name,
                      values,
                      'work-authorization',
                      isOwnListing,
                      values.personaFields
                    );
                  }}
                />
              </Tooltip>
            )}
          </h6>
          <Collapse
            ghost
            size="small"
            expandIconPosition="end"
            items={[
              {
                key: `work-details-${index}`,
                label: <>View Details</>,
                children: (
                  <ul style={{ paddingLeft: '0', margin: '0' }}>
                    {values.personaFields && Object.keys(values.personaFields).length > 0 ? (
                      createPersonaFieldItems(
                        values.personaFields,
                        verifyIcon,
                        unveriffiedIcon
                      ).map((field, idx) => (
                        <li key={idx}>
                          <img
                            src={field.icon}
                            title={field.isVerified ? 'Verified' : 'Unverified'}
                            width="15px"
                          />{' '}
                          {field.displayName || field.fieldName}
                        </li>
                      ))
                    ) : (
                      <li style={{ color: '#999', fontSize: 12 }}>Field details not yet available</li>
                    )}
                  </ul>
                ),
              },
            ]}
          />
        </div>
      ),
    };
  });

  const verificationBusinessChecks =
    currentListing?.attributes?.profile?.protectedData?.verificationBusinessChecks;
  const verificationBeneficialOwnerChecks =
    currentListing?.attributes?.profile?.protectedData?.verificationBeneficialOwnerChecks;
  const verificationInsuranceChecks =
    currentListing?.attributes?.profile?.protectedData?.verificationInsuranceChecks;

  // Calculate verification progress for business/agency users
  // Use state variables that account for expiry dates instead of helper functions
  const businessIdentityProgress = isBusinessIdentityVerified ? 100 : 0;

  // Calculate verification progress for candidates (using already declared variables: identityDocuments, workDocuments)
  const experiences = currentListing?.attributes?.publicData?.pastExperience || [];
  const educations = currentListing?.attributes?.publicData?.education || [];

  // Use state variables that account for expiry dates instead of helper functions
  // When viewing own listing, use current user's progress; otherwise use the listing author's progress
  const identityProgress = isOwnListing ? currentUserIdentityProgressValue : identityProgressValue; // Progressive: 0, 60, or 100
  const workProgress = isWorkVerified ? 100 : 0;
  const experienceProgress = isExperienceVerified ? 100 : 0;
  const educationProgress = isEducationVerified ? 100 : 0;

  const candidateAnchor = (
    <Anchor
      direction="horizontal"
      targetOffset={135}
      items={[
        {
          key: 'part-1',
          href: '#Tab1',
          title: 'Professional Summary',
        },
        {
          key: 'part-3',
          href: '#Tab3',
          title: 'iParley Credential Vault',
        },
        {
          key: 'part-2',
          href: '#Tab2',
          title: 'Skills',
        },
        {
          key: 'part-4',
          href: '#Tab4',
          title: 'Experience',
        },
        {
          key: 'part-5',
          href: '#Tab5',
          title: 'Education',
        },
        {
          key: 'part-6',
          href: '#Tab6',
          title: 'Certifications & Awards',
        },

        // {
        //   key: 'part-17',
        //   href: '#Tab17',
        //   title: 'Link Agencies'
        // },
        {
          key: 'part-18',
          href: '#Tab18',
          title: 'Projects',
        },
        // {
        //   key: 'part-14',
        //   href: '#Tab14',
        //   title: 'Referrals',
        // },
        {
          key: 'part-15',
          href: '#Tab15',
          title: 'Recommendations',
        },
      ]}
    />
  );

  const staffingItem =
    listingIsAgency &&
      currentListing?.attributes?.publicData?.agencyProfile?.agencyType?.includes('Staffing')
      ? [
        {
          key: 'part-staff',
          href: '#TabStaff',
          title: 'Staffing Summary',
        },
      ]
      : [];

  const implementationItem =
    listingIsAgency &&
      currentListing?.attributes?.publicData?.agencyProfile?.agencyType?.includes('Implementation')
      ? [
        {
          key: 'part-implementation',
          href: '#TabImplementation',
          title: 'Implementation Summary',
        },
      ]
      : [];

  const agencyAnchor = (
    <Anchor
      direction="horizontal"
      targetOffset={135}
      items={[
        {
          key: 'part-BusinessReg',
          href: '#TabBusinessReg',
          title: 'Business Registration',
        },
        {
          key: 'part-BusinessInfo',
          href: '#TabBusinessInfo',
          title: 'Business Information',
        },
        {
          key: 'part-Insurance',
          href: '#TabInsurance',
          title: 'Insurance and Ownership details',
        },
        {
          key: 'part-3',
          href: '#Tab3',
          title: 'Verification Summary',
        },
        ...staffingItem,
        ...implementationItem,
        // {
        //   key: 'part-8',
        //   href: '#Tab8',
        //   title: 'About',
        // },
        // {
        //   key: 'part-7',
        //   href: '#Tab7',
        //   title: 'Roles',
        // },
        // {
        //   key: 'part-2',
        //   href: '#Tab2',
        //   title: 'Skills',
        // },
        // {
        //   key: 'part-9',
        //   href: '#Tab9',
        //   title: 'Vendorships',
        // },
        // {
        //   key: 'part-10',
        //   href: '#Tab10',
        //   title: 'Partnerships',
        // },
        // {
        //   key: 'part-11',
        //   href: '#Tab11',
        //   title: 'Services',
        // },
        // {
        //   key: 'part-12',
        //   href: '#Tab12',
        //   title: 'Consultants',
        // },
        // {
        //   key: 'part-13',
        //   href: '#Tab13',
        //   title: 'Projects',
        // },
      ]}
    />
  );

  const customerAnchor = (
    <Anchor
      direction="horizontal"
      targetOffset={135}
      items={[
        {
          key: 'part-BusinessReg',
          href: '#TabBusinessReg',
          title: 'Business Registration',
        },
        {
          key: 'part-BusinessInfo',
          href: '#TabBusinessInfo',
          title: 'Business Information',
        },
        {
          key: 'part-Insurance',
          href: '#TabInsurance',
          title: 'Ownership and Management Details',
        },
        {
          key: 'part-3',
          href: '#Tab3',
          title: 'Verification Summary',
        },
        {
          key: 'part-ServicesNeed',
          href: '#TabServicesNeed',
          title: 'Services Need Summary',
        },
        // {
        //   key: 'part-7',
        //   href: '#Tab7',
        //   title: 'Roles',
        // },
        // {
        //   key: 'part-2',
        //   href: '#Tab2',
        //   title: 'Skills',
        // },
      ]}
    />
  );

  let aboutdata = [
    {
      avatar: <EnvironmentOutlined />,
      title: 'Location',
      description:
        currentListing.attributes?.publicData?.agencyProfile?.address?.selectedPlace?.address,
    },
    {
      avatar: <GlobalOutlined />,
      title: 'DBA/Alternate name',
      // description: 'www.rormado.com'
      description: currentListing.attributes?.publicData?.agencyProfile?.agencyAlternateName,
    },
    {
      avatar: <GlobalOutlined />,
      title: 'Website',
      // description: 'www.rormado.com'
      description: currentListing.attributes?.publicData?.agencyProfile?.website,
    },
    {
      avatar: <ProductOutlined />,
      title: 'Type',
      description: currentListing.attributes?.publicData?.agencyProfile?.agencyType,
    },
    {
      avatar: <MailOutlined />,
      title: 'Email',
      description: currentListing.attributes?.publicData?.agencyProfile?.agencyMail,
    },
  ];

  if (listingIsCustomer) {
    aboutdata = [
      {
        avatar: <EnvironmentOutlined />,
        title: 'Location',
        description:
          currentListing.attributes?.publicData?.customerProfile?.address?.selectedPlace?.address ||
          '',
      },
      {
        avatar: <IdcardOutlined />,
        title: 'DBA/Alternate name',
        // description: 'www.rormado.com'
        description: currentListing.attributes?.publicData?.customerProfile?.customerAlternateName,
      },
      {
        avatar: <GlobalOutlined />,
        title: 'Website',
        // description: 'www.rormado.com'
        description: currentListing.attributes?.publicData?.customerProfile?.website,
      },
      {
        avatar: <LinkedinOutlined />,
        title: 'LinkedIn',
        // description: 'www.rormado.com'
        description: currentListing.attributes?.publicData?.agencyProfile?.customerLinkedInPage,
      },
      {
        avatar: <ProductOutlined />,
        title: 'Type',
        description: currentListing.attributes?.publicData?.customerProfile?.customerType,
      },
      {
        avatar: <MailOutlined />,
        title: 'Email',
        description: currentListing.attributes?.publicData?.customerProfile?.customerMail,
      },
    ];
  }

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCardDetails(null);
  };

  const handleMessageSend = async () => {
    const msgTxnId = await onInitiateManualTransaction(
      toSendMessage,
      currentListing?.id?.uuid,
      false,
      true,
      false
    );

    setToSendMessage('');
    setShowMessageModal(false);

    if (msgTxnId !== undefined && msgTxnId !== null && msgTxnId !== '' && msgTxnId !== false) {
      history.push(
        createResourceLocatorString(
          'InboxPage',
          routeConfiguration,
          { tab: 'sales' },
          { conv: msgTxnId }
        )
      );
    }
  };

  const handleContactUserClick = () => {
    if (messageTxnId === null || messageTxnId === undefined || messageTxnId === '') {
      // onContactUser(currentAuthor);
      setShowMessageModal(true);
    } else {
      history.push(
        createResourceLocatorString(
          'InboxPage',
          routeConfiguration,
          { tab: 'sales' },
          { conv: messageTxnId }
        )
      );
    }
  };

  const redirectToProfileSettings = () => {
    history.push(createResourceLocatorString('ProfileSettingsPage', routeConfiguration, {}));
  };

  const handleRequestDocument = (
    documentName,
    documentValues,
    columnName = null,
    isShare = false,
    personaFieldsData = null
  ) => {
    setSelectedDocumentName(documentName);
    setSelectedDocument(documentValues);
    setIsShareMode(isShare); // Set whether this is share or request mode

    // Define fields based on the column Timeline (matching exact field names shown in UI)
    let fields = [];

    // If personaFieldsData is provided, use it to generate dynamic fields
    // Only include verified fields (where value is true)
    if (personaFieldsData && Object.keys(personaFieldsData).length > 0) {
      // First consolidate address fields
      const consolidatedFields = consolidateAddressFieldsForDisplay(personaFieldsData);

      // Then filter for verified fields and create field objects
      fields = consolidatedFields
        .filter(field => field.isVerified === true)
        .map(field => ({
          fieldName: field.fieldName, // Keep original or consolidated field name
          displayName: formatFieldName(field.fieldName), // Formatted name for display
          consolidatedFrom: field.consolidatedFrom, // Track which original fields this represents
        }));
    } else if (columnName === 'business-identity') {
      // Business Identity and Registry Timeline fields (grouped) - fallback
      fields = [
        // Business Information
        'Business Name',
        'Business Type',
        'DBA Name',
        'Business URL',

        // Business Address
        'Street Address',
        'City',
        'State/Province',
        'Zip/Postal Code',
        'Country',

        // Tax & Compliance
        'Employer Identification Number (EIN)',
        // "Tax Identification Number",
        'Federal Tax Classification',
        'Date of Incorporation',

        // Financial Information
        'Annual Revenue',
        'Number of Employees',

        // Form Filler Information
        // "Name",
        // "Email Address",
        // "Job Title",

        // "Registered Address Same As Physical",
        // "UBO 1 Name First",
        // "UBO 1 Name Last",
        // "UBO 1 Email Address",
        // "UBO 1 Percentage Ownership",

        // "UBO 2 Name First",
        // "UBO 2 Name Last",
        // "UBO 2 Email Address",
        // "UBO 2 Percentage Ownership",
      ];
    } else if (columnName === 'beneficial-owner') {
      // Beneficial Owner Identity Timeline fields - fallback
      fields = ['First name', 'Last name', 'Email Id', 'Ownership percentage'];
    } else if (columnName === 'associated-person') {
      // Associated Person Identity Timeline fields - fallback
      fields = ['First name', 'Last name', 'Email Id', 'Ownership percentage'];
    } else {
      // Default/fallback for other document types
      if (documentValues.nameMatches) fields.push('Name');
      if (documentValues.addressMatches) fields.push('Address');
      if (documentValues.hasPhone) fields.push('Phone number');
      if (documentValues.emailMatches) fields.push('Email contact');
    }

    setAvailableFields(fields); // Set all available fields to display
    setSelectedVerifiedFields([]); // Start with no fields selected
    setOfferedFiles(
      offeredCompanyFiles({
        listingIsAgency,
        columnName,
        isShare,
        heldFiles: heldCompanyFiles({ loaded: companyCredentialsLoaded, companyCredentials }),
      })
    );
    setTickedFiles([]);
    setShowRequestDocumentModal(true);
  };
  
  // Credential Access Request Handlers (credentialVault-based flow)
  const handleApproveCredentialAccess = async () => {
    if (!credentialAccessRequest?._id || !approvalConsentChecked) return;
    // The approval goes out only once its consent is saved; otherwise the dialog stays as it is so
    // the person can try again.
    const showConsentRetry = () =>
      message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
    setCredentialAccessLoading(true);
    try {
      let consentId;
      try {
        consentId = await saveConsentFor('CredentialShare', { consentText: CREDENTIAL_APPROVAL_WORDING });
      } catch (error) {
        if (!(error instanceof ConsentSaveFailedError)) throw error;
        showConsentRetry();
        return;
      }
      const response = await onApproveCredentialAccess(
        credentialAccessRequest._id,
        credentialAccessTickedFiles,
        consentId
      );
      if (response?.success && approvalEmailFailed(response)) {
        // Access was approved, but the requester was not told: keep the dialog open to resend.
        setCredentialAccessEmailFailedId(credentialAccessRequest._id);
      } else if (response?.success) {
        message.success('Credentials shared successfully! The requester has been notified via email.');
        setShowCredentialAccessModal(false);
        setCredentialAccessRequest(null);
        setCredentialAccessTickedFiles([]);
        setApprovalConsentChecked(false);
        // Clean the URL query param
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('credentialRequest');
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        message.error('Failed to share credentials. Please try again.');
      }
    } catch (error) {
      // A consent the server refuses is answered like one that could not be saved.
      if (isConsentRequiredRefusal(error)) {
        showConsentRetry();
        return;
      }
      reportCredentialFailure(error, {
        intl,
        notify: message,
        describe: e => decisionErrorMessage(e, { intl, fallback: 'Failed to approve credential access request.' }),
      });
    } finally {
      setCredentialAccessLoading(false);
    }
  };

  const handleRejectCredentialAccess = async () => {
    if (!credentialAccessRequest?._id) return;
    setCredentialAccessLoading(true);
    try {
      const response = await onRejectCredentialAccess(credentialAccessRequest._id);
      if (response?.success) {
        message.info('Credential access request declined.');
        setShowCredentialAccessModal(false);
        setCredentialAccessRequest(null);
        setCredentialAccessTickedFiles([]);
        setApprovalConsentChecked(false);
        // Clean the URL query param
        if (typeof window !== 'undefined') {
          const url = new URL(window.location.href);
          url.searchParams.delete('credentialRequest');
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        message.error('Failed to decline request. Please try again.');
      }
    } catch (error) {
      message.error(decisionErrorMessage(error, { intl, fallback: 'Failed to reject credential access request.' }));
    } finally {
      setCredentialAccessLoading(false);
    }
  };

  const handleCloseCredentialAccessModal = () => {
    setCredentialAccessEmailFailedId(null);
    setShowCredentialAccessModal(false);
    setCredentialAccessRequest(null);
    setCredentialAccessTickedFiles([]);
    setApprovalConsentChecked(false);
    // Clean the URL query param
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('credentialRequest');
      window.history.replaceState({}, '', url.toString());
    }
  };

  const handleRequestDocumentCancel = () => {
    setShowRequestDocumentModal(false);
    setSelectedDocument(null);
    setSelectedDocumentName('');
    setAvailableFields([]);
    setSelectedVerifiedFields([]);
    setOfferedFiles([]);
    setTickedFiles([]);
    setResponseByMail(false);
    setResponseByPhone(false);
    setConsentToRequest(false);
    setIsShareMode(false);
    setSharePhone('');
    setShareEmail('');
    setShareRecipientName('');
  };

  // Share Profile OTP handlers
  const handleShareOtpSubmit = async () => {
    if (!shareOtp || shareOtp.length !== 6) {
      message.error('Please enter a valid 6-digit Secure Verification Code');
      return;
    }

    setShareOtpLoading(true);
    setShareOtpError(null);

    try {
      // Verify OTP
      const verifyRes = await props.onVerifyShareOtp(shareToken, shareOtp);

      if (verifyRes.verified) {
        const requestType = verifyRes.requestType || shareRequestType || 'profile';
        // The visit lasts for this tab: kept so a refresh here needs no new code.
        const visit = verifyRes.shareSession ? { token: shareToken, session: verifyRes.shareSession } : null;
        if (visit) {
          keepShareVisit(shareToken, { session: visit.session, requestType });
          setShareVisit(visit);
          props.onFetchShareVisitorPersonaRecords(currentListing?.id?.uuid, visit);
        }
        setShareOtpVerified(true);
        setShareRequestType(requestType);
        message.success('Secure Verification Code verified successfully!');

        // Fetch shared profile data
        const profileRes = visit
          ? await props.onGetSharedProfile(shareToken, { shareVisit: visit })
          : await props.onGetSharedProfile(shareToken);
        setSharedProfileData(profileRes);
        setShowShareOtpModal(false);

        // Optionally reload the page to show the shared profile
        // or you can use the sharedProfileData to display it
        {shareRequestType === 'vault' ?
          message.success('Credential vault loaded successfully!')
        : message.success('Profile loaded successfully!')}
      }
    } catch (error) {
      const errorMsg = error?.error || error?.message || 'Invalid secure verification code. Please try again.';
      setShareOtpError(errorMsg);
      message.error(errorMsg);
    } finally {
      setShareOtpLoading(false);
    }
  };

  const handleShareOtpCancel = () => {
    setShowShareOtpModal(false);
    setShareOtp('');
    setShareOtpError(null);
    // Optionally redirect away if user cancels
  };

  const handleResendShareOtp = async () => {
    try {
      const res = await props.onRequestShareOtp(shareToken);
      message.success(`Secure Verification Code resent to ${res.email}`);
    } catch (error) {
      const errorMessage = error?.error || error?.message || 'Failed to resend Secure Verification Code';
      message.error(errorMessage);
    }
  };

  // Request Access handlers
  const handleRequestAccessClick = (type='profile') => {
    setRequestAccessType(type);
    setShowRequestAccessModal(true);
    setRequestAccessData({
    firstName: currentUser?.attributes?.profile?.displayName?.split(' ')[0] || '',
    lastName: currentUser?.attributes?.profile?.displayName?.split(' ').slice(1).join(' ') || '',
    email: currentUser?.attributes?.email || '',
    companyName:
      currentUser?.attributes?.profile?.publicData?.companyName ||
      currentUser?.attributes?.profile?.publicData?.agencyProfile?.name ||
      currentUser?.attributes?.profile?.publicData?.customerProfile?.name || '',
    message: '',
  });
    setRequestAccessError(null);
  };

  const handleRequestAccessCancel = () => {
    setShowRequestAccessModal(false);
    setRequestAccessType('profile');
    setRequestAccessData({
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
      message: '',
    });
    setRequestAccessError(null);
  };

  const handleRequestAccessChange = (field, value) => {
    setRequestAccessData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRequestAccessSubmit = async () => {
    // Validate required fields
    if (!requestAccessData.firstName?.trim()) {
      message.error('Please enter your first name');
      return;
    }
    if (!requestAccessData.lastName?.trim()) {
      message.error('Please enter your last name');
      return;
    }
    if (!requestAccessData.email?.trim()) {
      message.error('Please enter your email');
      return;
    }
    if (!requestAccessData.companyName?.trim()) {
      message.error('Please enter your company name');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(requestAccessData.email)) {
      message.error('Please enter a valid email address');
      return;
    }

    setRequestAccessLoading(true);
    setRequestAccessError(null);

    try {
      const toSendEmail = authorEmail || currentListingAdmin?.attributes?.email;
      // Call API to submit access request
      await props.onRequestProfileAccess({
        ...requestAccessData,
        profileListingId: currentListing?.id?.uuid,
        profileOwnerId: currentListing?.author?.id?.uuid,
        profileOwnerEmail: toSendEmail,
        profileOwnerName: authorDisplayName,
        profileTitle: currentListing?.attributes?.title,
        requestType: requestAccessType,
      });
      message.success(
        requestAccessType === 'vault'
          ? 'Credential vault access request submitted! The profile owner will be notified.'
          : 'Access request submitted successfully! The profile owner will be notified.'
      );
      handleRequestAccessCancel();
    } catch (error) {
      const errorMsg = error?.error || error?.message || 'Failed to submit access request';
      setRequestAccessError(errorMsg);
      message.error(errorMsg);
    } finally {
      setRequestAccessLoading(false);
    }
  };

  const handleRequestDocumentSubmit = async () => {
    // Validate recipient name, email and phone if in share mode
    if (isShareMode) {
      // Recipient name validation
      if (!shareRecipientName || shareRecipientName.trim() === '') {
        message.error('Please enter the recipient name');
        return;
      }
      if (responseByMail && !shareEmail) {
        message.error('Please enter a valid email address');
        return;
      }
      if (responseByPhone && !sharePhone) {
        message.error('Please enter a valid phone number');
        return;
      }
      // Email validation
      if (responseByMail && shareEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(shareEmail)) {
          message.error('Please enter a valid email address');
          return;
        }
      }
      // Phone validation
      if (responseByPhone && sharePhone) {
        if (!isSharePhoneValid(sharePhone)) {
          message.error('Please enter a valid phone number');
          return;
        }
      }
    }

    // The share or request goes out only once its consent is saved; otherwise the dialog stays as
    // it is so the person can try again.
    const showConsentRetry = () =>
      message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
    let consentId;
    try {
      consentId = await saveConsentFor(isShareMode ? 'CredentialShare' : 'CredentialRequest');
    } catch (error) {
      if (!(error instanceof ConsentSaveFailedError)) throw error;
      showConsentRetry();
      return;
    }

    if (isShareMode) {
      // Share mode: User is directly sharing their own credentials via email/SMS
      const shareData = {
        profileListingId: currentUserMainListingId,
        documentType: selectedDocumentName,
        vaultDocumentKey: selectedDocument?.documentType?.replace(/\./g, '_'),
        requestedFields: selectedVerifiedFields,
        recipientName: shareRecipientName || 'Recipient',
        recipientEmail: responseByMail ? shareEmail : null,
        recipientPhone: responseByPhone ? sharePhone : null,
        deliveryPreference: {
          byEmail: responseByMail,
          bySMS: responseByPhone,
        },
        issuerId: currentUser?.id?.uuid,
        issuerName: currentUser?.attributes?.profile?.displayName || 'iParley User',
        consentId,
      };

      // A refusal of the consent by the server is answered as a failed share carrying the retry
      // message, and leaves the dialog open.
      let consentRefused = false;
      const shareNamingConsent = async data => {
        try {
          return await onShareCredential(data);
        } catch (error) {
          if (!isConsentRequiredRefusal(error)) throw error;
          consentRefused = true;
          return {
            success: false,
            error: intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }),
          };
        }
      };

      const response = await submitCredentialShare({
        shareData: withCompanyFiles(shareData, tickedFiles),
        onShareCredential: shareNamingConsent,
        intl,
        notify: message,
      });
      if (consentRefused) return;
      if (response?.success && responseByPhone && onFetchSmsBalance) onFetchSmsBalance();
    } else {
      // Request mode: User is requesting credentials from someone else
      // COMMENTED OUT - Old Truvera/Dock request flow via onRequestDocumentPresentation
      // const requestData = {
      //   documentName: selectedDocumentName,
      //   documentValues: selectedDocument,
      //   requestedFields: selectedVerifiedFields,
      //   responsePreference: {
      //     byMail: responseByMail,
      //     byPhone: responseByPhone,
      //   },
      //   verifier: {
      //     id: currentUser?.id?.uuid,
      //     email: currentUser?.attributes?.email,
      //     displayName: currentUser?.attributes?.profile?.displayName,
      //     phoneNumber: currentUser?.attributes?.profile?.publicData?.phoneNumber,
      //     walletDID: currentUser?.attributes?.profile?.publicData?.walletDID,
      //   },
      //   issuer: {
      //     listingId: currentListing?.id?.uuid,
      //     listingTitle: currentListing?.attributes?.title,
      //     id: currentListing?.author?.id?.uuid,
      //     displayName: currentListing?.author?.attributes?.profile?.displayName,
      //     email: currentListingAdmin?.attributes?.email,
      //     phoneNumber: currentListing?.author?.attributes?.profile?.publicData?.phoneNumber,
      //   },
      //   timestamp: new Date().toISOString(),
      // };
      // onRequestDocumentPresentation(requestData);
      // message.success(`Request submitted for ${selectedDocumentName}`);

      // NEW: credentialVault-based credential access request flow
      const profileOwnerListingId = currentListing?.id?.uuid;
      const profileOwnerSlug = listingSlug || createSlug(currentListing?.attributes?.title || '');
      const profileOwnerId = currentListing?.author?.id?.uuid;
      const profileOwnerEmail = currentListingAdmin?.attributes?.email;
      const profileOwnerName = currentListing?.author?.attributes?.profile?.displayName || 'Profile Owner';

      const credentialRequestData = {
        requesterName: currentUser?.attributes?.profile?.displayName || 'Unknown User',
        requesterEmail: currentUser?.attributes?.email,
        requesterCompanyName: currentUser?.attributes?.profile?.publicData?.companyName || '',
        requesterUserId: currentUser?.id?.uuid,
        requesterProfileListingId: currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId || '',
        requesterPhone: currentUser?.attributes?.profile?.publicData?.phoneNumber || '',
        profileListingId: profileOwnerListingId,
        profileListingSlug: profileOwnerSlug,
        profileOwnerId,
        profileOwnerEmail,
        profileOwnerName,
        requestedFields: selectedVerifiedFields,
        documentType: selectedDocumentName,
        message: '', // Could be expanded with a message field in the modal
        deliveryPreference: {
          byEmail: responseByMail,
          bySMS: responseByPhone,
        },
        consentId,
      };

      try {
        const response = await onRequestCredentialAccess(
          withCompanyFiles(credentialRequestData, tickedFiles)
        );
        if (response?.success) {
          message.success(`Credential access request sent to ${profileOwnerName}. They will receive an email notification.`);
        } else {
          message.error('Failed to submit credential access request.');
        }
      } catch (error) {
        if (isConsentRequiredRefusal(error)) {
          showConsentRetry();
          return;
        }
        message.error(error.message || 'Failed to submit credential access request. Please try again.');
      }
    }

    handleRequestDocumentCancel();
  };

  const handleVerifiedFieldToggle = field => {
    setSelectedVerifiedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const getVendorStatusColor = status => {
    switch (status) {
      case 'accepted':
        return 'green';
      case 'rejected':
        return 'red';
      case 'requested':
        return 'orange';
      default:
        return 'default';
    }
  };

  const createCustomerColumns = () => [
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <div>
            {record?.customerListingId ? (
              <div style={{ fontWeight: 'bold' }}>
                <a
                  href={`/l/${record.customerListingId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              </div>
            ) : (
              <div style={{ fontWeight: 'bold' }}>{text}</div>
            )}

            {/* <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.customerEmail}
            </Text> */}
          </div>
        </Space>
      ),
    },
    {
      title: 'Supplier Type',
      dataIndex: 'type',
      key: 'type',
      render: type => (
        <Tag color={type === 'staffing' ? 'green' : 'green'}>
          {type?.toUpperCase() || 'STAFFING'}
        </Tag>
      ),
    },
  ];

  /**
   * Finds which categories are verified based on the provided data.
   * @param {object} categories - The map of categories to document lists.
   * @param {object} checks - The map of verified document names.
   * @returns {string[]} An array of verified category names.
   */
  const getVerifiedCategories = (categories, checks) => {
    const verifiedDocIds = new Set(Object.keys(checks));
    const verifiedCategories = new Set();

    for (const categoryName in categories) {
      const documentsInList = categories[categoryName];

      const isCategoryVerified = documentsInList.some(doc => verifiedDocIds.has(doc.id));

      if (isCategoryVerified) {
        verifiedCategories.add(categoryName);
      }
    }

    return Array.from(verifiedCategories);
  };

  const handleApplyWithConsultant = async (listingId, consultantId, pickedRow) => {
    try {
      const isUserInvited = isInvitedToPosting(currentListing, { currentUser, profileListing, mainUserId });

      // The consultant's listing comes from the applying agency's own link to the consultant, never
      // from the viewed listing author's links. The picker lists accepted consultants only.
      const { applyStatus, consultantListingId } = agencyApplyWithConsultant({
        link: { ...pickedRow, status: 'accepted' },
      });

      // The uploaded documents go with the application only under the agency's saved consent.
      const withConsent = !!agencyUploadConsentId;
      await onInitiateApplyTransaction(
        listingId,
        isUserInvited,
        consultantId,
        consultantListingId,
        true,
        withConsent ? applyIdentityDocs : [],
        withConsent ? applyWorkAuthDocs : [],
        null,
        applyStatus,
        agencyUploadConsentId || undefined
      );

      // Update local state to track this consultant as applied. Do NOT set the
      // listing-level `applied` flag here — agencies apply per consultant and
      // must keep applying for the remaining consultants.
      setLocalAppliedConsultants(prev => [...prev, consultantListingId]);
      // Reset the uploaded documents and their consent so the next consultant starts fresh.
      setApplyIdentityDocs([]);
      setApplyWorkAuthDocs([]);
      setAgencyUploadConsentId(null);
      setDocConsentChecked(false);
      setDocConsentContext({ mode: 'apply', docType: '' });
      setShowConsultantModal(false);
    } catch (error) {
      if (isConsentRequiredRefusal(error)) {
        // The server did not accept the consent: back to the consent dialog, still ticked, to retry.
        setAgencyUploadConsentId(null);
        setShowConsultantModal(false);
        setShowDocConsentModal(true);
        message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
        return;
      }
      message.error('Failed to send application. Please try again.');
    }
  };

  // An agency with uploaded documents gives its own upload consent before it picks a consultant.
  const openAgencyApply = () => {
    if (applyIdentityDocs.length > 0 || applyWorkAuthDocs.length > 0) {
      setAgencyUploadConsentId(null);
      setDocConsentChecked(false);
      setDocConsentContext({ mode: 'agency', docType: '' });
      setShowDocConsentModal(true);
      return;
    }
    setShowConsultantModal(true);
  };

  const contact = (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  );

  const formatBudgetRange = value => {
    const map = {
      '0To25': '$0 – $25K',
      '25To100': '$25K – $100K',
      '100To500': '$100K – $500K',
      '500To1000': '$500K – $1M',
      '1000Plus': '$1M+',
    };
    return map[value] || value;
  };

  const formatContractDuration = value => {
    const map = {
      '0To3Months': '0 – 3 months',
      '3To6Months': '3 – 6 months',
      '6To12Months': '6 – 12 months',
      '12PlusMonths': '12+ months',
    };
    return map[value] || value;
  };

  if (listingType == 'rfp_listing') {
    if (currentUserIsCandidate || (currentUserIsAgency && currentUserRole === 'Staffing Sales')) {
      return <NotFoundPage staticContext={props.staticContext} />;
    }

    const rfpPublicId = publicData?.jobRfpId;

    const createRFPPostingItems = listing => {
      const items = [];
      const publicData = listing?.attributes?.publicData;
      const rfpNumber =  publicData?.rfpNumberIdRFP;

      if (!publicData) return items;

      // Company
      if (publicData?.companyName) {
        if (currentListingAdmin?.attributes?.profile?.publicData?.profileListingId) {
          items.push({
            category: 'RFP Identification',
            icon: <ShopOutlined />,
            label: 'Company',
            description: (
              <a
                title="Company"
                href={`/l/${currentListingAdmin?.attributes?.profile?.publicData?.profileListingId}`}
                target="_blank"
              >
                {publicData?.companyName}
              </a>
            ),
          });
        } else {
          items.push({
            category: 'RFP Identification',
            icon: <ShopOutlined />,
            label: 'Company',
            description: (
              // <a title='Company' href={`/l/${currentListingAdmin?.attributes?.profile?.publicData?.profileListingId}`} target='_blank'>
              <>{publicData?.companyName}</>
              // </a>
            ),
          });
        }
      }

      // RFP Identification Number
      if (rfpNumber) {
        items.push({
          category: 'RFP Identification',
          icon: <IdcardOutlined />,
          label: 'RFP Number',
          description: rfpNumber,
        });
      }
      if (publicData.issuingOrgDepRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <BankOutlined />,
          label: 'Issuing Organization / Department',
          description: publicData.issuingOrgDepRFP,
        });
      }
      if (publicData.issuingOrgTypeRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <BankOutlined />,
          label: 'Issuing Organization Type',
          description: publicData.issuingOrgTypeRFP,
        });
      }
      if (publicData.categoryRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <TagOutlined />,
          label: 'Category',
          description: `${publicData.categoryRFP}${publicData.categoryCodeRFP ? ` (${publicData.categoryCodeRFP})` : ''
            }`,
        });
      }
      if (publicData.submissionDeadlineRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <CalendarOutlined />,
          label: 'Proposal Submission Due Date',
          description: publicData.submissionDeadlineRFP,
        });
      }
      if (publicData.singleMultiAwardIntentRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <TrophyOutlined />,
          label: 'Single/Multi-award Intent',
          description: publicData.singleMultiAwardIntentRFP,
        });
      }
      if (publicData.contractTermDurationRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <ClockCircleOutlined />,
          label: 'Contract Term/Duration',
          description: publicData.contractTermDurationRFP,
        });
      }
      if (publicData.incumbentProviderRFP) {
        items.push({
          category: 'RFP Identification',
          icon: <UserOutlined />,
          label: 'Incumbent Provider',
          description: publicData.incumbentProviderRFP,
        });
      }

      // Communications
      if (publicData.procurementContactPOCNameRFP) {
        items.push({
          category: 'Communications',
          icon: <ContactsOutlined />,
          label: 'Procurement Contact',
          description: `${publicData.procurementContactPOCNameRFP}, ${publicData.procurementContactPOCTitleRFP}`,
        });
      }
      if (publicData.QAWindowClarScheduleRFP) {
        items.push({
          category: 'Communications',
          icon: <CommentOutlined />,
          label: 'Q&A Window / Clarification Schedule',
          description: publicData.QAWindowClarScheduleRFP,
        });
      }
      if (publicData.prebidConfSiteVisitRFP) {
        items.push({
          category: 'Communications',
          icon: <EnvironmentOutlined />,
          label: 'Pre-bid Conference / Site Visit',
          description: publicData.prebidConfSiteVisitRFP,
        });
      }

      //Addenda / Amendment logs
      if (publicData.addendaAmendmentsLogRFP) {
        items.push({
          category: 'Communications',
          icon: <EnvironmentOutlined />,
          label: 'Addenda / Amendment log',
          description: publicData.addendaAmendmentsLogRFP,
        });
      }

      // Scope of Work
      if (publicData.ProjectServiceDomainRFP) {
        items.push({
          category: 'Scope of Work',
          icon: <SettingOutlined />,
          label: 'Project Service Domain',
          description: Array.isArray(publicData.ProjectServiceDomainRFP) ? (
            publicData.ProjectServiceDomainRFP.map((item, index) => (
              <Tag key={index} color="blue">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="blue">{publicData.ProjectServiceDomainRFP}</Tag>
          ),
        });
      }
      if (publicData.projTechFocusSpecNeedsRFP) {
        items.push({
          category: 'Scope of Work',
          icon: <ToolOutlined />,
          label: 'Technology focus/specializations needed',
          description: Array.isArray(publicData.projTechFocusSpecNeedsRFP) ? (
            publicData.projTechFocusSpecNeedsRFP.map((item, index) => (
              <Tag key={index} color="geekblue">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="geekblue">{publicData.projTechFocusSpecNeedsRFP}</Tag>
          ),
        });
      }
      if (publicData.deliverablesListRFP) {
        items.push({
          category: 'Scope of Work',
          icon: <FileProtectOutlined />,
          label: 'Deliverables List',
          description: publicData.deliverablesListRFP,
        });
      }

      // Milestones & timeline
      if (publicData?.milestonesTimelineRFP) {
        items.push({
          category: 'Scope of Work',
          icon: <FileProtectOutlined />,
          label: 'Milestones & Timeline',
          description: publicData.milestonesTimelineRFP,
        });
      }

      // Assumptions & constraints
      if (publicData?.assumptionsConstraintsRFP) {
        items.push({
          category: 'Scope of Work',
          icon: <FileProtectOutlined />,
          label: 'Assumptions & constraints',
          description: publicData.assumptionsConstraintsRFP,
        });
      }

      // Proposal Instructions
      if (publicData.submissionFormatRFP) {
        items.push({
          category: 'Proposal Instructions',
          icon: <FileTextOutlined />,
          label: 'Submission Format',
          description: publicData.submissionFormatRFP,
        });
      }
      if (publicData.evaluationCriteriaWeightsRFP) {
        items.push({
          category: 'Proposal Instructions',
          icon: <AuditOutlined />,
          label: 'Evaluation Criteria / Weights',
          description: publicData.evaluationCriteriaWeightsRFP,
        });
      }

      if (publicData?.pricingTemplateRFP) {
        items.push({
          category: 'Proposal Instructions',
          icon: <DollarCircleOutlined />,
          label: 'Pricing Template',
          description: publicData.pricingTemplateRFP,
        });
      }

      if (publicData?.NDARequiredRFP === 'Yes') {
        items.push({
          category: 'Proposal Instructions',
          icon: <FileProtectOutlined />,
          label: 'NDAs Required',
          description: publicData?.NDARequiredFieldRFP,
        });
      }

      if (publicData.submissionPortalMethodRFP) {
        items.push({
          category: 'Proposal Instructions',
          icon: <SendOutlined />,
          label: 'How to Submit',
          description:
            publicData.submissionPortalMethodRFP === 'URL'
              ? publicData.submissionPortalURLRFP
              : publicData.submissionPortalMethodRFP,
        });
      }

      // Vendor Criteria
      if (publicData.minimumQualificationsRFP) {
        items.push({
          category: 'Supplier Criteria',
          icon: <CheckCircleOutlined />,
          label: 'Minimum Qualifications',
          description: publicData.minimumQualificationsRFP,
        });
      }

      // Past performance references
      if (publicData?.pastPerformanceReferencesRFP === 'Yes') {
        items.push({
          category: 'Supplier Criteria',
          icon: <CheckCircleOutlined />,
          label: 'Past performance references',
          description: publicData.pastPerformanceReferencesFieldRFP,
        });
      }

      if (publicData.certificationsStandardsRFP) {
        items.push({
          category: 'Supplier Criteria',
          icon: <SafetyCertificateOutlined />,
          label: 'Required Supplier Certifications',
          description: Array.isArray(publicData.certificationsStandardsRFP) ? (
            publicData.certificationsStandardsRFP.map((item, index) => (
              <Tag key={index} color="purple">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="purple">{publicData.certificationsStandardsRFP}</Tag>
          ),
        });
      }

      if (publicData.geographicLegalRequirementsRFP) {
        items.push({
          category: 'Supplier Criteria',
          icon: <SafetyCertificateOutlined />,
          label: 'Geographic / Legal Requirements',
          description: publicData.geographicLegalRequirementsRFP,
        });
      }

      // Contract and Financials
      if (publicData.budgetRangeMinRFP || publicData.budgetRangeMaxRFP) {
        const min = publicData.budgetRangeMinRFP;
        const max = publicData.budgetRangeMaxRFP;
        let budgetDesc;
        if (min && max) budgetDesc = `$${min} – $${max}`;
        else if (min) budgetDesc = `$${min}+`;
        else budgetDesc = `Up to $${max}`;
        items.push({
          category: 'Contract and Financials',
          icon: <DollarCircleOutlined />,
          label: 'Estimated Budget Range',
          description: budgetDesc,
        });
      }
      if (publicData.contractTypeRFP) {
        items.push({
          category: 'Contract and Financials',
          icon: <FileProtectOutlined />,
          label: 'Contract Type',
          description: Array.isArray(publicData.contractTypeRFP) ? (
            publicData.contractTypeRFP.map((item, index) => (
              <Tag key={index} color="cyan">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="cyan">{publicData.contractTypeRFP}</Tag>
          ),
        });
      }
      if (publicData.contractTermsRFP) {
        items.push({
          category: 'Contract and Financials',
          icon: <AuditOutlined />,
          label: 'Is Subcontracting Allowed?',
          description: publicData.contractTermsRFP,
        });
      }
      if (publicData.deliveryModelsRFP) {
        items.push({
          category: 'Contract and Financials',
          icon: <CarOutlined />,
          label: 'Allowed Delivery Models',
          description: Array.isArray(publicData.deliveryModelsRFP) ? (
            publicData.deliveryModelsRFP.map((item, index) => (
              <Tag key={index} color="green">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="green">{publicData.deliveryModelsRFP}</Tag>
          ),
        });
      }
      if (publicData.paymentTermsSelectRFP) {
        items.push({
          category: 'Contract and Financials',
          icon: <DollarOutlined />,
          label: 'Payment Terms',
          description: publicData.paymentTermsSelectRFP,
        });
      }

      // Standard Ts&Cs
      if (publicData.standardTsCsRFP) {
        items.push({
          category: 'Contract and Financials',
          icon: <FileProtectOutlined />,
          label: 'Standard Terms & Conditions',
          description: publicData.standardTsCsRFP,
        });
      }

      // Supplier Verifications
      if (publicData.requiredVerificationsRFP) {
        items.push({
          category: 'Supplier Verification',
          icon: <VerifiedOutlined />,
          label: 'Required Verifications',
          description: Array.isArray(publicData.requiredVerificationsRFP) ? (
            publicData.requiredVerificationsRFP.map((item, index) => (
              <Tag key={index} color="gold">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="gold">{publicData.requiredVerificationsRFP}</Tag>
          ),
        });
      }
      if (
        Array.isArray(publicData?.requiredVerificationsRFP) &&
        publicData?.requiredVerificationsRFP.includes('Past performace evidence') &&
        publicData.pastPerformanceEvidenceRequiredInputRFP
      ) {
        items.push({
          category: 'Supplier Verification',
          icon: <VerifiedOutlined />,
          label: 'Past performance requirements',
          description: publicData?.pastPerformanceEvidenceRequiredInputRFP,
        });
      }
      if (
        Array.isArray(publicData?.requiredVerificationsRFP) &&
        publicData?.requiredVerificationsRFP.includes('Security Certifications') &&
        publicData.securityCertificationsRFP
      ) {
        items.push({
          category: 'Supplier Verification',
          icon: <SafetyCertificateOutlined />,
          label: 'Required Security Certifications',
          description: Array.isArray(publicData.securityCertificationsRFP) ? (
            publicData.securityCertificationsRFP.map((item, index) => (
              <Tag key={index} color="magenta">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="magenta">{publicData.securityCertificationsRFP}</Tag>
          ),
        });
      }
      if (
        publicData.diversityCertificationsRequiredRFP === 'Yes' &&
        publicData.diversityCertificationsRFP
      ) {
        items.push({
          category: 'Supplier Verification',
          icon: <UsergroupAddOutlined />,
          label: 'Required Diversity Certifications',
          description: Array.isArray(publicData.diversityCertificationsRFP) ? (
            publicData.diversityCertificationsRFP.map((item, index) => (
              <Tag key={index} color="volcano">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="volcano">{publicData.diversityCertificationsRFP}</Tag>
          ),
        });
      }

      if (publicData.dataProtectionControlsRFP) {
        items.push({
          category: 'Supplier Verification',
          icon: <DatabaseOutlined />,
          label: 'Data protection controls',
          description: publicData.dataProtectionControlsRFP,
        });
      }

      if (publicData.sanctionsDebarmentScreenRFP) {
        items.push({
          category: 'Supplier Verification',
          icon: <SendOutlined />,
          label: 'Sanctions / debarment screen',
          description: publicData.sanctionsDebarmentScreenRFP,
        });
      }

      // Additional RFP Fields
      if (publicData.archSummaryRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <InfoCircleOutlined />,
          label: 'Summary of Current Systems and Architecture',
          description: publicData.archSummaryRFP,
        });
      }
      if (publicData.archVisionRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <EyeOutlined />,
          label: 'To-Be Architecture Vision',
          description: publicData.archVisionRFP,
        });
      }
      if (publicData.systemInterfacesIntegrationPointsRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <NodeIndexOutlined />,
          label: 'System Interfaces and Required Integration Points',
          description: publicData.systemInterfacesIntegrationPointsRFP,
        });
      }
      if (publicData.dataMigrationScopeRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <DatabaseOutlined />,
          label: 'Volume of Data to Migrate (records, size)',
          description: publicData.dataMigrationScopeRFP,
        });
      }
      if (publicData.sampleSchemasRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <FileTextOutlined />,
          label: 'Sample Schemas / Payloads',
          description: publicData.sampleSchemasRFP,
        });
      }
      if (publicData.securityRequirementsRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <SecurityScanOutlined />,
          label: 'Security Requirements Suppliers Must Meet',
          description: Array.isArray(publicData.securityRequirementsRFP) ? (
            publicData.securityRequirementsRFP.map((item, index) => (
              <Tag key={index} color="red">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="red">{publicData.securityRequirementsRFP}</Tag>
          ),
        });
      }
      if (publicData.securityAndComplianceRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <SafetyCertificateOutlined />,
          label: 'Applicable Compliance Frameworks',
          description: Array.isArray(publicData.securityAndComplianceRFP) ? (
            publicData.securityAndComplianceRFP.map((item, index) => (
              <Tag key={index} color="orange">
                {item}
              </Tag>
            ))
          ) : (
            <Tag color="orange">{publicData.securityAndComplianceRFP}</Tag>
          ),
        });
      }
      if (publicData.nonFuncReqRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <FileTextOutlined />,
          label: 'Non-functional Requirements',
          description: publicData.nonFuncReqRFP,
        });
      }
      if (publicData.environmentStrategyRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <ProjectOutlined />,
          label: 'Environment Strategy',
          description: publicData.environmentStrategyRFP,
        });
      }
      if (publicData.cloudPlatPreferencesRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <GlobalOutlined />,
          label: 'Cloud/Platform Preferences',
          description: publicData.cloudPlatPreferencesRFP,
        });
      }
      if (publicData.deliveryMethodologyRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <CarOutlined />,
          label: 'Delivery Methodology',
          description: publicData.deliveryMethodologyRFP,
        });
      }
      if (publicData.testStrategyRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <AuditOutlined />,
          label: 'Test Strategy',
          description: publicData.testStrategyRFP,
        });
      }
      if (publicData.migrationCutoverPlanRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <NodeIndexOutlined />,
          label: 'Migration & Cutover Plan',
          description: publicData.migrationCutoverPlanRFP,
        });
      }
      if (publicData.BCPDRObjectivesRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <FileProtectOutlined />,
          label: 'BCP/DR Objectives',
          description: publicData.BCPDRObjectivesRFP,
        });
      }
      if (publicData.monitoringLoggingRFP) {
        items.push({
          category: 'Additional RFP fields',
          icon: <EyeOutlined />,
          label: 'Monitoring & Logging Details',
          description: publicData.monitoringLoggingRFP,
        });
      }

      return items;
    };

    const rfpPostingItems = createRFPPostingItems(currentListing);

    // Group items by category
    const groupedItems = rfpPostingItems.reduce((acc, item) => {
      const { category } = item;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    const categories = Object.keys(groupedItems);

    // Map categories to icons for the anchor links
    const categoryIcons = {
      'RFP Identification': <IdcardOutlined />,
      Communications: <MessageOutlined />,
      'Scope of Work': <ProjectOutlined />,
      'Proposal Instructions': <FileTextOutlined />,
      'Supplier Criteria': <CheckCircleOutlined />,
      'Contract and Financials': <FileProtectOutlined />,
      'Supplier Verification': <SafetyCertificateOutlined />,
      'Additional RFP fields': <AppstoreOutlined />,
    };

    const anchorItems = categories.map(category => ({
      key: category,
      href: `#${category.replace(/ /g, '_').replace(/&/g, 'and')}`,
      title: (
        <>
          {categoryIcons[category] || <AppstoreOutlined />} {category}
        </>
      ),
    }));

    const isOwnListingAdmin = mainUserId === currentListing?.attributes?.publicData?.adminId;

    if (
      isOwnListingAdmin &&
      currentListing?.attributes?.privateData &&
      Object.values(currentListing.attributes.privateData || {}).some(
        val => Array.isArray(val) && val.length > 0 && val[0].url
      )
    ) {
      anchorItems.push({
        key: 'attachments-anchor',
        href: '#Attachments',
        title: (
          <>
            <span className={css.anchorBtn}>
              <PaperClipOutlined /> Attachments
            </span>
          </>
        ),
      });
    }

    // activeKeys moved to top-level hooks to avoid changing hooks order between renders

    if (!currentListing?.attributes?.privateData) return null;

    const handleToggle = key => {
      setActiveKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    };

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            ...priceForSchemaMaybe(price),
            ...availabilityMaybe,
          },
        }}
      >
        <LayoutSingleColumn
          className={css.pageRoot}
          topbar={
            <>
              <TopbarContainer />
            </>
          }
          currentUser={currentUser}
        >
          {/* This is where you can reuse the job_listing UI structure for rfp_listing */}
          <div className={css.listingContainer}>
            <div className={css.profileBox}>
              <div className={css.profileCard}>
                <div className={css.profileHero}>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={18}>
                      <div className={css.profileCoverDetails}>
                        <div className={css.listingTitleRow}>
                          <h3 className={css.listingTitleText}>{currentListing?.attributes?.title}</h3>
                          {rfpPublicId ? <span className={css.listingPublicId}>{rfpPublicId}</span> : null}
                        </div>
                        <Row gutter={[16, 8]} className={css.iconLists} align="start">
                          <Col xs={24} md={12}>
                            <Space size="middle" direction="vertical" wrap>
                              <Text>
                                <CalendarOutlined /> <strong>Posted:</strong>{' '}
                                {formatDate(publicData?.postedAt)}
                              </Text>
                              <Text>
                                <FileTextOutlined /> <strong>Category:</strong>{' '}
                                {publicData.categoryRFP}{' '}
                                {publicData.categoryCodeRFP
                                  ? `(${publicData.categoryCodeRFP})`
                                  : ''}
                              </Text>
                              <Text>
                                <SafetyCertificateOutlined /> <strong>Technology Focus:</strong>{' '}
                                {Array.isArray(publicData.projTechFocusSpecNeedsRFP)
                                  ? publicData.projTechFocusSpecNeedsRFP.map((item, index) => (
                                    <>{item}</>
                                  ))
                                  : publicData.projTechFocusSpecNeedsRFP}
                              </Text>
                              <Text>
                                <ClockCircleOutlined /> <strong>Submission Due Date:</strong>{' '}
                                {publicData?.submissionDeadlineRFP}
                                CST
                              </Text>
                            </Space>
                          </Col>
                          <Col xs={24} md={12}>
                            <Space size="middle" direction="vertical" wrap>
                              <Text>
                                <TeamOutlined /> <strong>Org Type:</strong>{' '}
                                {publicData?.issuingOrgTypeRFP}
                              </Text>
                              <Text>
                                <ClockCircleOutlined /> <strong>Contract Term/Duration:</strong>{' '}
                                {publicData?.contractTermDurationRFP}
                              </Text>
                              <Text>
                                <SettingOutlined /> <strong>Service Domain:</strong>{' '}
                                {Array.isArray(publicData.ProjectServiceDomainRFP) ? (
                                  publicData.ProjectServiceDomainRFP.map((item, index) => (
                                    <>{item}</>
                                  ))
                                ) : (
                                  <>{publicData.ProjectServiceDomainRFP}</>
                                )}
                              </Text>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col
                      xs={24}
                      md={6}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div className={css.avatarImg}>
                        {currentListingAdmin?.profileImage?.attributes?.variants['default']?.url ? (
                          <Avatar
                            size={200}
                            src={
                              currentListingAdmin?.profileImage?.attributes?.variants['default']
                                ?.url
                            }
                          />
                        ) : (
                          <Avatar size={200} src={partnerImg1} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className={css.avatarInfo}>
                  <div className={css.listNav}>
                    <Flex gap="small" wrap justify={{ base: 'flex-start', md: 'flex-end' }}>
                      {/* <Button
                                            icon={<LinkOutlined />}
                                            size='large'
                                            onClick={() => {
                                                showModal('modalResume');
                                            }}
                                        >
                                            Attachments
                                        </Button> */}

                      {/* A posting read only through a prime's forward: the server said who shared it. */}
                      {postingForward ? <ForwardedPostingNotice sharedBy={postingForward.sharedBy} /> : null}
                      {/* A supplier who manages its network forwards another company's posting to its sub-suppliers. */}
                      {currentUserIsAgency && !isOwnListing && !isOwnListingAdmin && !postingForward && managesNetwork(currentUser) ? (
                        <TellSubSuppliersButton
                          listingId={currentListing?.id?.uuid}
                          title={currentListing?.attributes?.title}
                          listingType={currentListing?.attributes?.publicData?.listingType}
                        />
                      ) : null}

                      {!postingForward &&
                        mainUserId !== currentListing?.attributes?.publicData?.adminId &&
                        !currentUserIsCustomer && (
                          <>
                            {!applied &&
                              !isAppliedToListing &&
                              !isApplyStatePending && (
                                <Button
                                  iconPosition="end"
                                  size="large"
                                  type={!applying ? 'primary' : 'default'}
                                  onClick={async () => {
                                    if (!currentUser) {
                                      setLoginModalAgencyOnly(true);
                                      setIsLoginModalOpen(true);
                                      return;
                                    }
                                    if (!currentUserIsAgency) {
                                      message.error('Only suppliers can apply to RFPs.');
                                      return;
                                    }

                                    if (
                                      !subscriptionStatus ||
                                      !subscriptionStatus?.hasSubscription ||
                                      subscriptionStatus?.isExpired
                                    ) {
                                      message.error(
                                        'You should have an active subscription to apply to RFPs.'
                                      );
                                      return;
                                    }

                                    const requiredVerificationsRFP =
                                      currentListing?.attributes?.publicData
                                        ?.requiredVerificationsRFP || {};
                                    const requiredSecurityCertificationsRFP =
                                      currentListing?.attributes?.publicData
                                        ?.securityCertificationsRFP || [];
                                    const requiredDiversityCertificationsRFP =
                                      currentListing?.attributes?.publicData
                                        ?.diversityCertificationsRFP || [];
                                    const requiredVendorCertificationsRFP =
                                      currentListing?.attributes?.publicData
                                        ?.certificationsStandardsRFP || [];

                                    // const securityAndComplianceStandards = currentUser?.attributes?.profile?.publicData?.securityAndComplianceStandards || [];
                                    // const certifications = currentUser?.attributes?.profile?.publicData?.certifications || [];
                                    // const certificationsOrDesignations = currentUser?.attributes?.profile?.publicData?.onBoardingInfo?.certificationsOrDesignations || [];

                                    const securityAndComplianceStandards =
                                      profileListing?.attributes?.publicData
                                        ?.securityAndComplianceStandards || [];
                                    const certifications =
                                      profileListing?.attributes?.publicData?.certifications || [];
                                    const certificationsOrDesignations =
                                      profileListing?.attributes?.publicData?.onBoardingInfo
                                        ?.certificationsOrDesignations || [];

                                    // Combine required fields
                                    const requiredAll = [
                                      ...requiredSecurityCertificationsRFP,
                                      ...requiredDiversityCertificationsRFP,
                                      ...requiredVendorCertificationsRFP,
                                    ];

                                    // Combine user's fields
                                    const userAll = [
                                      ...securityAndComplianceStandards,
                                      ...certifications,
                                      ...certificationsOrDesignations,
                                    ];

                                    // Check if all required values are present in the user's combined array
                                    const hasAllRequiredCerts = requiredAll.every(requiredValue =>
                                      userAll.includes(requiredValue)
                                    );

                                    if (!hasAllRequiredCerts) {
                                      message.error(
                                        'You do not meet all the required certifications.'
                                      );
                                      return;
                                    }

                                    // Check if user has free applications remaining
                                    if (
                                      subscriptionStatus?.hasSubscription &&
                                      !subscriptionStatus?.expired &&
                                      rfpFreeApplicationsCount < freeApplicationLimit &&
                                      (subscriptionStatus?.planType ===
                                        'Verified Plus Delivery Vendor Yearly' ||
                                        subscriptionStatus?.planType ===
                                        'Verified Plus Delivery Vendor Monthly')
                                    ) {
                                      // Apply directly without payment
                                      setApplying(true);
                                      setApplied(true);
                                      onInitiateApplyTransaction(currentListing?.id?.uuid, false, '', '', false, [], [], null, 'pending')
                                        .then(() => {
                                          setApplying(false);
                                          const periodText = isMonthlyPlan
                                            ? 'this month'
                                            : 'this year';
                                          message.success(
                                            `Application sent successfully! You have used ${rfpFreeApplicationsCount +
                                            1} out of ${freeApplicationLimit} free application${freeApplicationLimit > 1 ? 's' : ''
                                            } ${periodText}.`
                                          );
                                        })
                                        .catch(error => {
                                          setApplying(false);
                                          setApplied(false);
                                          message.error(
                                            'Failed to send application. Please try again.'
                                          );
                                        });
                                    } else {
                                      // Show payment modal
                                      setShowPaymentModal(true);
                                    }
                                  }}
                                  disabled={
                                    sendInquiryInProgress ||
                                    isAppliedToListing ||
                                    isApplyStatePending ||
                                    applying
                                  }
                                >
                                  {!applying ? (
                                    isAppliedToListing ? (
                                      `${isUserInvited ? 'Invitation accepted' : 'Applied'}`
                                    ) : (
                                      `${isUserInvited ? 'Accept invitation' : 'Apply'}`
                                    )
                                  ) : (
                                    <Spinner />
                                  )}
                                </Button>
                              )}
                            {isAppliedToListing && (
                              <Tag
                                color="rgba(0, 119, 0, 0.6)"
                                style={{ fontSize: '15px', height: '40px', alignContent: 'center' }}
                              >
                                {isUserInvited ? 'Invitation accepted' : 'Applied'}
                              </Tag>
                            )}

                            {/* Free Applications Indicator for Verified Plus Delivery Vendor */}
                          </>
                        )}
                      {/* {!currentUser && (
                        <Button
                          iconPosition="end"
                          size="large"
                          type="primary"
                          onClick={() => {
                            message.error("Please login to apply!");
                            return;
                          }}
                        >
                          Apply
                        </Button>
                      )} */}
                      <Button
                        icon={<CopyOutlined />}
                        size="large"
                        onClick={() => {
                          const currentUrl = window.location.href;
                          navigator.clipboard
                            .writeText(currentUrl)
                            .then(() => {
                              message.success('Page URL copied to clipboard!');
                            })
                            .catch(() => {
                              message.error('Failed to copy URL');
                            });
                        }}
                      >
                        Copy link
                      </Button>
                    </Flex>
                  </div>
                </div>
                <div className={css.profileListContainer}>
                  <div className={css.profileViewLists}>
                    <div className={css.profileDescription}>
                      <div className={css.profileTabsAnchor}>
                        <Affix offsetTop={72}>
                          <Anchor direction="vertical" targetOffset={60} items={anchorItems} />
                        </Affix>
                      </div>
                    </div>

                    <div className={css.profileAllList}>
                      {currentListing?.attributes?.publicData?.ProjectBackgroundObjectivesRFP && (
                        <div id="Project_Background">
                          <>
                            <h4 className={css.profileListTitleHead}>
                              Project background / objectives
                            </h4>
                            <div
                              style={{
                                padding: '16px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '8px',
                                border: '1px solid #d6e7ff',
                                marginBottom: '16px',
                              }}
                            >
                              <span
                                style={{
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '16px',
                                }}
                              >
                                {
                                  currentListing.attributes.publicData
                                    .ProjectBackgroundObjectivesRFP
                                }
                              </span>
                            </div>
                          </>
                        </div>
                      )}

                      {/* Dynamically render each category section and its items */}
                      {categories.map(category => (
                        <div id={category.replace(/ /g, '_')} key={category}>
                          <h4 className={css.profileListTitleHead}>{category}</h4>
                          <List
                            itemLayout="horizontal"
                            dataSource={groupedItems[category]}
                            className={css.profileCompactList}
                            renderItem={item => (
                              <List.Item>
                                <List.Item.Meta
                                  avatar={<div className={css.profileListAvatar}>{item.icon}</div>}
                                  title={<span className={css.profileListTitle}>{item.label}</span>}
                                  description={
                                    <div className={css.profileListdescription}>
                                      {item.description}
                                    </div>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                        </div>
                      ))}

                      <div id="Attachments">
                        <h4 className={css.profileListTitleHead}>Attached documents</h4>

                        <Collapse
                          bordered={false}
                          expandIconPosition="end"
                          activeKey={activeKeys}
                          onChange={keys => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                          style={{ background: '#fff' }}
                          expandIcon={({ isActive }) => (
                            <DownOutlined
                              style={{
                                transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                              }}
                            />
                          )}
                        >
                          {isOwnListingAdmin &&
                            Object.entries(currentListing.attributes.privateData || {})
                              .filter(
                                ([key, documents]) =>
                                  Array.isArray(documents) && documents.length > 0 && key !== 'images'
                              )
                              .map(([key, documents]) => {
                                const label = key
                                  .replace(/Documents$/, '')
                                  .replace(/([A-Z])/g, ' $1')
                                  .trim()
                                  .split(' ')
                                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ');

                                return (
                                  <Collapse.Panel
                                    header={<span style={{ fontWeight: 600 }}>{label}</span>}
                                    key={key}
                                    onClick={() => handleToggle(key)}
                                    style={{
                                      border: '1px solid #e0e0e0',
                                      borderRadius: '8px',
                                      marginBottom: '8px',
                                      overflow: 'hidden',
                                      backgroundColor: '#fff',
                                    }}
                                  >
                                    <div
                                      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                    >
                                      {documents.map((doc, index) => (
                                        <a
                                          key={index}
                                          href={doc.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '12px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '6px',
                                            backgroundColor: '#fafafa',
                                            color: '#333',
                                            textDecoration: 'none',
                                            transition: 'all 0.2s',
                                          }}
                                          onMouseEnter={e => {
                                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                                            e.currentTarget.style.borderColor = '#0066cc';
                                          }}
                                          onMouseLeave={e => {
                                            e.currentTarget.style.backgroundColor = '#fafafa';
                                            e.currentTarget.style.borderColor = '#e0e0e0';
                                          }}
                                        >
                                          <FileOutlined
                                            style={{ fontSize: '18px', color: '#0066cc' }}
                                          />
                                          <span style={{ fontSize: '14px' }}>
                                            {doc.name || `Document ${index + 1}`}
                                          </span>
                                        </a>
                                      ))}
                                    </div>
                                  </Collapse.Panel>
                                );
                              })}
                        </Collapse>
                      </div>
                    </div>
                  </div>
                </div>

                {mainUserId !== currentListing?.attributes?.publicData?.adminId &&
                  (currentUserIsCustomer || enableMessageButton) && (
                    <FloatButton.Group
                      trigger="click"
                      type="primary"
                      description="Message User"
                      style={{ insetInlineEnd: 30 }}
                      icon={<CommentOutlined />}
                      onClick={handleContactUserClick}
                    />
                  )}
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              </div>
            </div>
          </div>
          {/* Payment Confirmation Modal */}
          <Modal
            title={
              (subscriptionStatus.planType === 'Verified Plus Delivery Vendor Monthly' ||
                subscriptionStatus.planType === 'Verified Plus Delivery Vendor Yearly') &&
                rfpFreeApplicationsCount >= freeApplicationLimit
                ? 'Free Applications Limit Reached'
                : 'Payment Required'
            }
            open={showPaymentModal}
            onOk={() => {
              setShowPaymentModal(false);
              // handleRFPApplication();
              // const path = window.location.href.replace(window.location.origin, '');
              const isUserInvited = isInvitedToPosting(currentListing, { currentUser, profileListing, mainUserId });
              if (typeof localStorage != 'undefined') {
                localStorage.setItem('rfpId', currentListing?.id?.uuid);
                localStorage.setItem('isUserInvited', isUserInvited ? 'true' : 'false');
              }
              const path = typeof window !== 'undefined' ? window.location.pathname.slice(1) : '';

              onPayForRFP(rfpPrice?.id, 1, 'rfpApplication', path);
            }}
            onCancel={() => setShowPaymentModal(false)}
            okText={`Pay ${formatPrice(rfpPrice)}`}
            cancelText="Cancel"
            width={450}
          >
            {subscriptionStatus.planType === 'Verified Plus Delivery Vendor Monthly' ||
              subscriptionStatus.planType === 'Verified Plus Delivery Vendor Yearly' ? (
              rfpFreeApplicationsCount >= freeApplicationLimit ? (
                <>
                  <p>
                    You have used all{' '}
                    <strong>
                      {freeApplicationLimit} free RFP application
                      {freeApplicationLimit > 1 ? 's' : ''}
                    </strong>{' '}
                    included in your {isMonthlyPlan ? 'monthly' : 'yearly'} subscription.
                  </p>
                  <p>
                    To apply to more RFPs, you need to pay <strong>{formatPrice(rfpPrice)}</strong>{' '}
                    per application.
                  </p>
                  <p>Would you like to proceed with the payment?</p>
                </>
              ) : (
                <>
                  <p>
                    Your subscription includes{' '}
                    <strong>
                      {freeApplicationLimit} free RFP application
                      {freeApplicationLimit > 1 ? 's' : ''}
                    </strong>{' '}
                    per {isMonthlyPlan ? 'month' : 'year'}.
                  </p>
                  <p>
                    You have used <strong>{rfpFreeApplicationsCount}</strong> out of{' '}
                    {freeApplicationLimit} free application{freeApplicationLimit > 1 ? 's' : ''}.
                  </p>
                  <p>
                    You can apply to{' '}
                    <strong>{freeApplicationLimit - rfpFreeApplicationsCount}</strong> more RFP
                    {freeApplicationLimit - rfpFreeApplicationsCount > 1 ? 's' : ''} for free.
                  </p>
                  <p>
                    Would you like to pay <strong>{formatPrice(rfpPrice)}</strong> instead of using
                    a free application?
                  </p>
                </>
              )
            ) : (
              <>
                <p>
                  You need to pay <strong>{formatPrice(rfpPrice)}</strong> to apply to this RFP.
                </p>
                <p>Would you like to proceed with the payment?</p>
              </>
            )}
          </Modal>
          <LoginPopupModal
            isOpen={isLoginModalOpen}
            onClose={() => { setIsLoginModalOpen(false); setLoginModalShowRoleSelect(true); }}
            from={`${location.pathname}${location.search}${location.hash}`}
            showRoleSelect={loginModalShowRoleSelect}
            agencyOnly={loginModalAgencyOnly}
          />
        </LayoutSingleColumn>
      </Page>
    );
  } else if (listingType == 'job_listing') {
    if (currentUserIsAgency && currentUserRole === 'Tech Sales') {
      return <NotFoundPage staticContext={props.staticContext} />;
    }

    const jobPublicId = publicData?.jobRfpId;

    // Create job posting items array with all the fields from the modal
    const createJobPostingItems = listing => {
      const items = [];
      const publicData = listing?.attributes?.publicData;

      // Company
      if (publicData?.companyName) {
        if (currentListingAdmin?.attributes?.profile?.publicData?.profileListingId) {
          items.push({
            category: 'Position Details',
            icon: <ShopOutlined />,
            label: 'Company',
            description: (
              <a
                title="Company"
                href={`/l/${currentListingAdmin?.attributes?.profile?.publicData?.profileListingId}`}
                target="_blank"
              >
                {publicData?.companyName}
              </a>
            ),
          });
        } else {
          items.push({
            category: 'Position Details',
            icon: <ShopOutlined />,
            label: 'Company',
            description: (
              // <a title='Company' href={`/l/${currentListingAdmin?.attributes?.profile?.publicData?.profileListingId}`} target='_blank'>
              <>{publicData?.companyName}</>
              // </a>
            ),
          });
        }
      }

      if (publicData?.createdByDisplayName) {
        items.push({
          category: 'Posting Metadata',
          icon: <UserOutlined />,
          label: 'Recruiter',
          description: publicData?.createdByDisplayName,
        });
      }

      if (publicData?.experienceLevelJob) {
        items.push({
          category: 'Position Details',
          icon: <UserOutlined />,
          label: 'Experience level',
          description: publicData.experienceLevelJob,
        });
      }

      // Expected Start Date
      if (publicData?.expectedStartDateJob) {
        items.push({
          category: 'Position Details',
          icon: <CalendarOutlined />,
          label: 'Expected Start Date',
          description: publicData.expectedStartDateJob,
        });
      }

      if (publicData?.applyByJob) {
        items.push({
          category: 'Position Details',
          icon: <CalendarOutlined />,
          label: <FormattedMessage id="ListingPage.applyBy" />,
          description: publicData.applyByJob,
        });
      }

      // Employment Type
      if (publicData?.employmentTypeJob) {
        items.push({
          category: 'Position Details',
          icon: <UserOutlined />,
          label: 'Employment Type',
          description: <Tag color="blue">{publicData.employmentTypeJob}</Tag>,
        });
      }

      // Work Engagement Type
      if (publicData?.workEngagementTypeJob) {
        items.push({
          category: 'Position Details',
          icon: <TeamOutlined />,
          label: 'Work Engagement Type',
          description: Array.isArray(publicData.workEngagementTypeJob) ? (
            publicData.workEngagementTypeJob.map((type, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                {type}
              </Tag>
            ))
          ) : (
            <Tag color="blue">{publicData.workEngagementTypeJob}</Tag>
          ),
        });
      }

      // Engagement Duration
      if (publicData?.engagementDurationJob) {
        items.push({
          category: 'Position Details',
          icon: <ClockCircleOutlined />,
          label: 'Engagement Duration',
          description: publicData.engagementDurationJob,
        });
      }

      // Work Site Type
      if (publicData?.workSiteTypeJob) {
        items.push({
          category: 'Position Details',
          icon: <EnvironmentOutlined />,
          label: 'Work Site Type',
          description: Array.isArray(publicData.workSiteTypeJob) ? (
            publicData.workSiteTypeJob.map((site, index) => (
              <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                {site}
              </Tag>
            ))
          ) : (
            <Tag color="green">{publicData.workSiteTypeJob}</Tag>
          ),
        });
      }
      // Travel Required
      if (publicData?.isTravelRequiredJob) {
        items.push({
          category: 'Position Details',
          icon: <CarOutlined />,
          label: 'Travel Required',
          description: publicData.isTravelRequiredJob,
        });
      }

      // Salary
      if (publicData?.salaryMinJob && publicData?.salaryMaxJob) {
        items.push({
          category: 'Position Details',
          icon: <DollarOutlined />,
          label: 'Salary/Pay Range (USD/annum)',
          description: `$${publicData.salaryMinJob}-$${publicData.salaryMaxJob}`,
        });
      }

      // Bill Rate
      if (publicData?.billRateMinJob && publicData?.billRateMaxJob) {
        items.push({
          category: 'Position Details',
          icon: <DollarCircleOutlined />,
          label: 'Bill Rate Range (USD/hr)',
          description: `$${publicData.billRateMinJob} - $${publicData.billRateMaxJob}`,
        });
      }

      // Benefits/Perks
      if (publicData?.benifitsPerksJob) {
        items.push({
          category: 'Position Details',
          icon: <GiftOutlined />,
          label: 'Benefits/Perks',
          description: (
            <div>
              <div>
                {Array.isArray(publicData.benifitsPerksJob) ? (
                  publicData.benifitsPerksJob.map((benefit, index) => (
                    <Tag key={index} color="purple" style={{ marginBottom: 4 }}>
                      {benefit}
                    </Tag>
                  ))
                ) : (
                  <Tag color="purple">{publicData.benifitsPerksJob}</Tag>
                )}
              </div>
              {publicData.otherBenefitsPerksJob && (
                <div style={{ marginTop: 8 }}>
                  <span>
                    <strong>Other:</strong> {publicData.otherBenefitsPerksJob}
                  </span>
                </div>
              )}
            </div>
          ),
        });
      }

      // Work authorization required
      if (publicData?.workAuthReqJob) {
        items.push({
          category: 'Position Details',
          icon: <SafetyOutlined />,
          label: 'Work Authorization Required',
          description:
            Array.isArray(publicData.workAuthReqJob) && publicData.workAuthReqJob.length > 0
              ? publicData.workAuthReqJob.join(', ')
              : publicData.workAuthReqJob,
        });
      }

      if (requiresCitizenship(publicData?.workAuthReqJob)) {
        items.push({
          category: 'Position Details',
          icon: <SafetyOutlined />,
          label: 'Justification for the work authorization requirement',
          description: publicData.workAuthReqDetailsJob,
        });
      }

      // Required Skills
      if (Array.isArray(publicData?.requiredSkillsJob) && publicData.requiredSkillsJob.length > 0) {
        items.push({
          category: 'Position Details',
          icon: <ToolOutlined />,
          label: 'Required Skills',
          description: (
            <div>
              {publicData.requiredSkillsJob.map((item, index) => (
                <div key={index} className={css.profileListBox}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <h6 style={{ margin: 0 }}>{item?.name || '-'}</h6>
                    <span>-</span>
                    <span>
                      {item?.skillExp || 0} {item?.skillExp == 1 ? 'year' : 'years'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ),
        });
      }

      // Preferred Skills
      if (Array.isArray(publicData?.preferredSkillsJob) && publicData.preferredSkillsJob.length > 0) {
        items.push({
          category: 'Position Details',
          icon: <ToolOutlined />,
          label: 'Preferred Skills',
          description: (
            <div>
              {publicData.preferredSkillsJob.map((item, index) => (
                <div key={index} className={css.profileListBox}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <h6 style={{ margin: 0 }}>{item?.name || '-'}</h6>
                    <span>-</span>
                    <span>
                      {item?.skillExp || 0} {item?.skillExp == 1 ? 'year' : 'years'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ),
        });
      }

      // Certifications
      if (publicData?.certificationsRequiredJob) {
        items.push({
          category: 'Position Details',
          icon: <SafetyCertificateOutlined />,
          label: 'Are Certifications Required?',
          description: (
            <div>
              <span>{publicData.certificationsRequiredJob}</span>
              {publicData.certificationsRequiredTagsJob &&
                publicData.certificationsRequiredJob === 'Yes' && (
                  <div style={{ marginTop: 4 }}>
                    {Array.isArray(publicData.certificationsRequiredTagsJob) ? (
                      publicData.certificationsRequiredTagsJob.map((cert, index) => (
                        <Tag key={index} color="red" style={{ marginBottom: 4 }}>
                          {cert}
                        </Tag>
                      ))
                    ) : (
                      <Tag color="red">{publicData.certificationsRequiredTagsJob}</Tag>
                    )}
                  </div>
                )}
            </div>
          ),
        });
      }

      // Project Scope/Objective
      if (publicData?.projectScopeObjectiveJob) {
        items.push({
          category: 'Position Details',
          icon: <ProjectOutlined />,
          label: 'Goal of Engagement',
          description: (
            <div
              style={{
                padding: '8px 12px',
                backgroundColor: '#f0f8ff',
                borderRadius: '4px',
                border: '1px solid #d6e7ff',
              }}
            >
              <span
                style={{
                  lineHeight: '1.5',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {publicData.projectScopeObjectiveJob}
              </span>
            </div>
          ),
        });
      }

      // Reporting Line
      if (publicData?.reportingLineJob) {
        items.push({
          category: 'Position Details',
          icon: <NodeIndexOutlined />,
          label: 'Reports To',
          description: (
            <div>
              <span>{publicData.reportingLineJob}</span>
              {publicData.otherReportingLineJob && (
                <span> - {publicData.otherReportingLineJob}</span>
              )}
            </div>
          ),
        });
      }

      // Team Structure
      if (publicData?.teamStructureJob) {
        items.push({
          category: 'Position Details',
          icon: <TeamOutlined />,
          label: 'Team Structure',
          description: (
            <div>
              <span>{publicData.teamStructureJob}</span>
              {publicData.otherTeamStructureJob && (
                <span> - {publicData.otherTeamStructureJob}</span>
              )}
            </div>
          ),
        });
      }

      // Project Type
      if (publicData?.projectTypeJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <FolderOutlined />,
          label: 'Project Type',
          description: publicData.projectTypeJob,
        });
      }

      // End Client Information

      if (publicData?.endClientDisclosureJob === 'Yes') {
        if (publicData?.endClientOrganizationJob) {
          items.push({
            category: 'Client & Project Context',
            icon: <GlobalOutlined />,
            label: 'End Client Organization',
            description: publicData.endClientOrganizationJob,
          });
        }
      }

      if (publicData?.endClientIndustryJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <BankOutlined />,
          label: 'End-Client Industry',
          description: (
            <div>
              <span>{publicData.endClientIndustryJob}</span>
              {publicData.otherEndClientIndustryJob && (
                <span> - {publicData.otherEndClientIndustryJob}</span>
              )}
            </div>
          ),
        });
      }

      // Relationship to End Client
      if (publicData?.relationshipToEndClientJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <LinkOutlined />,
          label: 'Relationship to End Client',
          description: publicData.relationshipToEndClientJob,
        });
      }

      // Upstream Vendors
      if (publicData?.upstreamVendorsJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <ToolOutlined />,
          label: 'Upstream Vendors',
          description: Array.isArray(publicData.upstreamVendorsJob)
            ? publicData.upstreamVendorsJob.map((skill, index) => (
              <Tag key={index} color="orange" style={{ marginBottom: 4 }}>
                {skill}
              </Tag>
            ))
            : publicData.upstreamVendorsJob,
        });
      }

      // MSP / VMS System job
      if (publicData?.MSPVMSSystemJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <FileTextOutlined />,
          label: 'MSP/VMS system that manages submissions for this project',
          description: publicData.MSPVMSSystemJob,
        });
      }

      // Contract Information
      if (publicData?.contractTypeJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <FileTextOutlined />,
          label: 'Contract Type',
          description:
            publicData.contractTypeJob === 'Subcontract (Select if not direct)'
              ? 'Subcontract'
              : publicData.contractTypeJob,
        });
      }

      if (publicData?.contractDurationJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <HistoryOutlined />,
          label: 'Contract Duration with client/prime-vendor',
          description: publicData.contractDurationJob,
        });
      }

      // Subcontracting Rules
      if (publicData?.subContractingRulesJob) {
        items.push({
          category: 'Client & Project Context',
          icon: <AuditOutlined />,
          label: 'Subcontracting Restrictions',
          description: (
            <div>
              <span>{publicData.subContractingRulesJob}</span>
              {publicData.subContractingRulesDetailsJob &&
                publicData.subContractingRulesDetailsJob === 'Yes' && (
                  <div style={{ marginTop: 4 }}>
                    <span>
                      <strong>Details:</strong> {publicData.subContractingRulesDetailsJob}
                    </span>
                  </div>
                )}
            </div>
          ),
        });
      }

      // Layering Disclosure
      if (publicData?.layeringDisclosureJob) {
        items.push({
          category: 'Legal & Compliance',
          icon: <BlockOutlined />,
          label: 'Layering Structure Disclosure',
          description: publicData.layeringDisclosureJob,
        });
      }

      // Security Clearance
      if (publicData?.securityClearanceRequiredJob) {
        items.push({
          category: 'Legal & Compliance',
          icon: <SecurityScanOutlined />,
          label: 'U.S. Security Clearance Required',
          description: (
            <div>
              <span>{publicData.securityClearanceRequiredJob}</span>
              {publicData.securityClearanceRequiredDetailsJob &&
                publicData.securityClearanceRequiredJob === 'Yes' && (
                  <div style={{ marginTop: 4 }}>
                    <span>
                      <strong>Level:</strong> {publicData.securityClearanceRequiredDetailsJob}
                    </span>
                    {publicData.otherSecurityClearanceRequiredDetailsJob && (
                      <span> - {publicData.otherSecurityClearanceRequiredDetailsJob}</span>
                    )}
                  </div>
                )}
            </div>
          ),
        });
      }

      // Compliance Requirements
      if (publicData?.layeringDisclosureJob === 'Yes' && publicData?.complianceRequirementsJob) {
        items.push({
          category: 'Legal & Compliance',
          icon: <CheckCircleOutlined />,
          label: 'Compliance Requirements from end-client/vendor chain',
          description: (
            <div>
              <div>
                {Array.isArray(publicData.complianceRequirementsJob) ? (
                  publicData.complianceRequirementsJob.map((req, index) => (
                    <Tag key={index} color="volcano" style={{ marginBottom: 4 }}>
                      {req}
                    </Tag>
                  ))
                ) : (
                  <Tag color="volcano">{publicData.complianceRequirementsJob}</Tag>
                )}
              </div>
              {publicData.otherComplianceRequirementsJob && (
                <div style={{ marginTop: 8 }}>
                  <span>
                    <strong>Other:</strong> {publicData.otherComplianceRequirementsJob}
                  </span>
                </div>
              )}
            </div>
          ),
        });
      }

      // Diversity Reporting
      if (publicData?.layeringDisclosureJob === 'Yes' && publicData?.diversityReportingJob) {
        items.push({
          category: 'Legal & Compliance',
          icon: <UsergroupAddOutlined />,
          label: 'End-client Requires Diversity Reporting',
          description: (
            <div>
              <span>{publicData.diversityReportingJob}</span>
              {publicData.diversityReportingDetailsJob &&
                publicData.diversityReportingJob === 'Yes' && (
                  <div style={{ marginTop: 4 }}>
                    <span>
                      <strong>Details:</strong> {publicData.diversityReportingDetailsJob}
                    </span>
                  </div>
                )}
            </div>
          ),
        });
      }

      // Vendor Engagement Rules
      // if (publicData?.layeringDisclosureJob === 'Yes' && publicData?.vendorEngagementRulesJob) {
      //   items.push({
      //     category: 'Legal & Compliance',
      //     icon: <SolutionOutlined />,
      //     label: 'Vendor Submission Process',
      //     description: (
      //       <div>
      //         <span>{publicData.vendorEngagementRulesJob}</span>
      //         {publicData.otherVendorEngagementRulesJob && (
      //           <span> - {publicData.otherVendorEngagementRulesJob}</span>
      //         )}
      //       </div>
      //     )
      //   });
      // }

      // Background check required
      if (publicData?.backChekRequiredJob) {
        items.push({
          category: 'Legal & Compliance',
          icon: <AuditOutlined />,
          label: 'Candidate has to clear background check',
          description: (
            <div>
              <span>{publicData.backChekRequiredJob}</span>
              {publicData.backChekRequiredOpJob?.length > 0 &&
                publicData.backChekRequiredJob === 'Yes' && (
                  <div style={{ marginTop: 4 }}>
                    <span>
                      <strong>Types of background check required:</strong>{' '}
                      {publicData.backChekRequiredOpJob.join(', ')}
                    </span>
                    {publicData.otherbackChekRequiredOpJob && (
                      <span> - {publicData.otherbackChekRequiredOpJob}</span>
                    )}
                  </div>
                )}
            </div>
          ),
        });
      }

      // Verification Requirements, without what can no longer be asked for
      const jobRequirements = offeredJobRequirements(publicData?.verificationRequirementsJob);
      if (Array.isArray(jobRequirements) && jobRequirements.length > 0) {
        items.push({
          category: 'Verifications & Trust',
          icon: <VerifiedOutlined />,
          label: 'Verification Requirements',
          description: jobRequirements.join(', '),
        });
      }

      // Job ID
      if (publicData?.jobIdRequisitionNumberJob) {
        items.push({
          category: 'Posting Metadata',
          icon: <TagOutlined />,
          label: 'Requisition Number',
          description: publicData.jobIdRequisitionNumberJob,
        });
      }

      // Applications
      items.push({
        category: 'Posting Metadata',
        icon: <UserAddOutlined />,
        label: 'Applications',
        description:
          (publicData?.noOfApplications || 0) > 0
            ? `👥 ${publicData.noOfApplications} applicants`
            : 'No applicants yet',
      });

      return items;
    };

    // Get the job posting items
    const jobpostingitems = createJobPostingItems(currentListing);

    // Group items by category
    const groupedItems = jobpostingitems.reduce((acc, item) => {
      const { category } = item;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    const categories = Object.keys(groupedItems);

    // Map categories to icons for the anchor links
    const categoryIcons = {
      'Position Details': <IdcardOutlined />,
      'Client & Project Context': <ProjectOutlined />,
      'Legal & Compliance': <SafetyCertificateOutlined />,
      'Verifications & Trust': <VerifiedOutlined />,
      'Posting Metadata': <TagOutlined />,
    };

    // Dynamically create anchor items from the available categories
    const anchorItems = categories.map(category => ({
      key: category,
      href: `#${category.replace(/ /g, '_').replace(/&/g, 'and')}`, // Create a valid href
      title: (
        <>
          {categoryIcons[category] || <AppstoreOutlined />} {category}
        </>
      ),
    }));

    // anchorItems.push({
    //     key: 'attachments-anchor',
    //     href: '#Attachments',
    //     title: (
    //         <>
    //             <span className={css.anchorBtn}><PaperClipOutlined /> Attachments</span>
    //         </>
    //     ),
    // });

    const isOwnListingAdmin = mainUserId === currentListing?.attributes?.publicData?.adminId;

    // Manually add the "Attachments" link if there are any documents
    if (
      isOwnListingAdmin &&
      currentListing?.attributes?.privateData &&
      Object.values(currentListing.attributes.privateData || {}).some(
        val => Array.isArray(val) && val.length > 0 && val[0].url
      )
    ) {
      anchorItems.push({
        key: 'attachments-anchor',
        href: '#Attachments',
        title: (
          <>
            <span className={css.anchorBtn}>
              <PaperClipOutlined /> Attachments
            </span>
          </>
        ),
      });
    }

    const privateData = currentListing?.attributes?.privateData || {};

    const handleToggle = key => {
      setActiveKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    };

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        description={description}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={{
          '@context': 'http://schema.org',
          '@type': 'Product',
          description: description,
          name: schemaTitle,
          image: schemaImages,
          offers: {
            '@type': 'Offer',
            url: productURL,
            ...priceForSchemaMaybe(price),
            ...availabilityMaybe,
          },
        }}
      >
        <LayoutSingleColumn
          className={css.pageRoot}
          topbar={
            <>
              <TopbarContainer />
            </>
          }
          currentUser={currentUser}
        >
          <div className={css.listingContainer}>
            <div className={css.profileBox}>
              <div className={css.profileCard}>
                <div className={css.profileHero}>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={18}>
                      <div className={css.profileCoverDetails}>
                        <div className={css.listingTitleRow}>
                          <h3 className={css.listingTitleText}>{currentListing?.attributes?.title}</h3>
                          {jobPublicId ? <span className={css.listingPublicId}>{jobPublicId}</span> : null}
                        </div>
                        <Row gutter={[16, 8]} className={css.iconLists} align="start">
                          <Col xs={24} md={12}>
                            <Space size="middle" direction="vertical" wrap>
                              {publicData?.companyName ?
                                <Text>
                                  <BankOutlined /> <strong>Company:</strong>{' '}
                                  {currentListingAdminProfileListingId ? (
                                    <a
                                      title="Company"
                                      href={`/l/${currentListingAdminProfileListingId}`}
                                      className={css.companyMetaLink}
                                      target="_blank"
                                    >
                                      {publicData?.companyName}
                                    </a>
                                  ) : (
                                    publicData?.companyName
                                  )}
                                </Text>
                                : null
                              }
                              <Text>
                                <CalendarOutlined /> <strong>Posted on:</strong>{' '}
                                {formatDate(publicData?.postedAt)}
                              </Text>
                              <Text>
                                <FileTextOutlined /> <strong>Employment type:</strong>{' '}
                                {publicData?.employmentTypeJob}
                              </Text>
                              <Text>
                                <SafetyCertificateOutlined /> <strong>Required skills:</strong>{' '}
                                {Array.isArray(publicData?.requiredSkillsJob)
                                  ? publicData.requiredSkillsJob.map((item, index) => (
                                    <React.Fragment key={index}>
                                      {item?.name}
                                      {index < publicData.requiredSkillsJob.length - 1 ? ', ' : ''}
                                    </React.Fragment>
                                  ))
                                  : '-'}
                              </Text>
                              <Text>
                                <ClockCircleOutlined /> <strong>Expected Start Date:</strong>{' '}
                                {publicData?.expectedStartDateJob
                                  ? `${publicData?.expectedStartDateJob} CST`
                                  : 'Not Specified'}
                              </Text>
                            </Space>
                          </Col>
                          <Col xs={24} md={12}>
                            <Space size="middle" direction="vertical" wrap>
                              {publicData?.createdByDisplayName ?
                                <Text>
                                  <UserOutlined /> <strong>Posted by:</strong> {publicData?.createdByDisplayName}
                                </Text>
                                : null
                              }
                              <Text>
                                <TeamOutlined /> <strong>Experience Level:</strong>{' '}
                                {publicData?.experienceLevelJob}
                              </Text>
                              <Text>
                                <TrophyOutlined /> <strong>Work-Site Type:</strong>{' '}
                                {Array.isArray(publicData?.workSiteTypeJob)
                                  ? publicData?.workSiteTypeJob.join(', ')
                                  : publicData?.workSiteTypeJob}
                              </Text>
                              <Text>
                                <OneToOneOutlined /> <strong>Bill Rate Range:</strong> $
                                {publicData?.billRateMinJob} - ${publicData?.billRateMaxJob} / hr
                              </Text>
                              <Text>
                                <EnvironmentFilled /> <strong>Job Location:</strong>{' '}
                                {publicData?.jobLocationJob?.search ||
                                  publicData?.jobLocationJob?.selectedPlace?.address ||
                                  publicData?.jobLocationJob}
                              </Text>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col
                      xs={24}
                      md={6}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div className={css.avatarImg}>
                        {currentListingAdmin?.profileImage?.attributes?.variants['default']?.url ? (
                          <Avatar
                            size={200}
                            src={
                              currentListingAdmin?.profileImage?.attributes?.variants['default']
                                ?.url
                            }
                          />
                        ) : (
                          <Avatar size={200} src={partnerImg1} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className={css.avatarInfo}>
                  <div className={css.listNav}>
                    <Flex gap="small" wrap justify={{ base: 'flex-start', md: 'flex-end' }}>
                      {/* A posting read only through a prime's forward: the server said who shared it. */}
                      {postingForward ? <ForwardedPostingNotice sharedBy={postingForward.sharedBy} /> : null}
                      {/* A supplier who manages its network tells its consultants about a customer's job. */}
                      {currentUserIsAgency && !isOwnListing && !isOwnListingAdmin && !postingForward && managesNetwork(currentUser) ? (
                        <TellConsultantsButton listingId={currentListing?.id?.uuid} title={currentListing?.attributes?.title} />
                      ) : null}
                      {/* A supplier who manages its network forwards another company's posting to its sub-suppliers. */}
                      {currentUserIsAgency && !isOwnListing && !isOwnListingAdmin && !postingForward && managesNetwork(currentUser) ? (
                        <TellSubSuppliersButton
                          listingId={currentListing?.id?.uuid}
                          title={currentListing?.attributes?.title}
                          listingType={currentListing?.attributes?.publicData?.listingType}
                        />
                      ) : null}

                      {!postingForward &&
                        mainUserId !== currentListing?.attributes?.publicData?.adminId &&
                        !currentUserIsCustomer && (
                          <>
                            {currentUserIsAgency && (
                              <Button
                                iconPosition="end"
                                size="large"
                                type={!applying ? 'primary' : 'default'}
                                onClick={async () => {
                                  if (!currentUser) {
                                    setLoginModalAgencyOnly(false);
                                    setIsLoginModalOpen(true);
                                    return;
                                  }

                                  if (!isProfileComplete) {
                                    history.push({
                                      pathname: '/profile-settings',
                                      search: `?applyWorkId=${encodeURIComponent(currentListing.id.uuid)}`,
                                    });
                                    return;
                                  }

                                  if (currentUserIsAgency) {
                                    if (acceptedConsultantsExist === false) {
                                      message.error(NO_ACCEPTED_CONSULTANT);
                                      return;
                                    }

                                    if (
                                      !subscriptionStatus ||
                                      !subscriptionStatus?.hasSubscription ||
                                      subscriptionStatus?.isExpired
                                    ) {
                                      const numberOfActiveApplications = numOfProcessingApplications(
                                        allApplyTransactions
                                      );

                                      if (numberOfActiveApplications >= 10) {
                                        message.error(
                                          'You should have an active subscription to have more than 10 active job applications as a supplier.'
                                        );
                                        return;
                                      }
                                    }

                                    openAgencyApply();
                                    return;
                                  }
                                }}
                                disabled={sendInquiryInProgress || applying}
                              >
                                {!applying ? 'Apply' : <Spinner />}
                              </Button>
                            )}
                            {!isOwnListing &&
                              currentUserIsCandidate &&
                              !isAppliedToListing &&
                              !isApplyStatePending && (
                                <Button
                                  iconPosition="end"
                                  size="large"
                                  type={!applying ? 'primary' : 'default'}
                                  onClick={async () => {
                                    if (!currentUser) {
                                      setLoginModalAgencyOnly(false);
                                      setIsLoginModalOpen(true);
                                      return;
                                    }

                                    if (!isProfileComplete) {
                                      history.push({
                                        pathname: '/profile-settings',
                                        search: `?applyWorkId=${encodeURIComponent(currentListing.id.uuid)}`,
                                      });
                                      return;
                                    }

                                    // Give unlimited number of active applications for now
                                    // if (currentUserIsCandidate) {
                                    //   const candidateVerified =
                                    //     isCurrentUserIdentityVerified ||
                                    //     isCurrentUserWorkVerified ||
                                    //     isCurrentUserEducationVerified ||
                                    //     isCurrentUserExperienceVerified;

                                    //   if (!candidateVerified) {
                                    //     const numberOfActiveApplications = numOfProcessingApplications(
                                    //       allApplyTransactions
                                    //     );

                                    //     if (numberOfActiveApplications >= 3) {
                                    //       message.error(
                                    //         'You should complete atleast one verification to have more than 3 active applications.'
                                    //       );
                                    //       return;
                                    //     }
                                    //   }
                                    // }

                                    if (currentUserIsAgency) {
                                      if (acceptedConsultantsExist === false) {
                                        message.error(NO_ACCEPTED_CONSULTANT);
                                        return;
                                      }
                                      openAgencyApply();
                                      return;
                                    }

                                    // const currentUserSkills = currentUser?.attributes?.profile?.publicData?.skills || [];
                                    // const requiredVerificationsJob =
                                    //   currentListing?.attributes?.publicData
                                    //     ?.verificationRequirementsJob || {};

                                    // if (
                                    //   Array.isArray(requiredVerificationsJob) &&
                                    //   requiredVerificationsJob.length > 0 &&
                                    //   !requiredVerificationsJob.includes('None')
                                    // ) {
                                    //   const normalize = str => str.toLowerCase();

                                    //   const missingVerifications = requiredVerificationsJob.filter(
                                    //     reqVerif => {
                                    //       const req = normalize(reqVerif);

                                    //       // if (req.includes('education') && isCurrentUserEducationVerified) return false;

                                    //       if (
                                    //         req.includes('work authorization') &&
                                    //         isCurrentUserWorkVerified
                                    //       )
                                    //         return false;
                                    //       // if (req.includes('work experience') && isCurrentUserExperienceVerified) return false;

                                    //       if (
                                    //         req.includes('identity') &&
                                    //         isCurrentUserIdentityVerified
                                    //       )
                                    //         return false;

                                    //       return true;
                                    //     }
                                    //   );

                                    //   if (missingVerifications.length > 0) {
                                    //     message.error(
                                    //       `You are missing the following required verifications to apply: ${missingVerifications.join(
                                    //         ', '
                                    //       )}`
                                    //     );
                                    //     return;
                                    //   }
                                    // }

                                    // Check if ALL required skills are present in the user's skills with required or higher experience
                                    // const hasAllRequiredSkills = currentListing?.attributes?.publicData?.requiredSkillsJob?.every(jobListingSkill => {
                                    //   const matchedSkill = currentUserSkills.find(currentUserSkill =>
                                    //     currentUserSkill.name?.toLowerCase() === jobListingSkill.name?.toLowerCase()
                                    //   );

                                    //   return matchedSkill && Number(matchedSkill.skillExp) >= Number(jobListingSkill.skillExp);
                                    // });

                                    // if (currentUser?.attributes?.profile?.publicData?.userType === 'candidate' && !hasAllRequiredSkills) {
                                    //   message.error("You do not meet all the required skills for this job.");
                                    //   return;
                                    // }

                                    const isUserInvited = isInvitedToPosting(currentListing, {
                                      currentUser,
                                      profileListing,
                                      mainUserId,
                                    });

                                    const resumeToUse = selectedResumeForApplication;
                                    if (currentUserIsCandidate && !resumeToUse?.url) {
                                      message.error('A resume is required to apply. Upload a resume for this application or add one to your profile.');
                                      setShowResumeModal(true);
                                      return;
                                    }

                                    // If documents uploaded, require consent before applying
                                    const hasDocuments = applyIdentityDocs.length > 0 || applyWorkAuthDocs.length > 0;
                                    if (hasDocuments) {
                                      setDocConsentChecked(false);
                                      setPendingApplyIsInvited(isUserInvited);
                                      setPendingApplyResume(resumeToUse);
                                      setDocConsentContext({ mode: 'apply', docType: '' });
                                      setShowDocConsentModal(true);
                                      return;
                                    }

                                    setApplying(true);
                                    setApplied(true);
                                    try {
                                      await onInitiateApplyTransaction(
                                        currentListing?.id?.uuid,
                                        isUserInvited,
                                        '',
                                        '',
                                        false,
                                        applyIdentityDocs,
                                        applyWorkAuthDocs,
                                        resumeToUse,
                                        'pending'
                                      );
                                      message.success('Application sent successfully!');
                                    } catch (error) {
                                      setApplied(false);
                                      message.error('Failed to send application. Please try again.');
                                    } finally {
                                      setApplying(false);
                                    }
                                  }}
                                  disabled={
                                    sendInquiryInProgress ||
                                    isAppliedToListing ||
                                    isApplyStatePending ||
                                    applying
                                  }
                                >
                                  {!applying ? (
                                    isAppliedToListing ? (
                                      `${isUserInvited ? 'Invitation accepted' : 'Applied'}`
                                    ) : (
                                      `${isUserInvited ? 'Accept invitation' : 'Apply'}`
                                    )
                                  ) : (
                                    <Spinner />
                                  )}
                                </Button>
                              )}
                            {currentUserIsCandidate && candidateApplicationStatus === 'pending' && (
                              <Tag
                                color="green"
                                icon={<CheckOutlined />}
                                onClick={() => {
                                  return;
                                }}
                                style={{ marginBottom: 0, fontSize: '15px', height: '40px', alignContent: 'center' }}
                              >
                                {isUserInvited ? 'Invitation accepted' : 'Applied'}
                              </Tag>
                            )}
                            {currentUserIsCandidate && candidateApplicationStatus === 'shortlisted' && (
                              <Tag
                                color="blue"
                                icon={<UnorderedListOutlined />}
                                onClick={() => {
                                  return;
                                }}
                                style={{ marginBottom: 0, fontSize: '15px', height: '40px', alignContent: 'center' }}
                              >
                                Shortlisted
                              </Tag>
                            )}

                            {currentUserIsCandidate && candidateApplicationStatus === 'scheduled' && (
                              <Tag
                                color="purple"
                                icon={<CalendarOutlined />}
                                onClick={() => {
                                  return;
                                }}
                                style={{ marginBottom: 0, fontSize: '15px', height: '40px', alignContent: 'center' }}
                              >
                                Interview Scheduled
                              </Tag>
                            )}

                            {currentUserIsCandidate && candidateApplicationStatus === 'rejected' && (
                              <Tag
                                color="red"
                                icon={<CloseCircleOutlined />}
                                onClick={() => {
                                  return;
                                }}
                                style={{ marginBottom: 0, fontSize: '15px', height: '40px', alignContent: 'center' }}
                              >
                                Rejected
                              </Tag>
                            )}

                            {currentUserIsCandidate && isAppliedToListing && (
                              <>
                                <Button
                                  style={{ fontSize: 15 }}
                                  type="default"
                                  size="large"
                                  icon={<EyeOutlined />}
                                  onClick={() => {
                                    if (resumeToView?.url) {
                                      if (isBlobStorageUrl(resumeToView.url)) {
                                        handleOpenPreview(resumeToView.url, resumeViewName);
                                      } else {
                                        window.open(resumeToView.url, '_blank', 'noopener,noreferrer');
                                      }
                                    } else {
                                      message.info('Resume is not available for preview.');
                                    }
                                  }}
                                >
                                  View Resume
                                </Button>

                                {showPostApplyIdentityUpload ? (
                                  <UploadDocumentButton
                                    modalTitle="Upload Identity Documents"
                                    description="Driver's License, State ID, Passport, Birth Certificate, etc."
                                    buttonLabel="Upload ID"
                                    uploadedLabel="ID Uploaded"
                                    documents={postApplyIdentityDocs}
                                    setDocumentsWithRef={setPostApplyIdentityDocsWithRef}
                                    onUploadBlobFile={createPostApplyUploader('identity')}
                                    isOpen={postApplyIdentityModalOpen}
                                    onOpenChange={handlePostApplyIdentityModalChange}
                                    onBeforeOpen={() => handlePostApplyUploadStart('identity')}
                                    submitLabel="Submit"
                                    submitDisabled={postApplyIdentitySubmitDisabled}
                                    submitLoading={postApplySubmitInProgress.identity}
                                    onSubmit={() => handlePostApplySubmit('identity')}
                                  />
                                ) : (
                                  <Button
                                    style={{ fontSize: 15 }}
                                    type="default"
                                    size="large"
                                    icon={<EyeOutlined />}
                                    onClick={() =>
                                      openDocListModal('Identity Documents', appliedIdentityDocs)
                                    }
                                  >
                                    View ID{appliedIdentityDocs.length > 1 ? ` (${appliedIdentityDocs.length})` : ''}
                                  </Button>
                                )}

                                {showPostApplyWorkUpload ? (
                                  <UploadDocumentButton
                                    modalTitle="Upload Work Authorization Documents"
                                    description="EAD Card, Visa, I-94, Social Security Card, etc."
                                    buttonLabel="Upload Work Docs"
                                    uploadedLabel="Work Docs Uploaded"
                                    documents={postApplyWorkDocs}
                                    setDocumentsWithRef={setPostApplyWorkDocsWithRef}
                                    onUploadBlobFile={createPostApplyUploader('work_auth')}
                                    isOpen={postApplyWorkModalOpen}
                                    onOpenChange={handlePostApplyWorkModalChange}
                                    onBeforeOpen={() => handlePostApplyUploadStart('work_auth')}
                                    submitLabel="Submit"
                                    submitDisabled={postApplyWorkSubmitDisabled}
                                    submitLoading={postApplySubmitInProgress.work_auth}
                                    onSubmit={() => handlePostApplySubmit('work_auth')}
                                  />
                                ) : (
                                  <Button
                                    style={{ fontSize: 15 }}
                                    type="default"
                                    size="large"
                                    icon={<EyeOutlined />}
                                    onClick={() =>
                                      openDocListModal(
                                        'Work Authorization Documents',
                                        appliedWorkDocs
                                      )
                                    }
                                  >
                                    View Work Docs{appliedWorkDocs.length > 1 ? ` (${appliedWorkDocs.length})` : ''}
                                  </Button>
                                )}
                              </>
                            )}

                            {!isOwnListing &&
                              !currentUserIsCustomer &&
                              isProfileComplete &&
                              (currentUserIsAgency || !isAppliedToListing) && (
                                <>
                                  <UploadDocumentButton
                                    modalTitle="Upload Identity Documents"
                                    description="Driver's License, State ID, Passport, Birth Certificate, etc."
                                    buttonLabel="Upload ID"
                                    uploadedLabel="ID Uploaded"
                                    documents={applyIdentityDocs}
                                    setDocumentsWithRef={setApplyIdentityDocs}
                                    onUploadBlobFile={onUploadBlobFile}
                                  />
                                  <UploadDocumentButton
                                    modalTitle="Upload Work Authorization Documents"
                                    description="EAD Card, Visa, I-94, Social Security Card, etc."
                                    buttonLabel="Upload Work Docs"
                                    uploadedLabel="Work Docs Uploaded"
                                    documents={applyWorkAuthDocs}
                                    setDocumentsWithRef={setApplyWorkAuthDocs}
                                    onUploadBlobFile={onUploadBlobFile}
                                  />
                                  {currentUserIsCandidate ? (
                                    <Button
                                      style={{ fontSize: 15 }}
                                      type="default"
                                      size="large"
                                      onClick={() => setShowResumeModal(true)}
                                    >
                                      {applicationResume?.url ? 'Resume Selected' : 'Change Resume'}
                                    </Button>
                                  ) : null}
                                </>
                              )}
                          </>
                        )}
                      {!currentUser && (
                        <>
                          <Button
                            size="large"
                            type="primary"
                            onClick={() => { setLoginModalAgencyOnly(false); setLoginModalShowRoleSelect(false); setIsLoginModalOpen(true); }}
                          >
                            Apply as Candidate
                          </Button>
                          <Button
                            size="large"
                            type="primary"
                            onClick={() => { setLoginModalAgencyOnly(true); setLoginModalShowRoleSelect(false); setIsLoginModalOpen(true); }}
                          >
                            Apply as Supplier
                          </Button>
                        </>
                      )}
                      <Button
                        icon={<CopyOutlined />}
                        size="large"
                        onClick={() => {
                          const currentUrl = window.location.href;
                          navigator.clipboard
                            .writeText(currentUrl)
                            .then(() => {
                              message.success('Page URL copied to clipboard!');
                            })
                            .catch(() => {
                              message.error('Failed to copy URL');
                            });
                        }}
                      >
                        Copy link
                      </Button>
                    </Flex>
                  </div>
                </div>
                <div className={css.profileListContainer}>
                  <div className={css.profileViewLists}>
                    <div className={css.profileAllList} style={{ width: '100%' }}>
                      <Collapse
                        bordered={false}
                        expandIconPosition="end"
                        style={{ background: '#fff' }}
                        expandIcon={({ isActive }) => (
                          <DownOutlined
                            style={{
                              transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s',
                            }}
                          />
                        )}
                      >
                        {currentListing?.attributes?.description && (
                          <Collapse.Panel
                            header={
                              <span style={{ fontWeight: 600 }}>
                                Job description / Responsibilities
                              </span>
                            }
                            key="job_description"
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              overflow: 'hidden',
                              backgroundColor: '#fff',
                            }}
                          >
                            <div
                              style={{
                                padding: '16px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '8px',
                                border: '1px solid #d6e7ff',
                              }}
                            >
                              <span
                                style={{
                                  lineHeight: '1.6',
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '16px',
                                }}
                              >
                                {currentListing.attributes.description}
                              </span>
                            </div>
                          </Collapse.Panel>
                        )}

                        {categories.map(category => (
                          <Collapse.Panel
                            header={
                              <span style={{ fontWeight: 600 }}>
                                {categoryIcons[category] || <AppstoreOutlined />}{' '}
                                {category}
                              </span>
                            }
                            key={category.replace(/ /g, '_').replace(/&/g, 'and')}
                            style={{
                              border: '1px solid #e0e0e0',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              overflow: 'hidden',
                              backgroundColor: '#fff',
                            }}
                          >
                            <List
                              itemLayout="horizontal"
                              dataSource={groupedItems[category]}
                              className={css.profileCompactList}
                              renderItem={item => (
                                <List.Item className={css.fullwidthList}>
                                  <List.Item.Meta
                                    avatar={<div className={css.profileListAvatar}>{item.icon}</div>}
                                    title={<span className={css.profileListTitle}>{item.label}</span>}
                                    description={
                                      <div className={css.profileListdescription}>
                                        {item.description}
                                      </div>
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          </Collapse.Panel>
                        ))}

                        <Collapse.Panel
                          header={
                            <span style={{ fontWeight: 600 }}>
                              <PaperClipOutlined style={{ marginRight: 6 }} />
                              Attached documents
                            </span>
                          }
                          key="attachments"
                          style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            marginBottom: '8px',
                            overflow: 'hidden',
                            backgroundColor: '#fff',
                          }}
                        >
                          <Collapse
                            bordered={false}
                            expandIconPosition="end"
                            activeKey={activeKeys}
                            onChange={keys => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
                            style={{ background: '#fff' }}
                            expandIcon={({ isActive }) => (
                              <DownOutlined
                                style={{
                                  transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s',
                                }}
                              />
                            )}
                          >
                            {isOwnListingAdmin &&
                              Object.entries(currentListing.attributes.privateData || {})
                                .filter(
                                  ([key, documents]) =>
                                    Array.isArray(documents) && documents.length > 0 && key !== 'images'
                                )
                                .map(([key, documents]) => {
                                  const label = key
                                    .replace(/Documents$/, '')
                                    .replace(/([A-Z])/g, ' $1')
                                    .trim()
                                    .split(' ')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ');

                                  return (
                                    <Collapse.Panel
                                      header={<span style={{ fontWeight: 600 }}>{label}</span>}
                                      key={key}
                                      onClick={() => handleToggle(key)}
                                      style={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        marginBottom: '8px',
                                        overflow: 'hidden',
                                        backgroundColor: '#fff',
                                      }}
                                    >
                                      <div
                                        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                                      >
                                        {documents.map((doc, index) => (
                                          <a
                                            key={index}
                                            href={doc.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '8px',
                                              padding: '12px',
                                              border: '1px solid #e0e0e0',
                                              borderRadius: '6px',
                                              backgroundColor: '#fafafa',
                                              color: '#333',
                                              textDecoration: 'none',
                                              transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={e => {
                                              e.currentTarget.style.backgroundColor = '#f0f8ff';
                                              e.currentTarget.style.borderColor = '#0066cc';
                                            }}
                                            onMouseLeave={e => {
                                              e.currentTarget.style.backgroundColor = '#fafafa';
                                              e.currentTarget.style.borderColor = '#e0e0e0';
                                            }}
                                          >
                                            <FileOutlined
                                              style={{ fontSize: '18px', color: '#0066cc' }}
                                            />
                                            <span style={{ fontSize: '14px' }}>
                                              {doc.name || `Document ${index + 1}`}
                                            </span>
                                          </a>
                                        ))}
                                      </div>
                                    </Collapse.Panel>
                                  );
                                })}
                          </Collapse>
                        </Collapse.Panel>
                      </Collapse>
                    </div>
                  </div>
                </div>

                {mainUserId !== currentListing?.attributes?.publicData?.adminId &&
                  (currentUserIsCustomer || enableMessageButton) && (
                    <FloatButton.Group
                      trigger="click"
                      type="primary"
                      description="Message User"
                      style={{ insetInlineEnd: 30 }}
                      icon={<CommentOutlined />}
                      onClick={handleContactUserClick}
                    />
                  )}
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              </div>
            </div>
          </div>
        </LayoutSingleColumn>

        <ConsultantSelectionModal
          isOpen={showConsultantModal}
          onClose={() => setShowConsultantModal(false)}
          listing={currentListing}
          onApplyWithConsultant={handleApplyWithConsultant}
          localAppliedConsultants={localAppliedConsultants}
          onSearch={onSearchAcceptedConsultants}
        />
        <LoginPopupModal
          isOpen={isLoginModalOpen}
          onClose={() => { setIsLoginModalOpen(false); setLoginModalShowRoleSelect(true); }}
          from={`${location.pathname}${location.search}${location.hash}`}
          applyWorkId={rawParams.id}
          showRoleSelect={loginModalShowRoleSelect}
          agencyOnly={loginModalAgencyOnly}
        />

        <ApplicationResumeModal
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          selectedResume={selectedResumeForApplication}
          resumeDisplayName={selectedResumeDisplayName}
          hasUploadedResume={!!applicationResume?.url}
          resumeUploading={resumeUploading}
          onUploadResume={handleApplyResumeUpload}
          onUseProfileResume={() => setApplicationResume(null)}
        />

        <Modal
          open={docListModal.visible}
          title={
            <div className={css.docListModalTitleWrap}>
              <div className={css.docListModalTitleIcon}>
                {(docListModal.title || '').toLowerCase().includes('identity') ? (
                  <IdcardOutlined />
                ) : (docListModal.title || '').toLowerCase().includes('work') ? (
                  <FileProtectOutlined />
                ) : (
                  <FileOutlined />
                )}
              </div>
              <div className={css.docListModalTitleTextBlock}>
                <div className={css.docListModalTitle}>{docListModal.title || 'Documents'}</div>
                <div className={css.docListModalSubtitle}>
                  {(docListModal.docs || []).length}{' '}
                  {(docListModal.docs || []).length === 1 ? 'file' : 'files'} available
                </div>
              </div>
            </div>
          }
          onCancel={() => setDocListModal({ visible: false, title: '', docs: [] })}
          footer={null}
          width={620}
          centered
          wrapClassName={css.docListModalWrap}
          destroyOnClose
        >
          <div className={css.docListList}>
            {(docListModal.docs || []).map((doc, index) => {
              const displayName = formatDocumentNameForDisplay(doc?.name);
              const fallbackName = `Document ${index + 1}`;
              const fileName = displayName || fallbackName;
              const extension =
                fileName && fileName.includes('.')
                  ? fileName
                    .split('.')
                    .pop()
                    .toUpperCase()
                  : null;

              return (
                <Button
                  key={`${doc?.blobName || doc?.url || 'doc'}_${index}`}
                  type="text"
                  className={css.docListItemButton}
                  onClick={() => {
                    setDocListModal({ visible: false, title: '', docs: [] });
                    handleOpenPreview(doc?.url, fileName);
                  }}
                  disabled={!doc?.url}
                >
                  <div className={css.docListItemMain}>
                    <div className={css.docListItemBadge}>
                      {(docListModal.title || '').toLowerCase().includes('identity') ? (
                        <IdcardOutlined />
                      ) : (docListModal.title || '').toLowerCase().includes('work') ? (
                        <FileProtectOutlined />
                      ) : (
                        <FileOutlined />
                      )}
                    </div>
                    <div className={css.docListItemContent}>
                      <div className={css.docListItemName}>{fileName}</div>
                      <div className={css.docListItemHint}>
                        {doc?.url ? 'Open preview' : 'File unavailable'}
                      </div>
                    </div>
                  </div>
                  <div className={css.docListItemRight}>
                    {extension ? <Tag className={css.docListItemTag}>{extension}</Tag> : null}
                  </div>
                </Button>
              );
            })}
            {(docListModal.docs || []).length === 0 ? (
              <div className={css.docListEmptyState}>No documents are available right now.</div>
            ) : null}
          </div>
        </Modal>

        <Modal
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingRight: 32,
              }}
            >
              <Space>
                <FileProtectOutlined style={{ color: '#16a34a' }} />
                <span style={{ fontWeight: 600 }}>{docPreviewModal.name}</span>
              </Space>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  disabled={!docPreviewModal.url}
                  onClick={() => {
                    if (docPreviewModal.url) {
                      const a = document.createElement('a');
                      a.href = docPreviewModal.url;
                      a.download = docPreviewModal.name;
                      a.click();
                    }
                  }}
                >
                  Download
                </Button>
              </Space>
            </div>
          }
          open={docPreviewModal.visible}
          onCancel={() => {
            setDocPreviewModal({ visible: false, url: '', name: '' });
            setPreviewLoading(false);
          }}
          footer={null}
          centered
          width="min(92vw, 1100px)"
          styles={{ body: { height: '82vh', padding: 0, overflow: 'hidden', background: '#f5f5f5' } }}
          zIndex={1100}
          destroyOnClose
        >
          {previewLoading ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Spin size="large" tip="Preparing preview..." />
            </div>
          ) : docPreviewModal.url && (() => {
            const url = docPreviewModal.url;
            const cleanUrl = url.split('?')[0];
            const ext = cleanUrl.split('.').pop().toLowerCase();
            const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            const isPdf = ext === 'pdf';
            const isWord = ['doc', 'docx'].includes(ext);
            const isText = ext === 'txt';

            if (isPdf) {
              return (
                <iframe
                  src={url}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={docPreviewModal.name}
                />
              );
            }

            if (isWord) {
              const officeViewerUrl =
                `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
              return (
                <iframe
                  src={officeViewerUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title={docPreviewModal.name}
                  allow="fullscreen"
                />
              );
            }

            if (isImage) {
              return (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#1a1a1a',
                    overflow: 'auto',
                  }}
                >
                  <img
                    src={url}
                    alt={docPreviewModal.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                    }}
                  />
                </div>
              );
            }

            if (isText) {
              return (
                <iframe
                  src={url}
                  style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
                  title={docPreviewModal.name}
                />
              );
            }

            return (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 16,
                  background: '#fafafa',
                }}
              >
                <FileProtectOutlined style={{ fontSize: 56, color: '#bfbfbf' }} />
                <p style={{ color: '#595959', fontSize: 15 }}>
                  Preview not available for this file type.
                </p>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download to view
                </Button>
              </div>
            );
          })()}
        </Modal>

        {/* Document upload consent modal */}
        <Modal
          open={showDocConsentModal}
          title="Document Upload Consent"
          onCancel={() => {
            setShowDocConsentModal(false);
            setDocConsentChecked(false);
            setPendingApplyResume(null);
            setDocConsentContext({ mode: 'apply', docType: '' });
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setShowDocConsentModal(false);
                setDocConsentChecked(false);
                setPendingApplyResume(null);
                setDocConsentContext({ mode: 'apply', docType: '' });
              }}
            >
              Cancel
            </Button>,
            <Button
              key="apply"
              type="primary"
              disabled={!docConsentChecked}
              loading={applying}
              onClick={async () => {
                if (!docConsentChecked) {
                  message.error('You must provide consent to proceed with the uploaded documents.');
                  return;
                }
                const isPostApplyConsent = docConsentContext?.mode === 'post-apply';
                if (!isPostApplyConsent && currentUserIsCandidate && !pendingApplyResume?.url) {
                  message.error('A resume is required to apply. Upload a resume for this application or add one to your profile.');
                  setShowResumeModal(true);
                  return;
                }
                setApplying(true);
                const isAgencyConsent = docConsentContext?.mode === 'agency';
                // The documents go with the application only once the consent is saved; otherwise
                // the dialog stays open, still ticked, so the person can try again. An agency picks
                // the consultant afterwards, so its consent names only the job.
                let consentId;
                try {
                  consentId = await saveConsentFor('IdWorkAuthDocUpload', {
                    candidateListingId: isAgencyConsent
                      ? undefined
                      : currentUser?.attributes?.profile?.publicData?.profileListingId ||
                        currentUser?.attributes?.profile?.publicData?.firstListingId,
                    jobListingId: currentListing?.id?.uuid,
                  });
                } catch (error) {
                  setApplying(false);
                  if (!(error instanceof ConsentSaveFailedError)) throw error;
                  message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
                  return;
                }
                setShowDocConsentModal(false);

                if (isAgencyConsent) {
                  setAgencyUploadConsentId(consentId);
                  setApplying(false);
                  setShowConsultantModal(true);
                  return;
                }

                if (isPostApplyConsent) {
                  try {
                    await onFetchJobConsent('IdWorkAuthDocUpload', currentListing?.id?.uuid);
                    message.success('Consent submitted successfully.');
                    if (docConsentContext?.docType === 'identity') {
                      setPostApplyIdentityDocs([]);
                      setPostApplyIdentityModalOpen(true);
                    } else if (docConsentContext?.docType === 'work_auth') {
                      setPostApplyWorkDocs([]);
                      setPostApplyWorkModalOpen(true);
                    } else {
                      message.error('Unable to determine which documents to upload.');
                    }
                  } finally {
                    setApplying(false);
                    setDocConsentChecked(false);
                    setDocConsentContext({ mode: 'apply', docType: '' });
                  }
                  return;
                }

                const resetApplyConsent = () => {
                  setApplying(false);
                  setDocConsentChecked(false);
                  setPendingApplyResume(null);
                  setDocConsentContext({ mode: 'apply', docType: '' });
                };
                try {
                  await onInitiateApplyTransaction(
                    currentListing?.id?.uuid,
                    pendingApplyIsInvited,
                    '',
                    '',
                    false,
                    applyIdentityDocs,
                    applyWorkAuthDocs,
                    pendingApplyResume,
                    'pending',
                    consentId
                  );
                  setApplied(true);
                  message.success('Application sent successfully!');
                } catch (error) {
                  if (!isConsentRequiredRefusal(error)) {
                    resetApplyConsent();
                    throw error;
                  }
                  // The server did not accept the consent: reopen the dialog, still ticked, to retry.
                  setApplying(false);
                  setShowDocConsentModal(true);
                  message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
                  return;
                }
                resetApplyConsent();
              }}
            >
              {docConsentContext?.mode === 'apply' ? 'Agree & Apply' : 'Agree & Continue'}
            </Button>,
          ]}
        >
          <div className={css.docConsentCheckbox}>
            <Checkbox
              checked={docConsentChecked}
              onChange={e => setDocConsentChecked(e.target.checked)}
            >
              I consent to the uploaded identity and work authorization documents being accessed
              and reviewed by the job poster and authorized platform administrators for the
              purpose of evaluating my job application.
              <span className={css.docConsentRequired}>*</span>
            </Checkbox>
          </div>
        </Modal>

      </Page >
    );
  }

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={
          <>
            <TopbarContainer />
          </>
        }
        // useDashboardSideNav
        currentUser={currentUser}
      // footer={<FooterContainer />}
      >
        <div className={css.listingContainer}>
          <div className={css.profileBox}>
            <div className={css.profileCard}>
              <div className={css.profileCoverRole}>
                <Badge.Ribbon
                  placement="start"
                  text={
                    listingIsJob
                      ? 'Job post'
                      : listingIsRFP
                        ? 'RFP'
                        : listingIsAgency
                          ? `Supplier - ${getAgencyTypeDisplayLabel(currentListing?.attributes?.publicData?.agencyProfile?.agencyType)}`
                          : listingIsCustomer
                            ? `Customer - ${getCustomerTypeDisplayLabel(currentListing?.attributes?.publicData?.customerProfile?.customerType)}`
                            : 'Candidate'
                  }
                >
                  <div></div>
                </Badge.Ribbon>
              </div>
              <div className={css.profileCover}>
                <img
                  src={currentListing?.attributes?.publicData?.profileCoverImageUploadUrl || cover}
                  alt=""
                  className={css.profileCoverImg}
                />
                <div className={css.profileCoverBtns}>
                  {/* <div
                    className={css.profileCoverUpload}
                  >
                    <Dropdown menu={{ items }} placement="bottomRight">
                      <Button
                        onClick={e => e.preventDefault()}
                        shape="circle"
                        icon={<MoreOutlined />}
                      />
                    </Dropdown>

                  </div> */}

                  <Flex gap="small" wrap justify={{ base: 'flex-start', md: 'flex-end' }}>
                    {/* {contact} */}

                    {/* <Button
                      // rootClassName={css.contact}
                      icon={<SendOutlined />}
                      iconPosition="start"
                      onClick={handleContactUserClick}
                      enforcePagePreloadFor="SignupPage"
                    >
                      <FormattedMessage id="UserCard.contactUser" />
                    </Button> */}

                    {isOwnProfile ? (
                      <Button
                        icon={<FormOutlined />}
                        iconPosition="start"
                        onClick={redirectToProfileSettings}
                      >
                        Edit Profile
                      </Button>
                    ) : null}
                    {!isExternalUser && (
                      <Button
                        icon={<ContactsOutlined />}
                        iconPosition="start"
                        onClick={() => {
                          if (!currentUser && !shareOtpVerified) {
                            message.error('Please Sign-in to view the details!');
                            return;
                          }
                          showModal('modalContact');
                        }}
                      >
                        Contact Info
                      </Button>
                    )}

                    {/* Current Employer Button - Only for Candidates */}
                    {listingIsCandidate &&
                      currentListing?.attributes?.publicData?.currentEmployer?.company?.label && (
                        <Button
                          icon={<HarmonyOSOutlined />}
                          iconPosition="start"
                          onClick={() => {
                            if (!currentUser && !shareOtpVerified) {
                              message.error('Please Sign-in to view the details!');
                              return;
                            }
                            showModal('modalCurrentEmp');
                          }}
                        >
                          Current Employer
                        </Button>
                      )}

                    {listingIsCandidate &&
                      ensuredAuthor?.attributes?.profile?.publicData?.resumeUrl ? (
                      <Button
                        type="primary"
                        icon={<IdcardOutlined />}
                        onClick={() => {
                          if (isExternalUser) {
                            message.error('Please Sign-in to view the details');
                            return;
                          }
                          showModal('modalResume');
                        }}
                      >
                        Resume
                      </Button>
                    ) : null}
                    {!isExternalUser ? (
                      listingIsCandidate ? (
                        <Button
                          type="primary"
                          icon={<ExportOutlined />}
                          onClick={() => {
                            setInviteModalOpen(true);
                          }}
                        >
                          Invite to job
                        </Button>
                      ) : listingIsAgency && listingAgencyType === 'Implementation Service' ? (
                        <Button
                          type="primary"
                          icon={<ExportOutlined />}
                          onClick={() => {
                            setInviteModalOpen(true);
                          }}
                        >
                          Invite to an RFP
                        </Button>
                      ) : listingIsAgency && listingAgencyType === 'Staffing Services' ? (
                        <Button
                          type="primary"
                          icon={<ExportOutlined />}
                          onClick={() => {
                            setInviteModalOpen(true);
                          }}
                        >
                          Invite to job
                        </Button>
                      ) : listingIsAgency ? (
                        <Button
                          type="primary"
                          icon={<ExportOutlined />}
                          onClick={() => {
                            setInviteModalOpen(true);
                          }}
                        >
                          Invite to Job/RFP
                        </Button>
                      ) : null
                    ) : null}

                    {/* Add this candidate to the viewer's Preferred Talent (a supplier's Consultants). */}
                    {listingIsCandidate && !isExternalUser && !isOwnProfile && canAddToPreferred(currentUser) ? (
                      <AddToPreferredButton
                        partyId={ensuredAuthor?.id?.uuid}
                        currentUser={currentUser}
                        lookUpStatus
                        size="middle"
                      />
                    ) : null}

                    {/* Add this supplier to the viewer's Preferred Suppliers, under the service it offers. */}
                    {listingIsAgency && !isExternalUser && !isOwnProfile && canAddToPreferred(currentUser, 'agency') ? (
                      <AddToPreferredButton
                        side="suppliers"
                        agencyType={listingAgencyType}
                        partyId={ensuredAuthor?.id?.uuid}
                        currentUser={currentUser}
                        lookUpStatus
                        size="middle"
                      />
                    ) : null}

                    {/* Request Access Button - Only for External Users */}
                    {isExternalUser && !isOwnProfile && (
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={handleRequestAccessClick}
                        style={{
                          backgroundColor: '#0e9941',
                          borderColor: '#0e9941',
                        }}
                      >
                        Request Full Profile Access
                      </Button>
                    )}
                  </Flex>
                </div>
              </div>

              <div className={css.avatarInfo}>
                <div className={css.avatarImg}>
                  {ensuredAuthor?.profileImage?.attributes?.variants['square-small']?.url ? (
                    <Avatar
                      size={100}
                      src={ensuredAuthor?.profileImage?.attributes?.variants['square-small']?.url}
                    />
                  ) : (
                    <Avatar size={100} src={listingIsAgency ? partnerImg1 : Profile1} />
                  )}
                </div>
                <div className={css.listNav}>
                  <div>
                    <div>
                      {listingIsCandidate ? (
                        <>
                          <h4>{currentListing?.attributes?.title}</h4>
                        </>
                      ) : listingIsAgency ? (
                        <>
                          <h4>
                            {currentListing?.attributes?.publicData?.agencyProfile?.name ||
                              currentListing?.attributes?.title}
                          </h4>
                        </>
                      ) : (
                        <>
                          <h4>
                            {currentListing?.attributes?.publicData?.customerProfile?.name ||
                              currentListing?.attributes?.title}
                          </h4>
                        </>
                      )}
                    </div>

                    {listingIsCandidate && (
                      <div className={css.candidateHeading}>
                        <span>
                          <b>
                            {
                              currentListing?.attributes?.publicData?.onBoardingInfo
                                ?.candidateHeading
                            }
                          </b>
                        </span>
                      </div>
                    )}
                    {currentListing?.attributes?.publicData?.onBoardingInfo
                      ?.linkedInCandidateLink !== null &&
                      currentListing?.attributes?.publicData?.onBoardingInfo
                        ?.linkedInCandidateLink !== undefined &&
                      currentListing?.attributes?.publicData?.onBoardingInfo
                        ?.linkedInCandidateLink !== '' && (
                        <div className={css.candidateLinkedIn}>
                          <a
                            href={
                              currentListing?.attributes?.publicData?.onBoardingInfo
                                ?.linkedInCandidateLink
                            }
                            target="_blank"
                            style={{ color: 'black', fontSize: '16px' }}
                          >
                            LinkedIn <LinkedinFilled />{' '}
                          </a>
                        </div>
                      )}
                    {currentListing?.attributes?.publicData?.customerProfile
                      ?.customerLinkedInPage !== null &&
                      currentListing?.attributes?.publicData?.customerProfile
                        ?.customerLinkedInPage !== undefined &&
                      currentListing?.attributes?.publicData?.customerProfile
                        ?.customerLinkedInPage !== '' && (
                        <div className={css.candidateLinkedIn}>
                          <a
                            href={
                              currentListing?.attributes?.publicData?.customerProfile
                                ?.customerLinkedInPage
                            }
                            target="_blank"
                            style={{ color: 'black', fontSize: '16px' }}
                          >
                            LinkedIn <LinkedinFilled />{' '}
                          </a>
                        </div>
                      )}
                    {currentListing?.attributes?.publicData?.agencyProfile?.agencyLinkedInPage !==
                      null &&
                      currentListing?.attributes?.publicData?.agencyProfile?.agencyLinkedInPage !==
                      undefined &&
                      currentListing?.attributes?.publicData?.agencyProfile?.agencyLinkedInPage !==
                      '' && (
                        <div className={css.candidateLinkedIn}>
                          <a
                            href={
                              currentListing?.attributes?.publicData?.agencyProfile
                                ?.agencyLinkedInPage
                            }
                            target="_blank"
                            style={{ color: 'black', fontSize: '16px' }}
                          >
                            LinkedIn <LinkedinFilled />{' '}
                          </a>
                        </div>
                      )}

                    {!listingIsCandidate && isBusinessIdentityVerified && (
                      <div className={css.verficationStats}>
                        <div>
                          <img src={verified} width={20} alt="Verified" title="Verified" /> Company
                          verified
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={css.listNavItems}>
                    <div>
                      <h5>{currentListing?.attributes?.publicData?.cityAddress}</h5>
                      <label>City</label>
                    </div>
                    <div>
                      <h5>{currentListing?.attributes?.publicData?.stateAddress}</h5>
                      <label>State</label>
                    </div>
                    <div>
                      <h5>{currentListing?.attributes?.publicData?.countryAddress}</h5>
                      <label>Country</label>
                    </div>
                  </div>
                </div>
              </div>

              {listingIsCandidate ? (
                <div className={css.profileBio}>
                  <strong>Profile summary: </strong>
                  {currentListing?.attributes?.publicData?.bio || currentListing?.attributes?.publicData?.onBoardingInfo?.profileSummary}
                </div>
              ) : null}

              <div className={css.profileTabsContainer}>
                {listingIsCandidate
                  ? candidateAnchor
                  : listingIsAgency
                    ? agencyAnchor
                    : customerAnchor}
                {/* <Anchor
                  direction="horizontal"
                  targetOffset={135}
                  items={[
                    {
                      key: 'part-1',
                      href: '#Tab1',
                      title: 'Preferences',
                    },
                    {
                      key: 'part-2',
                      href: '#Tab2',
                      title: 'Roles',
                    },
                    {
                      key: 'part-3',
                      href: '#Tab3',
                      title: 'Verification Summary',
                    },
                  ]}
                /> */}

                {listingIsCandidate ? (
                  <>
                    <div id="Tab1" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={preferenceNav} alt="Professional Summary" width={40} />{' '}
                          Professional Summary
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {listingIsCandidate ? (
                            <>
                              {(!isExternalUser || visibilitySettings?.['openToWork']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Open to Work</label>
                                      <h6>{listingPublicData?.onBoardingInfo?.openToWork}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['openToWork'] ? 'green' : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['openToWork']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['openToWorkDate']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Ready to join on</label>
                                      <h6>
                                        {formatDate(
                                          listingPublicData?.onBoardingInfo?.openToWorkDate
                                        )}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['openToWorkDate']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['openToWorkDate']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['workSite']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Preferred work-site</label>
                                      <h6>{listingPublicData?.onBoardingInfo?.workSite || '-'}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['workSite'] ? 'green' : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['workSite'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['preferredWorkLocation']) &&
                                listingPublicData?.onBoardingInfo?.preferredWorkLocation
                                  ?.selectedPlace?.address && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Preferred work location</label>
                                        <h6>
                                          {listingPublicData?.onBoardingInfo?.preferredWorkLocation
                                            ?.selectedPlace?.address || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['preferredWorkLocation']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['preferredWorkLocation']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['willingToRelocate']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Willing to relocate</label>
                                      <h6>
                                        {listingPublicData?.onBoardingInfo?.willingToRelocate ||
                                          'No'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['willingToRelocate']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['willingToRelocate']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['workType']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Work type</label>
                                      <h6>
                                        {Array.isArray(listingPublicData?.onBoardingInfo?.workType)
                                          ? listingPublicData?.onBoardingInfo?.workType?.join(', ')
                                          : listingPublicData?.onBoardingInfo?.workType || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['workType'] ? 'green' : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['workType'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {/* {visibilitySettings?.['authorizedToWorkInUs'] && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <label>Authorized to work in US.
                                      </label>
                                      <h6>{listingPublicData?.identityAndWorkAuthorizationDetails?.authorizedToWorkInUs}</h6>
                                    </div>
                                  </Col>
                                )} */}
                              {(!isExternalUser || visibilitySettings?.['workAuthCandidate']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Work authorization</label>
                                      <h6>
                                        {
                                          listingPublicData?.identityAndWorkAuthorizationDetails
                                            ?.workAuthCandidate
                                        }
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['workAuthCandidate']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['workAuthCandidate']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['eligibleForRolesInUs']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Eligible for roles requiring U.S citizenship.</label>
                                        <h6>
                                          {
                                            listingPublicData?.identityAndWorkAuthorizationDetails
                                              ?.eligibleForRolesInUs
                                          }
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['eligibleForRolesInUs']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['eligibleForRolesInUs']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['workAuthType']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Work Auth type</label>
                                      <h6>{listingWorkAuthType}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['workAuthType']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['workAuthType']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {showsEadCategory && (!isExternalUser || visibilitySettings?.['eadCategory']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>EAD Category</label>
                                      <h6>{listingWorkProfile.eadCategory || '-'}</h6>
                                      {eadCategoryNote && (
                                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--colorGrey500)' }}>
                                          {eadCategoryNote}
                                        </p>
                                      )}
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['eadCategory']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['eadCategory']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['billingRateTarget']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Billing rate target</label>
                                      <h6>
                                        {listingPublicData?.onBoardingInfo?.billingRateTarget ||
                                          '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['billingRateTarget']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['billingRateTarget']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['minBillingRateExpectation']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Minimum billing rate expectation</label>
                                        <h6>
                                          {listingPublicData?.onBoardingInfo
                                            ?.minBillingRateExpectation || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['minBillingRateExpectation']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['minBillingRateExpectation']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['workAuthExpiryCandidate']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Date of Work Auth Expiry</label>
                                        <h6>
                                          {formatDate(
                                            listingIsCandidate
                                              ? listingPublicData?.onBoardingInfo
                                                ?.workAuthExpiryCandidate
                                              : listingPublicData?.onBoardingInfo?.workAuthExpiry
                                          )}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['workAuthExpiryCandidate']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['workAuthExpiryCandidate']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['countryOfCitizenship']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Country of citizenship</label>
                                        <h6>
                                          {listingPublicData?.identityAndWorkAuthorizationDetails
                                            ?.countryOfCitizenship || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['countryOfCitizenship']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['countryOfCitizenship']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['countryOfResidence']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Country of residence</label>
                                      <h6>
                                        {listingPublicData?.identityAndWorkAuthorizationDetails
                                          ?.countryOfResidence || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['countryOfResidence']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['countryOfResidence']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['timeZone']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Time Zone</label>
                                      <h6>
                                        {listingPublicData?.onBoardingInfo?.timeZone?.label || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['timeZone'] ? 'green' : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['timeZone'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['remoteWorkAuthorization']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Remote work authorization</label>
                                        <h6>
                                          {listingPublicData?.onBoardingInfo
                                            ?.remoteWorkAuthorization || 'No'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['remoteWorkAuthorization']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['remoteWorkAuthorization']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['willingToBackCheck']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Willing to undergo background check</label>
                                      <h6>
                                        {listingPublicData?.onBoardingInfo?.willingToBackCheck ||
                                          'No'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['willingToBackCheck']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['willingToBackCheck']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                            </>
                          ) : (
                            <>
                              {(!isExternalUser || visibilitySettings?.['stateOfIncorp']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>
                                        State of Incorporation (Domestic Main Office) &nbsp;
                                      </label>
                                      <h6>
                                        {
                                          currentListing.attributes?.publicData?.onBoardingInfo
                                            ?.stateOfIncorp
                                        }
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['stateOfIncorp']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['stateOfIncorp']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['dateOfFormation']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>{'Date of formation'} &nbsp;</label>
                                      <h6>
                                        {formatDate(
                                          listingIsCustomer
                                            ? currentListing.attributes?.publicData?.onBoardingInfo
                                              ?.dateOfFormation
                                            : currentListing.attributes?.publicData?.onBoardingInfo
                                              ?.dateOfIncorporation
                                        )}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['dateOfFormation']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['dateOfFormation']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['employeeCount']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Employee count &nbsp;</label>
                                      <h6>
                                        {
                                          currentListing.attributes?.publicData?.onBoardingInfo
                                            ?.employeeCount
                                        }
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['employeeCount']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['employeeCount']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['typeOfBusiness']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Business type &nbsp;</label>
                                      <h6>
                                        {
                                          currentListing.attributes?.publicData?.onBoardingInfo
                                            ?.typeOfBusiness
                                        }
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['typeOfBusiness']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['typeOfBusiness']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['publicOrPrivateCompany']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Public or Private &nbsp;</label>
                                        <h6>
                                          {
                                            currentListing.attributes?.publicData?.onBoardingInfo
                                              ?.publicOrPrivateCompany
                                          }
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['publicOrPrivateCompany']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['publicOrPrivateCompany']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['annualRevenue']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Annual Revenue &nbsp;</label>
                                      <h6>
                                        {
                                          currentListing.attributes?.publicData?.onBoardingInfo
                                            ?.annualRevenue
                                        }
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['annualRevenue']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['annualRevenue']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['statesIncorpForeign']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>State incorporated as Foreign Entity? &nbsp;</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.statesIncorpForeign
                                        )
                                          ? currentListing?.attributes?.publicData?.onBoardingInfo?.statesIncorpForeign?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.statesIncorpForeign || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['statesIncorpForeign']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['statesIncorpForeign']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['geographiesServed']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Geographies Served &nbsp;</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.geographiesServed
                                        )
                                          ? currentListing?.attributes?.publicData?.onBoardingInfo?.geographiesServed?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.geographiesServed || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['geographiesServed']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['geographiesServed']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {/* {(!isExternalUser || visibilitySettings?.['identificationType']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Tax Identification Type &nbsp;

                                      </label>
                                      <h6>{currentListing.attributes?.publicData?.companyIdentification?.identificationType}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['identificationType'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['identificationType'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['identificationNumber']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Tax Identification Number &nbsp;

                                      </label>
                                      <h6>{currentListing.attributes?.publicData?.companyIdentification?.identificationNumber}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['identificationNumber'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['identificationNumber'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )} */}
                              {(!isExternalUser || visibilitySettings?.['industryFocus']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Industry focus &nbsp;</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.industryFocus
                                        )
                                          ? currentListing?.attributes?.publicData?.onBoardingInfo?.industryFocus?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.industryFocus || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['industryFocus']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['industryFocus']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['workAuthSponsorship']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Work-Auth sponsorship &nbsp;</label>
                                      <h6>
                                        {currentListing?.attributes?.publicData?.onBoardingInfo
                                          ?.workAuthSponsorship || 'No'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['workAuthSponsorship']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['workAuthSponsorship']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {/* {(!isExternalUser || visibilitySettings?.['sponsorshipPolicyVisibility']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Sponsorship visibility &nbsp;

                                      </label>
                                      <h6>{currentListing?.attributes?.publicData?.onBoardingInfo?.sponsorshipPolicyVisibility || "No"}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['sponsorshipPolicyVisibility'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['sponsorshipPolicyVisibility'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )} */}
                              {(!isExternalUser ||
                                visibilitySettings?.['procurementProcessType']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Procurement process type &nbsp;</label>
                                        <h6>
                                          {currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.procurementProcessType || 'No'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['procurementProcessType']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['procurementProcessType']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser || visibilitySettings?.['jobRfpVisibility']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Job/RFP posts visibility &nbsp;</label>
                                        <h6>
                                          {currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.jobRfpVisibility ? (
                                            <PostingVisibilityLabel
                                              value={currentListing.attributes.publicData.onBoardingInfo.jobRfpVisibility}
                                            />
                                          ) : (
                                            '-'
                                          )}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['jobRfpVisibility']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['jobRfpVisibility']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser ||
                                  visibilitySettings?.['contractModelsForStaffing']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Contract models accepted for staffing &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.customerProfile
                                              ?.contractModelsForStaffing
                                          )
                                            ? currentListing.attributes.publicData.customerProfile.contractModelsForStaffing.join(
                                              ', '
                                            )
                                            : '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['contractModelsForStaffing']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['contractModelsForStaffing']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {/* {listingIsCustomer && (!isExternalUser || visibilitySettings?.['subcontractedSubmissionsPreference']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Subcontracted submissions preference &nbsp;

                                      </label>
                                      <h6>{currentListing?.attributes?.publicData?.customerProfile?.subcontractedSubmissionsPreference || "-"}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['subcontractedSubmissionsPreference'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['subcontractedSubmissionsPreference'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )} */}
                              {listingIsCustomer &&
                                (!isExternalUser ||
                                  visibilitySettings?.['contractModelsImpProjects']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>
                                          Contract models accepted for implementaion projects &nbsp;
                                        </label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.customerProfile
                                              ?.contractModelsImpProjects
                                          )
                                            ? currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.customerProfile?.contractModelsImpProjects || '-'}
                                        </h6>
                                        {/* <h6>{currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects || "-"}</h6> */}
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['contractModelsImpProjects']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['contractModelsImpProjects']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {/* {listingIsCustomer && (!isExternalUser || visibilitySettings?.['subContractedWorkPreference']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Subcontracted work preference &nbsp;

                                      </label>
                                      <h6>{currentListing?.attributes?.publicData?.customerProfile?.subContractedWorkPreference || "-"}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['subContractedWorkPreference'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['subContractedWorkPreference'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )} */}
                              {/* {(!isExternalUser || visibilitySettings?.['paymentTerms']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Payment Terms &nbsp;

                                      </label>
                                      <h6>{currentListing?.attributes?.publicData?.customerProfile?.paymentTerms || "-"}</h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag color={visibilitySettings?.['paymentTerms'] ? 'green' : 'default'}>
                                          {visibilitySettings?.['paymentTerms'] ? 'Public' : 'Private'}
                                        </Tag>
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )} */}
                              {(!isExternalUser ||
                                visibilitySettings?.['securityAndComplianceStandards']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Security and compliance standards &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.securityAndComplianceStandards
                                          )
                                            ? currentListing?.attributes?.publicData?.securityAndComplianceStandards?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.securityAndComplianceStandards || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['securityAndComplianceStandards']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['securityAndComplianceStandards']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser ||
                                  visibilitySettings?.['procurementSystemUsed']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Procurement system used &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.customerProfile
                                              ?.procurementSystemUsed
                                          )
                                            ? currentListing?.attributes?.publicData?.customerProfile?.procurementSystemUsed?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.customerProfile?.procurementSystemUsed || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['procurementSystemUsed']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['procurementSystemUsed']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser || visibilitySettings?.['projectBudgetRange']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Project budget range &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.customerProfile
                                              ?.projectBudgetRange
                                          )
                                            ? currentListing.attributes.publicData.customerProfile.projectBudgetRange
                                              .map(formatBudgetRange)
                                              .join(', ')
                                            : currentListing?.attributes?.publicData
                                              ?.customerProfile?.projectBudgetRange
                                              ? formatBudgetRange(
                                                currentListing.attributes.publicData.customerProfile
                                                  .projectBudgetRange
                                              )
                                              : '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['projectBudgetRange']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['projectBudgetRange']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser ||
                                  visibilitySettings?.['expectedContractDuration']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Expected contract duration &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.customerProfile
                                              ?.expectedContractDuration
                                          )
                                            ? currentListing.attributes.publicData.customerProfile.expectedContractDuration
                                              .map(formatContractDuration)
                                              .join(', ')
                                            : currentListing?.attributes?.publicData
                                              ?.customerProfile?.expectedContractDuration
                                              ? formatContractDuration(
                                                currentListing.attributes.publicData.customerProfile
                                                  .expectedContractDuration
                                              )
                                              : '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['expectedContractDuration']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['expectedContractDuration']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser || visibilitySettings?.['techStaffNeeds']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Technology Staffing needs &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.techStaffNeeds
                                          )
                                            ? currentListing?.attributes?.publicData?.techStaffNeeds?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.techStaffNeeds || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['techStaffNeeds']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['techStaffNeeds']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser || visibilitySettings?.['staffRoleNeeds']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Staffing Role needs &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.staffRoleNeeds
                                          )
                                            ? currentListing?.attributes?.publicData?.staffRoleNeeds?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.staffRoleNeeds || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['staffRoleNeeds']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['staffRoleNeeds']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {listingIsCustomer &&
                                (!isExternalUser || visibilitySettings?.['techImpNeeds']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Technology implementaion needs &nbsp;</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.techImpNeeds
                                          )
                                            ? currentListing?.attributes?.publicData?.techImpNeeds?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.techImpNeeds || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['techImpNeeds']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['techImpNeeds']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                            </>
                          )}
                        </Row>
                      </div>
                    </div>

                    {!isExternalUser ? (
                      //Candidate Verification Summary
                      <div id="Tab3" className={css.profileSection}>
                        <div className={css.profileCardHeading}>
                          <h3 className={css.profileCardTitle}>
                            <img src={verificationNav} alt="iParley Credential Vault" width={40} />{' '}
                            iParley Credential Vault
                          </h3>
                        </div>
                        {showCredentialVault ? (
                          <Collapse
                            bordered={false}
                            defaultActiveKey={['integrity-checks', 'verification-summary']}
                            expandIconPosition="end"
                            style={{ background: 'transparent' }}
                            expandIcon={({ isActive }) => (
                              <DownOutlined
                                style={{
                                  transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.3s',
                                }}
                              />
                            )}
                          >
                          <Collapse.Panel
                              header={<span className={css.credentialSubHeading}>Integrity Checks</span>}
                              key="integrity-checks"
                              className={css.credentialPanel}
                            >
                              <div className={css.integrityChecksGrid}>
                                <div className={css.integrityCheckItem}>
                                  <img
                                    src={isIdentityVerified ? verified : unverified}
                                    width={20}
                                    alt={isIdentityVerified ? 'Verified' : 'Unverified'}
                                    title={isIdentityVerified ? 'Verified' : 'Unverified'}
                                  />
                                  Identity
                                </div>
                                <div className={css.integrityCheckItem}>
                                  <img
                                    src={isWorkVerified ? verified : unverified}
                                    width={20}
                                    alt={isWorkVerified ? 'Verified' : 'Unverified'}
                                    title={isWorkVerified ? 'Verified' : 'Unverified'}
                                  />
                                  Work Authorization
                                </div>
                                <div className={css.integrityCheckItem}>
                                  <img
                                    src={isExperienceVerified ? verified : unverified}
                                    width={20}
                                    alt={isExperienceVerified ? 'Verified' : 'Unverified'}
                                    title={isExperienceVerified ? 'Verified' : 'Unverified'}
                                  />
                                  Experience
                                </div>
                                <div className={css.integrityCheckItem}>
                                  <img
                                    src={isEducationVerified ? verified : unverified}
                                    width={20}
                                    alt={isEducationVerified ? 'Verified' : 'Unverified'}
                                    title={isEducationVerified ? 'Verified' : 'Unverified'}
                                  />
                                  Education
                                </div>
                              </div>

                              {/* Work auth field-level check cards */}
                              {listingIsCandidate && (
                                <div style={{ borderTop: '0.5px solid var(--borderColor)', padding: '15px' }}>
                                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--colorGrey500)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                    Work auth field checks
                                  </p>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                    {[
                                      { 
                                        label: 'Work Authorization', 
                                        value: listingPublicData?.identityAndWorkAuthorizationDetails?.workAuthCandidate || listingPublicData?.onBoardingInfo?.workAuthType || '-',
                                        verified: !!workChecks?.workAuthCandidate,
                                        documentCheck: true
                                      },
                                      { 
                                        label: 'Eligible for U.S. citizenship roles', 
                                        value: listingPublicData?.identityAndWorkAuthorizationDetails?.eligibleForRolesInUs || '-',
                                        verified: !!workChecks?.eligibleForRolesInUs,
                                        documentCheck: true
                                      },
                                      { 
                                        label: 'Work Auth Type', 
                                        value: listingWorkAuthType || '-',
                                        verified: !!workChecks?.workAuthType,
                                        documentCheck: true
                                      },
                                      ...(
                                        showsEadCategory
                                          ? [{ 
                                              label: 'EAD Category', 
                                              value: listingWorkProfile.eadCategory || '-',
                                              note: eadCategoryNote,
                                              verified: !!workChecks?.eadCategory,
                                              documentCheck: true
                                            }]
                                          : []
                                      ),
                                      { 
                                        label: 'Work Auth Expiry', 
                                        value: listingPublicData?.onBoardingInfo?.workAuthExpiryCandidate 
                                          ? formatDate(listingPublicData?.onBoardingInfo?.workAuthExpiryCandidate) 
                                          : '-',
                                        verified: !!workChecks?.workAuthExpiry,
                                        documentCheck: true
                                      },
                                      { 
                                        label: 'Country of Citizenship', 
                                        value: listingPublicData?.identityAndWorkAuthorizationDetails?.countryOfCitizenship || '-',
                                        verified: !!workChecks?.countryOfCitizenship 
                                      },
                                      { 
                                        label: 'Country of Permanent Residence', 
                                        value: listingPublicData?.identityAndWorkAuthorizationDetails?.countryOfResidence || '-',
                                        verified: !!workChecks?.countryOfPermanentResidence 
                                      },
                                    ].map(({ label, value, note, verified, documentCheck }) => (
                                      <div
                                        key={label}
                                        style={{
                                          border: '1px solid var(--borderColor)',
                                          borderRadius: '8px',
                                          padding: '12px 16px',
                                          background: '#fff',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '6px',
                                          position: 'relative',
                                        }}
                                      >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                          <span style={{ fontSize: '13px', color: 'var(--colorGrey500)', lineHeight: 1.3, flex: 1 }}>
                                            {label}
                                          </span>
                                          <div style={{ flexShrink: 0, transform: 'scale(0.85)', transformOrigin: 'top right', marginLeft: 'auto' }}>
                                            <VerificationTag isVerified={verified} {...verificationTagText({ documentCheck })} />
                                          </div>
                                        </div>
                                        <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--colorGrey800)', lineHeight: 1.4 }}>
                                          {value}
                                        </span>
                                        {note && (
                                          <span style={{ fontSize: '12px', color: 'var(--colorGrey500)', lineHeight: 1.4 }}>
                                            {note}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </Collapse.Panel>
                        <Collapse.Panel
                              header={<span className={css.credentialSubHeading}>Verification Summary</span>}
                              key="verification-summary"
                              className={css.credentialPanel}
                            >
                              <div className={css.profileCardContentFull}>
                                <div className={css.profileMediaContainer}>
                                  <div className={css.profileMedia}>
                                    <div>
                                      <img src={identityIcon} alt="identity" />
                                    </div>
                                    <div className={css.profileMediaBox}>
                                      <div className={css.profileMediaText}>
                                        <h6>
                                          {listingIsCandidate
                                            ? 'Identity'
                                            : 'Business Identity and Registry'}
                                        </h6>
                                        <Progress
                                          type="circle"
                                          percent={
                                            listingIsCandidate
                                              ? identityProgress
                                              : businessIdentityProgress
                                          }
                                          status={
                                            listingIsCandidate
                                              ? identityProgress === 100
                                                ? 'success'
                                                : identityProgress > 0
                                                  ? 'active'
                                                  : 'exception'
                                              : businessIdentityProgress === 100
                                                ? 'success'
                                                : 'exception'
                                          }
                                          size={20}
                                        />
                                      </div>
                                      <Progress
                                        percent={
                                          listingIsCandidate ? identityProgress : businessIdentityProgress
                                        }
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                  {/* A company's owners, persons and insurance carry no verification of their own. */}
                                  {listingIsCandidate && (
                                    <>
                                  <div className={css.profileMedia}>
                                    <div>
                                      <img src={workIcon} alt="Work" />
                                    </div>
                                    <div className={css.profileMediaBox}>
                                      <div className={css.profileMediaText}>
                                        <h6>
                                          Work Authorization
                                        </h6>
                                        <Progress
                                          type="circle"
                                          percent={
                                            workProgress
                                          }
                                          status={
                                            workProgress === 100
                                                ? 'success'
                                                : 'normal'
                                          }
                                          size={20}
                                        />
                                      </div>
                                      <Progress
                                        percent={
                                          workProgress
                                        }
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                  <div className={css.profileMedia}>
                                    <div>
                                      <img src={experienceIcon} alt="Experience" />
                                    </div>
                                    <div className={css.profileMediaBox}>
                                      <div className={css.profileMediaText}>
                                        <h6>
                                          Experience
                                        </h6>
                                        <Progress
                                          type="circle"
                                          percent={
                                            experienceProgress
                                          }
                                          status={
                                            experienceProgress === 100
                                                ? 'success'
                                                : 'exception'
                                          }
                                          size={20}
                                        />
                                      </div>
                                      <Progress
                                        percent={
                                          experienceProgress
                                        }
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                  <div className={css.profileMedia}>
                                    <div>
                                      <img src={educationIcon} alt="Education" />
                                    </div>
                                    <div className={css.profileMediaBox}>
                                      <div className={css.profileMediaText}>
                                        <h6>Education</h6>
                                        <Progress
                                          type="circle"
                                          percent={
                                            educationProgress
                                          }
                                          status={
                                            educationProgress === 100
                                                ? 'success'
                                                : 'exception'
                                          }
                                          size={20}
                                        />
                                      </div>
                                      <Progress
                                        percent={
                                          educationProgress
                                        }
                                        size="small"
                                      />
                                    </div>
                                  </div>
                                    </>
                                  )}
                                </div>
                                <div className={`${css.profileLists} ${css.profileListWide}`}>
                                  <>
                                    <div className={css.profileListBox}>
                                      <div className={css.profileHead}>
                                        <div>
                                          <h5>
                                            <Badge dot /> Identity
                                          </h5>
                                          <p>
                                            <small> </small>
                                          </p>
                                        </div>
                                      </div>
                                      <div className={css.profileBody}>
                                        {identityProgress === 100 || (identityTimelineItems && identityTimelineItems.length > 0) ? (
                                          <Timeline items={identityTimelineItems} />
                                        ) : (
                                          <p style={{ fontStyle: 'italic', color: '#999' }}>Yet to verify credentials</p>
                                        )}
                                        {/* <div className={css.requestButtonContainer}>
                                    <Button
                                      icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                      onClick={() => {
                                        handleRequestDocument('Identity', identityDocuments, 'identity', isOwnListing);
                                      }}
                                    >
                                      {isOwnListing ? 'Share All' : 'Request All'}
                                    </Button>
                                  </div> */}
                                      </div>
                                    </div>

                                    <div className={css.profileListBox}>
                                      <div className={css.profileHead}>
                                        <div>
                                          <h5>
                                            <Badge dot /> Work Authorization
                                          </h5>
                                          <p>
                                            <small> </small>
                                          </p>
                                        </div>
                                      </div>
                                      <div className={css.profileBody}>
                                        {workTimelineItems.length > 0 ? (
                                          <Timeline items={workTimelineItems} />
                                        ) : (
                                          <p style={{ fontStyle: 'italic', color: '#999' }}>Yet to verify credentials</p>
                                        )}
                                        {/* <div className={css.requestButtonContainer}>
                                    <Button
                                      icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                      onClick={() => handleRequestDocument('Work Authorization', workDocuments, 'work-authorization', isOwnListing)}
                                    >
                                      {isOwnListing ? 'Share All' : 'Request All'}
                                    </Button>
                                  </div> */}
                                  </div>
                                </div>

                                    <div className={css.profileListBox}>
                                      <div className={css.profileHead}>
                                        <h5>
                                          <Badge dot /> Experience{' '}
                                        </h5>
                                      </div>
                                      <div className={css.profileBody}>
                                        {(!currentListing?.attributes?.publicData?.currentEmployer?.company?.label &&
                                          (!Array.isArray(currentListing?.attributes?.publicData?.pastExperience) ||
                                            currentListing?.attributes?.publicData?.pastExperience.length === 0)) ? (
                                          <p style={{ fontStyle: 'italic', color: '#999' }}>Yet to verify credentials</p>
                                        ) : (
                                          <Timeline
                                            items={[
                                              // Current Employer (if exists)
                                              currentListing?.attributes?.publicData?.currentEmployer
                                                ?.company?.label && {
                                                color: '#1da0f2',
                                                dot: (
                                                  <img
                                                    src={
                                                      isCurrentEmployerConfirmed
                                                        ? verified
                                                        : unverified
                                                    }
                                                    alt={
                                                      isCurrentEmployerConfirmed
                                                        ? 'Verified'
                                                        : 'Not Verified'
                                                    }
                                                    width="22"
                                                    title={
                                                      isCurrentEmployerConfirmed
                                                        ? 'Verified'
                                                        : 'Not Verified'
                                                    }
                                                  />
                                                ),
                                                children: (
                                                  <div
                                                    style={{ position: 'relative' }}
                                                    onMouseEnter={() => setHoveredExperience('current')}
                                                    onMouseLeave={() => setHoveredExperience(null)}
                                                  >
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                      }}
                                                    >
                                                      <div style={{ flex: 1 }}>
                                                        <h6>
                                                          {
                                                            currentListing?.attributes?.publicData
                                                              ?.currentEmployer?.company?.label
                                                          }
                                                        </h6>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                          <FormattedMessage id={currentEmployerConfirmationMessageId} />
                                                        </div>
                                                        {!expandedExperiences['current'] &&
                                                          currentListing?.attributes?.publicData
                                                            ?.currentEmployer?.title && (
                                                            <div
                                                              style={{ fontSize: '13px', marginTop: '2px' }}
                                                            >
                                                              {
                                                                currentListing?.attributes?.publicData
                                                                  ?.currentEmployer?.title
                                                              }
                                                            </div>
                                                          )}
                                                      </div>
                                                      {(hoveredExperience === 'current' ||
                                                        expandedExperiences['current']) && (
                                                          <Button
                                                            type="text"
                                                            size="small"
                                                            icon={
                                                              expandedExperiences['current'] ? (
                                                                <UpOutlined />
                                                              ) : (
                                                                <DownOutlined />
                                                              )
                                                            }
                                                            onClick={() =>
                                                              setExpandedExperiences(prev => ({
                                                                ...prev,
                                                                current: !prev['current'],
                                                              }))
                                                            }
                                                            style={{ marginLeft: '8px' }}
                                                          />
                                                        )}
                                                    </div>
                                                    {expandedExperiences['current'] && (
                                                      <>
                                                        {currentListing?.attributes?.publicData
                                                          ?.currentEmployer?.title && (
                                                            <div
                                                              style={{ fontSize: '13px', marginTop: '2px' }}
                                                            >
                                                              {
                                                                currentListing?.attributes?.publicData
                                                                  ?.currentEmployer?.title
                                                              }
                                                            </div>
                                                          )}
                                                        <div className={css.verificationCurrentEmployer}>
                                                          Current employer
                                                        </div>
                                                        <div className={css.verificationDateRange}>
                                                          {formatMonthYear(
                                                            currentListing?.attributes?.publicData
                                                              ?.currentEmployer?.startDate?.date
                                                          )}{' '}
                                                          - Present
                                                        </div>
                                                        {currentListing?.attributes?.publicData
                                                          ?.currentEmployer?.description && (
                                                            <div
                                                              style={{
                                                                fontSize: '12px',
                                                                marginTop: '4px',
                                                                color: '#666',
                                                              }}
                                                            >
                                                              {
                                                                currentListing?.attributes?.publicData
                                                                  ?.currentEmployer?.description
                                                              }
                                                            </div>
                                                          )}
                                                        {currentListing?.attributes?.publicData
                                                          ?.currentEmployer?.skillsUsed && (
                                                            <div style={{ marginTop: '6px' }}>
                                                              {currentListing?.attributes?.publicData?.currentEmployer?.skillsUsed?.map(
                                                                (skill, index, arr) => (
                                                                  <React.Fragment key={index}>
                                                                    <Tag
                                                                      style={{
                                                                        fontSize: '11px',
                                                                        marginBottom: '4px',
                                                                      }}
                                                                    >
                                                                      {skill.trim()}
                                                                    </Tag>
                                                                  </React.Fragment>
                                                                )
                                                              )}
                                                            </div>
                                                          )}
                                                      </>
                                                    )}
                                                  </div>
                                                ),
                                              },
                                              // Past Experiences
                                              ...((Array.isArray(
                                                currentListing?.attributes?.publicData?.pastExperience
                                              ) &&
                                                currentListing?.attributes?.publicData?.pastExperience?.map(
                                                  (item, index) => ({
                                                    color: '#1da0f2',
                                                    dot: (
                                                      <img
                                                        src={item?.isVerified ? verified : unverified}
                                                        alt={item?.isVerified ? 'Verified' : 'Not Verified'}
                                                        width="22"
                                                        title={
                                                          item?.isVerified ? 'Verified' : 'Not Verified'
                                                        }
                                                      />
                                                    ),
                                                    children: (
                                                      <div
                                                        style={{ position: 'relative' }}
                                                        onMouseEnter={() =>
                                                          setHoveredExperience(`past-${index}`)
                                                        }
                                                        onMouseLeave={() => setHoveredExperience(null)}
                                                      >
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                          }}
                                                        >
                                                          <div style={{ flex: 1 }}>
                                                            <h6>{item?.company?.label}</h6>
                                                            {!expandedExperiences[`past-${index}`] &&
                                                              item?.title && (
                                                                <div className={css.verificationJobTitle}>
                                                                  {item?.title}
                                                                </div>
                                                              )}
                                                          </div>
                                                          {(hoveredExperience === `past-${index}` ||
                                                            expandedExperiences[`past-${index}`]) && (
                                                              <Button
                                                                type="text"
                                                                size="small"
                                                                icon={
                                                                  expandedExperiences[`past-${index}`] ? (
                                                                    <UpOutlined />
                                                                  ) : (
                                                                    <DownOutlined />
                                                                  )
                                                                }
                                                                onClick={() =>
                                                                  setExpandedExperiences(prev => ({
                                                                    ...prev,
                                                                    [`past-${index}`]: !prev[`past-${index}`],
                                                                  }))
                                                                }
                                                                style={{ marginLeft: '8px' }}
                                                              />
                                                            )}
                                                        </div>
                                                        {expandedExperiences[`past-${index}`] && (
                                                          <>
                                                            {item?.title && (
                                                              <div className={css.verificationJobTitle}>
                                                                {item?.title}
                                                              </div>
                                                            )}
                                                            <div
                                                              className={
                                                                css.verificationDateRangeWithMargin
                                                              }
                                                            >
                                                              {item?.startDate?.date && formatMonthYear(item.startDate.date)}
                                                              {item?.startDate?.date && ' - '}
                                                              {item?.endDate?.date
                                                                ? formatMonthYear(item.endDate.date)
                                                                : item?.startDate?.date && 'Present'}
                                                            </div>
                                                            {item?.description && (
                                                              <div
                                                                style={{
                                                                  fontSize: '12px',
                                                                  marginTop: '4px',
                                                                  color: '#666',
                                                                }}
                                                              >
                                                                {item?.description}
                                                              </div>
                                                            )}
                                                            {item?.roleSkillsTags &&
                                                              Array.isArray(item?.roleSkillsTags) &&
                                                              item?.roleSkillsTags.length > 0 && (
                                                                <div style={{ marginTop: '6px' }}>
                                                                  {item?.roleSkillsTags?.map(
                                                                    (skill, skillIndex) => (
                                                                      <Tag
                                                                        key={skillIndex}
                                                                        style={{
                                                                          fontSize: '11px',
                                                                          marginBottom: '4px',
                                                                        }}
                                                                      >
                                                                        {skill}
                                                                      </Tag>
                                                                    )
                                                                  )}
                                                                </div>
                                                              )}
                                                          </>
                                                        )}
                                                      </div>
                                                    ),
                                                  })
                                                )) ||
                                                []),
                                            ].filter(Boolean)}
                                          />
                                        )}
                                      </div>
                                    </div>

                                    <div className={css.profileListBox}>
                                      <div className={css.profileHead}>
                                        <h5>
                                          <Badge dot /> Education{' '}
                                        </h5>
                                      </div>
                                      <div className={css.profileBody}>
                                        {(!Array.isArray(currentListing?.attributes?.publicData?.education) ||
                                          currentListing?.attributes?.publicData?.education.length === 0) ? (
                                          <p style={{ fontStyle: 'italic', color: '#999' }}>Yet to verify credentials</p>
                                        ) : (
                                          <Timeline
                                            items={(
                                              currentListing?.attributes?.publicData?.education || []
                                            ).map((item, index) => ({
                                              color: '#1da0f2',
                                              dot: (
                                                <img
                                                  src={item?.isVerified ? verified : unverified}
                                                  alt={item?.isVerified ? 'Verified' : 'Not Verified'}
                                                  width="22"
                                                  title={item?.isVerified ? 'Verified' : 'Not Verified'}
                                                />
                                              ),
                                              children: (
                                                <div
                                                  style={{ position: 'relative' }}
                                                  onMouseEnter={() => setHoveredEducation(`edu-${index}`)}
                                                  onMouseLeave={() => setHoveredEducation(null)}
                                                >
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between',
                                                    }}
                                                  >
                                                    <div style={{ flex: 1 }}>
                                                      <h6>{item?.institution}</h6>
                                                      {!expandedEducations[`edu-${index}`] &&
                                                        item?.degree && (
                                                          <div className={css.verificationJobTitle}>
                                                            {item?.degree}
                                                          </div>
                                                        )}
                                                    </div>
                                                    {(hoveredEducation === `edu-${index}` ||
                                                      expandedEducations[`edu-${index}`]) && (
                                                        <Button
                                                          type="text"
                                                          size="small"
                                                          icon={
                                                            expandedEducations[`edu-${index}`] ? (
                                                              <UpOutlined />
                                                            ) : (
                                                              <DownOutlined />
                                                            )
                                                          }
                                                          onClick={() =>
                                                            setExpandedEducations(prev => ({
                                                              ...prev,
                                                              [`edu-${index}`]: !prev[`edu-${index}`],
                                                            }))
                                                          }
                                                          style={{ marginLeft: '8px' }}
                                                        />
                                                      )}
                                                  </div>
                                                  {expandedEducations[`edu-${index}`] && (
                                                    <>
                                                      {item?.degree && (
                                                        <div className={css.verificationJobTitle}>
                                                          {item?.degree}
                                                        </div>
                                                      )}
                                                      {item?.major && (
                                                        <div
                                                          style={{
                                                            fontSize: '12px',
                                                            marginTop: '2px',
                                                            color: '#666',
                                                          }}
                                                        >
                                                          Major: {item?.major}
                                                        </div>
                                                      )}
                                                      {(item?.startDate?.date || item?.endDate?.date) && (
                                                        <div
                                                          className={css.verificationDateRangeWithMargin}
                                                        >
                                                          {item?.startDate?.date &&
                                                            formatMonthYear(item?.startDate?.date)}
                                                          {item?.startDate?.date &&
                                                            item?.endDate?.date &&
                                                            ' - '}
                                                          {item?.endDate?.date &&
                                                            formatMonthYear(item?.endDate?.date)}
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              ),
                                            }))}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </>
                                </div>
                              </div>
                            </Collapse.Panel>
                          </Collapse>
                        ) : (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px 20px',
                          color: '#999',
                          background: '#fafafa',
                          borderRadius: '8px',
                          marginTop: '12px',
                        }}>
                          <LockOutlined style={{ fontSize: '32px', marginBottom: '12px', color: '#bbb' }} />
                          <p style={{ margin: 0, fontSize: '14px', marginBottom: '16px' }}>
                            Run an integrity check or request access to unlock credential details
                          </p>
                              {currentUserIsCustomer && !isOwnProfile && (
                                <Button
                                  type="primary"
                                  icon={<UnlockOutlined />}
                                  onClick={() => handleRequestAccessClick('vault')}
                                  style={{ marginTop: '8px', backgroundColor: '#0e9941', borderColor: '#0e9941' }}
                                >
                                  Request Access
                                </Button>
                              )}
                        </div>
                      )}
                      </div>
                    ) : null}

                    {!isExternalUser && listingIsCandidate ? (
                      //Candidate Experience Section
                      <div id="Tab4" className={css.profileSection}>
                        <div className={css.profileCardHeading}>
                          <h3 className={css.profileCardTitle}>
                            <img src={experienceIcon} alt="Experience" width={40} />
                            Experience
                          </h3>
                        </div>
                        <div className={css.profileCardContent}>
                          {(currentListing?.attributes?.publicData?.currentEmployer ||
                            currentListing?.attributes?.publicData?.pastExperience) && (
                              <Timeline
                                items={[
                                  // Current Employer (if exists)
                                  currentListing?.attributes?.publicData?.currentEmployer
                                    ?.company?.label && {
                                    children: (
                                      <div className="formRow">
                                        {currentListing?.attributes?.publicData?.currentEmployer
                                          ?.company?.id ? (
                                          <h4>
                                            <a
                                              href={`/l/${currentListing?.attributes?.publicData?.currentEmployer?.company?.listingName}/${currentListing?.attributes?.publicData?.currentEmployer?.company?.listingId}`}
                                              style={{ color: 'black' }}
                                            >
                                              {
                                                currentListing?.attributes?.publicData
                                                  ?.currentEmployer?.company?.label
                                              }
                                            </a>
                                          </h4>
                                        ) : (
                                          <h4>
                                            {
                                              currentListing?.attributes?.publicData?.currentEmployer
                                                ?.company?.label
                                            }
                                          </h4>
                                        )}
                                        {currentListing?.attributes?.publicData?.currentEmployer
                                          ?.title && (
                                            <h5>
                                              {
                                                currentListing?.attributes?.publicData?.currentEmployer
                                                  ?.title
                                              }
                                            </h5>
                                          )}
                                        <span>Current employer</span>
                                        <br />
                                        <span>
                                          <FormattedMessage id={currentEmployerConfirmationMessageId} />
                                        </span>
                                        <br />
                                        <span>
                                          From:{' '}
                                          {formatMonthYear(
                                            currentListing?.attributes?.publicData?.currentEmployer
                                              ?.startDate?.date
                                          )}{' '}
                                          - Present
                                        </span>
                                        {currentListing?.attributes?.publicData?.currentEmployer
                                          ?.description && (
                                            <>
                                              <br />
                                              <p style={{ marginTop: '8px', fontSize: '14px' }}>
                                                {
                                                  currentListing?.attributes?.publicData
                                                    ?.currentEmployer?.description
                                                }
                                              </p>
                                            </>
                                          )}
                                        {currentListing?.attributes?.publicData?.currentEmployer
                                          ?.skillsUsed && (
                                            <div style={{ marginTop: '8px' }}>
                                              {currentListing?.attributes?.publicData?.currentEmployer?.skillsUsed?.map(
                                                (skill, index, arr) => (
                                                  <React.Fragment key={index}>
                                                    <Tag style={{ marginBottom: '4px' }}>
                                                      {skill.trim()}
                                                    </Tag>
                                                  </React.Fragment>
                                                )
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    ),
                                  },

                                  // Past Experiences
                                  ...((Array.isArray(
                                    currentListing?.attributes?.publicData?.pastExperience
                                  ) &&
                                    currentListing?.attributes?.publicData?.pastExperience?.map(
                                      item => ({
                                        children: (
                                          <>
                                            <h4>
                                              {item?.company?.id ? (
                                                <a
                                                  href={`/l/${item?.company?.listingId}`}
                                                  style={{ color: 'black' }}
                                                >
                                                  {item?.company?.label}
                                                </a>
                                              ) : (
                                                <span>{item?.company?.label}</span>
                                              )}
                                            </h4>
                                            <h5>{item?.title}</h5>

                                            {/* Dates */}
                                            <div>
                                              {item?.startDate?.date && formatMonthYear(item.startDate.date)}
                                              {item?.startDate?.date && ' - '}
                                              {item?.endDate?.date
                                                ? formatMonthYear(item.endDate.date)
                                                : item?.startDate?.date && 'Present'}
                                            </div>

                                            {/* Description */}
                                            <div>{item?.description}</div>

                                            {/* Tags */}
                                            {Array.isArray(item?.roleSkillsTags) &&
                                              item.roleSkillsTags.length > 0 && (
                                                <div style={{ marginTop: '8px' }}>
                                                  {item.roleSkillsTags.map((tag, index) => (
                                                    <React.Fragment key={index}>
                                                      <Tag style={{ marginBottom: '4px' }}>
                                                        {tag || '-'}
                                                      </Tag>
                                                    </React.Fragment>
                                                  ))}
                                                </div>
                                              )}
                                          </>
                                        ),
                                      })
                                    )) ||
                                    []),
                                ]}
                              />
                            )}
                        </div>
                      </div>
                    ) : null}

                    {!isExternalUser && listingIsCandidate ? (
                      //Candidate Education Section
                      <div id="Tab5" className={css.profileSection}>
                        <div className={css.profileCardHeading}>
                          <h3 className={css.profileCardTitle}>
                            <img src={educationIcon} alt="Education" width={40} /> Education{' '}
                          </h3>
                        </div>
                        <div className={css.profileCardContent}>
                          <List
                            itemLayout="horizontal"
                            dataSource={currentListing?.attributes?.publicData?.education || []}
                            renderItem={item => (
                              <List.Item>
                                <List.Item.Meta
                                  avatar={<img src={educationLogo} width={40} />}
                                  title={
                                    item?.websiteLink ? (
                                      <a href={item?.websiteLink} target="_blank">
                                        {item.institution}
                                      </a>
                                    ) : (
                                      <span>{item.institution}</span>
                                    )
                                  }
                                  description={
                                    <>
                                      <div>Degree: {item?.degree}</div>
                                      <div>Major field of study: {item?.major}</div>
                                      {item?.minor && <div>Minor field of study: {item.minor}</div>}
                                      {item?.grade && <div>Grade: {item.grade}</div>}
                                      <div>
                                        {item?.startDate?.date && formatMonthYear(item.startDate.date)}
                                        {item?.startDate?.date && item?.endDate?.date && ' - '}
                                        {item?.endDate?.date && formatMonthYear(item.endDate.date)}
                                      </div>
                                      <div className="item.description">{item?.description}</div>
                                    </>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : null}

                {listingIsAgency ? (
                  <>
                    <div id="TabBusinessReg" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={registrationNav} alt="Business Registration" width={40} />{' '}
                          Business Registration
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['agencyProfileName']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Supplier Name</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile?.name ||
                                      '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyProfileName']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyProfileName']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'agencyProfileName',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyAlternateName']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>DBA/Alternate Name</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyAlternateName || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyAlternateName']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyAlternateName']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'agencyAlternateName',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['countryOfFormation']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Country of Formation</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.formationDetails
                                      ?.countryOfFormation || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['countryOfFormation']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['countryOfFormation']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'countryOfFormation',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['stateOfIncorp']) &&
                            currentListing?.attributes?.publicData?.formationDetails
                              ?.countryOfFormation === 'United States' && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>State of Incorporation (Domestic Main Office)</label>
                                    <h6>
                                      {currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.stateOfIncorp || '-'}
                                    </h6>
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['stateOfIncorp']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['stateOfIncorp']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag
                                        isVerified={getFieldVerificationStatus(
                                          effectivePersonaChecks,
                                          'stateOfIncorp',
                                          personaRecords
                                        )}
                                      />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                          {(!isExternalUser || visibilitySettings?.['statesIncorpForeign']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>States incorporated as Foreign Entity</label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.statesIncorpForeign
                                    )
                                      ? currentListing?.attributes?.publicData?.onBoardingInfo?.statesIncorpForeign?.join(
                                        ', '
                                      )
                                      : currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.statesIncorpForeign || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['statesIncorpForeign']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['statesIncorpForeign']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'statesIncorpForeign',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyAddress']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Supplier Address</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile?.address
                                      ?.selectedPlace?.address || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyAddress'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyAddress'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'agencyAddress',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['dateOfFormation']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Date of Formation</label>
                                  <h6>
                                    {formatDate(
                                      currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.dateOfFormation
                                    ) || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['dateOfFormation']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['dateOfFormation']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'dateOfFormation',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['typeOfBusiness']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Business type</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.typeOfBusiness || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['typeOfBusiness'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['typeOfBusiness']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'typeOfBusiness',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['publicOrPrivateCompany']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Public or Private</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.publicOrPrivateCompany || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['publicOrPrivateCompany']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['publicOrPrivateCompany']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['identificationType']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Tax Identification Type</label>
                                  <h6>{currentListing?.attributes?.publicData?.companyIdentification?.identificationType || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['identificationType'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['identificationType'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {companyCredentialsLoaded && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Tax Identification Number</label>
                                  <h6>{companyCredentials.companyIdentification?.identificationNumber || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color="default">Private</Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['taxClassification']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Federal Tax Classification</label>
                                  <h6>{currentListing?.attributes?.publicData?.companyIdentification?.taxClassification || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['taxClassification'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['taxClassification'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {(!isExternalUser ||
                            visibilitySettings?.['securityAndComplianceStandards']) && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>Security and compliance standards</label>
                                    <h6>
                                      {Array.isArray(
                                        currentListing?.attributes?.publicData
                                          ?.securityAndComplianceStandards
                                      )
                                        ? currentListing?.attributes?.publicData?.securityAndComplianceStandards?.join(
                                          ', '
                                        )
                                        : currentListing?.attributes?.publicData
                                          ?.securityAndComplianceStandards || '-'}
                                    </h6>
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['securityAndComplianceStandards']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['securityAndComplianceStandards']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag selfDeclared={true} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                        </Row>
                      </div>
                    </div>

                    <div id="TabBusinessInfo" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={infoNav} alt="Business Information" width={40} /> Business
                          Information
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['agencyDescription']) && (
                            <Col xs={24}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Supplier Description</label>
                                  <h6 style={{ textAlign: 'left' }}>
                                    {currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyDescription || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyDescription']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyDescription']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyWebsite']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Website</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyWebsite || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyWebsite'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyWebsite'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'agencyWebsite',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyLinkedInPage']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>LinkedIn Page</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyLinkedInPage || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyLinkedInPage']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyLinkedInPage']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['employeeCount']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Employee count</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.employeeCount || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['employeeCount'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['employeeCount'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'employeeCount',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['annualRevenue']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Annual Revenue</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.annualRevenue || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['annualRevenue'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['annualRevenue'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'annualRevenue',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyMail']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Contact Email Address</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyMail || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyMail'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyMail'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'agencyMail',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['agencyType']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Supplier Type</label>
                                  <h6>
                                    {getAgencyTypeDisplayLabel(currentListing?.attributes?.publicData?.agencyProfile
                                      ?.agencyType) || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['agencyType'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['agencyType'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['subcontractingPolicy']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Subcontracting Policy</label>
                                  <h6>{currentListing?.attributes?.publicData?.subcontractingPolicy || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['subcontractingPolicy'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['subcontractingPolicy'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                        </Row>
                      </div>
                    </div>

                    <div id="TabInsurance" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        {/* <h3 className={css.profileCardTitle}><img src={insuranceNav} alt="Insurance and Ownership details" width={40} /> Insurance and Ownership details</h3> */}
                        <h3 className={css.profileCardTitle}>
                          <img
                            src={insuranceNav}
                            alt="Insurance and Ownership details"
                            width={40}
                          />{' '}
                          Ownership details
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {/* {(!isExternalUser || visibilitySettings?.['insuranceDetailsVisibility']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Insurance Details Visibility</label>
                                  <h6>{currentListing?.attributes?.publicData?.insuranceDetailsVisibility || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['insuranceDetailsVisibility'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['insuranceDetailsVisibility'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {canSeeCompanyDocuments({ credentialsLoaded: companyCredentialsLoaded, shareOtpVerified }) && listingIsAgency && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>W9 Form</label>
                                  <CompanyDocumentFile
                                    listingId={currentListing?.id?.uuid}
                                    document="w9"
                                    shareVisit={companyCredentialsLoaded ? null : shareVisit}
                                    hasFile={companyCredentialsLoaded ? !!companyCredentials.companyIdentification?.w9FormUrl : undefined}
                                  />
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color="default">Private</Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'w9FormUrl', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {canSeeCompanyDocuments({ credentialsLoaded: companyCredentialsLoaded, shareOtpVerified }) && listingIsAgency && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Insurance Details Certificate</label>
                                  <CompanyDocumentFile
                                    listingId={currentListing?.id?.uuid}
                                    document="insurance"
                                    shareVisit={companyCredentialsLoaded ? null : shareVisit}
                                    hasFile={companyCredentialsLoaded ? !!companyCredentials.insuranceCertificateUrl : undefined}
                                  />
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color="default">Private</Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'insuranceCertificateUrl', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                        </Row>
                        <div className={css.accordianDiv}>
                          <Collapse accordion expandIconPosition="left">
                            {(!isExternalUser || visibilitySettings?.['insuranceDetails']) && listingIsAgency && (
                              <Panel header="Insurance Details" key="4">
                                <div className={css.profileLists}>
                                  {Array.isArray(currentListing.attributes?.publicData?.insuranceDetails) && currentListing.attributes?.publicData?.insuranceDetails
                                    ?.filter(item => item?.coverageAmounts && typeof item.coverageAmounts === 'object' && Object.keys(item.coverageAmounts).length > 0)
                                    ?.map((item, index) => (
                                      <div key={item.id || index} className={css.profileListBox}>
                                        <SafetyOutlined style={{ fontSize: 18, marginRight: 5, color: "#35ba9b" }} /> <b>{item?.category || '-'} </b>
                                        <ul className={css.coverageList}>
                                          {Object.entries(item.coverageAmounts).map(([coverageType, amount], i) => (
                                            <li key={i}>
                                              <strong>{coverageType || '-'}:</strong> $ {amount || 0}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ))}
                                </div>
                              </Panel>
                            )}
                            {companyCredentialsLoaded && (
                              <Panel header="Beneficial Owners" key="1">
                                <div className={css.profileLists}>
                                  {Array.isArray(companyCredentials.beneficialOwners) &&
                                    companyCredentials.beneficialOwners.map(
                                      (item, index) => {
                                        if (item?.ownerType === 'individual') {
                                          return (
                                            <div key={index} className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />
                                                  {item?.firstName || '-'} {item?.middleName || ''}{' '}
                                                  {item?.lastName || ''}
                                                </List.Item>
                                                <List.Item>
                                                  <PercentageOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  {item?.ownershipPercentage + '%' || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <EnvironmentOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#35ba9b',
                                                    }}
                                                  />
                                                  {item?.addressDetail?.selectedPlace?.address || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PhoneOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d56fac',
                                                    }}
                                                  />
                                                  {item?.workLandlineNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }

                                        if (item?.ownerType === 'company') {
                                          return (
                                            <div key={index} className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <BankOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />
                                                  {item?.companyName || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <NumberOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  Reg. No: {item?.registrationNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PercentageOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  {item?.ownershipPercentage + '%' || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <EnvironmentOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#35ba9b',
                                                    }}
                                                  />
                                                  {item?.addressDetail?.selectedPlace?.address || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PhoneOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d56fac',
                                                    }}
                                                  />
                                                  {item?.workLandlineNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }

                                        // fallback if ownerType is missing or unknown
                                        return null;
                                      }
                                    )}
                                </div>
                              </Panel>
                            )}
                            {(!isExternalUser ||
                              visibilitySettings?.['keyManagementPersonnel']) && (
                                <Panel header="Key Management Personnel" key="2">
                                  <div className={css.profileLists}>
                                    {Array.isArray(
                                      currentListing.attributes?.publicData?.keyManagementPersonnel
                                    ) &&
                                      currentListing.attributes?.publicData?.keyManagementPersonnel?.map(
                                        (item, index) => {
                                          return (
                                            <div className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />{' '}
                                                  {item?.firstName && item?.lastName
                                                    ? `${item.firstName} ${item.lastName}`
                                                    : item?.name || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <ContactsOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />{' '}
                                                  {item?.role || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />{' '}
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />{' '}
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>
                                </Panel>
                              )}
                            {(!isExternalUser ||
                              visibilitySettings?.['keyProcurementContacts']) && (
                                <Panel header="Key Procurement Contacts" key="3">
                                  <div className={css.profileLists}>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.keyProcurementContacts
                                    ) &&
                                      currentListing?.attributes?.publicData?.keyProcurementContacts?.map(
                                        (item, index) => {
                                          return (
                                            <div className={css.profileListBox} key={index}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />{' '}
                                                  {item?.name || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <ContactsOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />{' '}
                                                  {item?.title || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />{' '}
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />{' '}
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>
                                </Panel>
                              )}
                            {(!isExternalUser || visibilitySettings?.['authorizedSignatories']) && (
                              <Panel header="Authorized Signatories" key="4">
                                <div className={css.profileLists}>
                                  {Array.isArray(
                                    currentListing.attributes?.publicData?.authorizedSignatories
                                  ) &&
                                    currentListing.attributes?.publicData?.authorizedSignatories?.map(
                                      (item, index) => {
                                        return (
                                          <div className={css.profileListBox}>
                                            <List size="small" bordered={false}>
                                              <List.Item>
                                                <UserOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#d94452',
                                                  }}
                                                />{' '}
                                                {item?.firstName && item?.lastName
                                                  ? `${item.firstName} ${item.lastName}`
                                                  : item?.name || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <ContactsOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#f5b945',
                                                  }}
                                                />{' '}
                                                {item?.role || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <MailOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#5e9cea',
                                                  }}
                                                />{' '}
                                                {item?.email || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <MobileOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#9579da',
                                                  }}
                                                />{' '}
                                                {item?.phoneNumber || '-'}
                                              </List.Item>
                                            </List>
                                          </div>
                                        );
                                      }
                                    )}
                                </div>
                              </Panel>
                            )}
                          </Collapse>
                        </div>
                      </div>
                    </div>

                    {!isExternalUser ? (
                      // Agency Verification Summary
                      <div id="Tab3" className={css.profileSection}>
                        <div className={css.profileCardHeading}>
                          <h3 className={css.profileCardTitle}>
                            <img src={verificationNav} alt="Verification Summary" width={40} />{' '}
                            Verification Summary
                          </h3>
                        </div>
                        <div className={css.profileCardContentFull}>
                          <div className={css.profileMediaContainer}>
                            <div className={css.profileMedia}>
                              <div>
                                <img src={identityIcon} alt="identity" />
                              </div>
                              <div className={css.profileMediaBox}>
                                <div className={css.profileMediaText}>
                                  <h6>Business Identity and Registry</h6>
                                  <Progress
                                    type="circle"
                                    percent={businessIdentityProgress}
                                    status={
                                      businessIdentityProgress === 100 ? 'success' : 'exception'
                                    }
                                    size={20}
                                  />
                                </div>
                                <Progress percent={businessIdentityProgress} size="small" />
                              </div>
                            </div>
                          </div>
                          <div className={`${css.profileLists} ${css.profileListWide}`}>
                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot /> Business Identity and Registry
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBBusinessDetails({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Business Identity and Registry', personaChecks['Business Registry Verification (KYB)'], 'business-identity', isOwnListing, personaChecks['Business Registry Verification (KYB)'])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot />
                                    Beneficial Owner Identity
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBBeneficialOwners({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Beneficial Owner Identity', personaChecks['Business Registry Verification (KYB)']?.beneficialOwners || [], 'beneficial-owner', isOwnListing, personaChecks['Business Registry Verification (KYB)']?.beneficialOwners || [])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot />
                                    Authorized Person Identity
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBAuthorizedPersons({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Authorized Person Identity', personaChecks['Business Registry Verification (KYB)']?.authorizedPersons || [], 'authorized-person', isOwnListing, personaChecks['Business Registry Verification (KYB)']?.authorizedPersons || [])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot /> Insurance and Reports
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBInsuranceAndReports({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {currentListing?.attributes?.publicData?.agencyProfile?.agencyType?.includes(
                      'Staffing'
                    ) && (
                        <div id="TabStaff" className={css.profileSection}>
                          <div className={css.profileCardHeading}>
                            <h3 className={css.profileCardTitle}>
                              <img src={staffingNav} alt="Staffing Summary" width={40} /> Staffing
                              Summary
                            </h3>
                          </div>
                          <div className={css.profileCardContent}>
                            <Row gutter={[16, 16]}>
                              {(!isExternalUser ||
                                visibilitySettings?.['staffingServicesOffered']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Staffing Services Offered</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.staffingServicesOffered
                                          )
                                            ? currentListing?.attributes?.publicData?.staffingServicesOffered?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.staffingServicesOffered || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['staffingServicesOffered']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['staffingServicesOffered']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['staffingIndustryFocus']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Staffing Industry Focus</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData
                                            ?.staffingIndustryFocus
                                        )
                                          ? currentListing?.attributes?.publicData?.staffingIndustryFocus?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData
                                            ?.staffingIndustryFocus || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['staffingIndustryFocus']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['staffingIndustryFocus']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                        <VerificationTag selfDeclared={true} />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['staffingServiceDomains']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Staffing Service Domains</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.staffingServiceDomains
                                          )
                                            ? currentListing?.attributes?.publicData?.staffingServiceDomains?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.staffingServiceDomains || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['staffingServiceDomains']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['staffingServiceDomains']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['staffingGeographiesServed']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Staffing Geographies Served</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.staffingGeographiesServed
                                          )
                                            ? currentListing?.attributes?.publicData?.staffingGeographiesServed?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.staffingGeographiesServed || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['staffingGeographiesServed']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['staffingGeographiesServed']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['staffingTechFocSpec']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Staffing Technology focus/Specializations</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.staffingTechFocSpec
                                        )
                                          ? currentListing?.attributes?.publicData?.staffingTechFocSpec?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData
                                            ?.staffingTechFocSpec || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['staffingTechFocSpec']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['staffingTechFocSpec']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                        <VerificationTag selfDeclared={true} />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser || visibilitySettings?.['clientRefVerified']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Client references verified?</label>
                                      <h6>
                                        {currentListing?.attributes?.publicData?.onBoardingInfo
                                          ?.clientRefVerified || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['clientRefVerified']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['clientRefVerified']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                        <VerificationTag selfDeclared={true} />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['contractTypesSupported']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Contract types supported</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.contractTypesSupported
                                          )
                                            ? currentListing?.attributes?.publicData?.onBoardingInfo?.contractTypesSupported?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.contractTypesSupported || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['contractTypesSupported']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['contractTypesSupported']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['certificationsOrDesignations']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Staffing Certifications or Designations</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.certificationsOrDesignations
                                          )
                                            ? currentListing?.attributes?.publicData?.onBoardingInfo?.certificationsOrDesignations?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.certificationsOrDesignations || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['certificationsOrDesignations']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['certificationsOrDesignations']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                            </Row>

                            <div className={css.accordianDiv}>
                              <Collapse accordion expandIconPosition="left">
                                {(!isExternalUser ||
                                  visibilitySettings?.['staffingRolesOffered']) && (
                                    <Panel header="Staffing Roles Offered" key="1">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData?.staffingRolesOffered
                                        ) &&
                                          currentListing.attributes?.publicData?.staffingRolesOffered?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <h6>{item?.name}</h6>
                                                <Rate disabled value={item?.rating} />
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['staffingSkillsOffered']) && (
                                    <Panel header="Staffing Skills Offered" key="2">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData?.staffingSkillsOffered
                                        ) &&
                                          currentListing.attributes?.publicData?.staffingSkillsOffered?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <h6>{item?.name}</h6>
                                                {/* <span>
                                              {item?.skillExp}{' '}
                                              {item?.skillExp == 1 ? 'year' : 'years'}{' '}
                                            </span>{' '} */}
                                                <br />
                                                <Rate disabled value={item?.rating} />
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['linkedCustomers']) && (
                                    <Panel header="Staffing Clients" key="staffingPartners">
                                      <Table
                                        dataSource={linkItems('staffingCustomers')}
                                        columns={createCustomerColumns()}
                                        rowKey={record => record._id}
                                        pagination={partnerTablePaging('staffingCustomers')}
                                        locale={{
                                          emptyText: linkPageFailed('staffingCustomers')
                                            ? 'These links could not be loaded.'
                                            : 'No staffing clients yet',
                                        }}
                                      />
                                    </Panel>
                                  )}
                                {/* A supplier's own sub-suppliers, shown when it switched the list on. */}
                                {showsPreferredList('staffingSubSuppliers') && (
                                  <Panel header={<FormattedMessage id="ListingPage.subSuppliers.staffing" />} key="staffingSubSuppliers">
                                    {linkPageFailure('staffingSubSuppliers')}
                                    <div className={css.profileLists}>
                                      {linkItems('staffingSubSuppliers').map((item, index) => (
                                        <div className={css.profileListBox} key={index}>
                                          <h6>
                                            {item?.label}{' '}
                                            {item?.listingId ? (
                                              <a href={`/l/${item?.listingName}/${item?.listingId}`} target="_blank" rel="noopener noreferrer">
                                                <ExportOutlined />
                                              </a>
                                            ) : null}
                                          </h6>
                                        </div>
                                      ))}
                                    </div>
                                    {linkPager('staffingSubSuppliers')}
                                  </Panel>
                                )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['staffingPartnerships']) && (
                                    <Panel header="Staffing Alliances & Partnerships" key="3">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData?.staffingPartnerships
                                        ) &&
                                          currentListing.attributes?.publicData?.staffingPartnerships?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <List size="small" bordered={false}>
                                                  <List.Item>
                                                    <UserOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#d94452',
                                                      }}
                                                    />{' '}
                                                    {item?.label}
                                                  </List.Item>
                                                  <List.Item>Referal: {item?.referal}</List.Item>
                                                </List>
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['staffingKeyDeliveryLeaders']) && (
                                    <Panel header="Staffing Key Delivery Leaders" key="5">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.staffingKeyDeliveryLeaders
                                        ) &&
                                          currentListing.attributes?.publicData?.staffingKeyDeliveryLeaders?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox}>
                                                <List size="small" bordered={false}>
                                                  <List.Item>
                                                    <UserOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#d94452',
                                                      }}
                                                    />{' '}
                                                    {item?.firstName && item?.lastName
                                                      ? `${item.firstName} ${item.lastName}`
                                                      : item?.name || '-'}
                                                  </List.Item>
                                                  <List.Item>
                                                    <ContactsOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#f5b945',
                                                      }}
                                                    />{' '}
                                                    {item?.role || '-'}
                                                  </List.Item>
                                                  <List.Item>
                                                    <MailOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#5e9cea',
                                                      }}
                                                    />{' '}
                                                    {item?.email || '-'}
                                                  </List.Item>
                                                  <List.Item>
                                                    <MobileOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#9579da',
                                                      }}
                                                    />{' '}
                                                    {item?.phoneNumber || '-'}
                                                  </List.Item>
                                                </List>
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser || visibilitySettings?.['staffingClientList']) && (
                                  <Panel header="Staffing Client List" key="6">
                                    <div className={css.profileLists}>
                                      {Array.isArray(
                                        currentListing.attributes?.publicData?.staffingClientList
                                      ) &&
                                        currentListing.attributes?.publicData?.staffingClientList?.map(
                                          (item, index) => (
                                            <div className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />{' '}
                                                  {item?.clientName || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <FileTextOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />{' '}
                                                  {item?.projectDescription || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          )
                                        )}
                                    </div>
                                  </Panel>
                                )}
                                {(!isExternalUser || visibilitySettings?.['staffingCaseStudies']) && (
                                  <Panel header="Staffing Case Studies or Project Summaries" key="7">
                                    <div className={css.profileLists}>
                                      {Array.isArray(
                                        currentListing.attributes?.publicData?.staffingCaseStudies
                                      ) &&
                                        currentListing.attributes?.publicData?.staffingCaseStudies?.map(
                                          (item, index) => (
                                            <div className={css.profileListBox} key={index}>
                                              <h6>
                                                {item?.title}
                                                <br />
                                                {item?.summary}
                                                <br />
                                                {item?.technologies}
                                              </h6>
                                            </div>
                                          )
                                        )}
                                    </div>
                                  </Panel>
                                )}
                                {showsPreferredList('staffingConsultants') && (
                                  <Panel header="Staffing Consultants" key="8">
                                    {linkPageFailure('staffingConsultants')}
                                    <div className={css.profileLists}>
                                      {linkItems('staffingConsultants').map((item, index) => {
                                        let candidate = null;
                                        if ((item?.consultantId || item?.listingId) && candidatesListings?.length) {
                                          candidate = candidatesListings.find(
                                            x => x.id.uuid == item.listingId
                                          );
                                        }

                                        // Get initials for avatar
                                        const getInitials = name => {
                                          if (!name) return 'N/A';
                                          return name
                                            .split(' ')
                                            .map(word => word[0])
                                            .join('')
                                            .toUpperCase()
                                            .substring(0, 2);
                                        };

                                        if (item?.status == 'requested' && item?.listingId) {
                                          return null;
                                        }
                                        // Card for existing candidate (has joined iParley)
                                        if (candidate) {
                                          const candidateName =
                                            candidate.attributes?.title || item?.label || 'N/A';
                                          const candidateHeading =
                                            candidate.attributes?.publicData?.onBoardingInfo
                                              ?.candidateHeading || '';
                                          const candidateCity =
                                            candidate.attributes?.publicData?.cityAddress || '';
                                          const candidateImage =
                                            candidate.images?.[0]?.attributes?.variants?.[
                                              'square-small'
                                            ]?.url || Profile1;
                                          const profileSlug =
                                            item?.listingName || createSlug(candidateName);

                                          return (
                                            <div className={css.profileListBox} key={index}>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'flex-start',
                                                  gap: 16,
                                                  width: '100%',
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 16,
                                                    flex: 1,
                                                  }}
                                                >
                                                  <Avatar
                                                    size={46}
                                                    src={candidateImage}
                                                    style={
                                                      !candidateImage
                                                        ? {
                                                          backgroundColor: '#0e9941',
                                                          fontSize: '20px',
                                                          fontWeight: 600,
                                                        }
                                                        : {}
                                                    }
                                                  >
                                                    {!candidateImage && getInitials(candidateName)}
                                                  </Avatar>
                                                  <div style={{ flex: 1 }}>
                                                    <Text strong style={{ fontSize: 16 }}>
                                                      {candidateName}
                                                    </Text>
                                                    <br />
                                                    {candidateHeading && (
                                                      <>
                                                        <Text type="secondary">
                                                          {candidateHeading}
                                                        </Text>
                                                        <br />
                                                      </>
                                                    )}
                                                    {candidateCity && (
                                                      <div
                                                        style={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 4,
                                                          width: '100%',
                                                        }}
                                                      >
                                                        <EnvironmentOutlined
                                                          style={{ color: 'var(--marketplaceColor)' }}
                                                        />
                                                        <Text
                                                          type="secondary"
                                                          style={{ fontSize: 13 }}
                                                        >
                                                          {candidateCity}
                                                        </Text>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                                <Button
                                                  size="middle"
                                                  onClick={() => {
                                                    const profilePath = createResourceLocatorString(
                                                      'ListingPage',
                                                      routeConfiguration,
                                                      { id: item?.listingId, slug: profileSlug },
                                                      {}
                                                    );
                                                    history.push(profilePath);
                                                  }}
                                                >
                                                  View Profile
                                                </Button>
                                              </div>
                                              {item?.description && (
                                                <div
                                                  style={{
                                                    borderTop: '1px solid #f0f0f0',
                                                    marginTop: 16,
                                                    paddingTop: 12,
                                                  }}
                                                >
                                                  <Text>{item.description}</Text>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }

                                        // Card for non-existing candidate (yet to join iParley)
                                        const candidateName = item?.label || 'N/A';

                                        return (
                                          <div className={css.profileListBox} key={index}>
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 16,
                                                width: '100%',
                                              }}
                                            >
                                              <Avatar
                                                size={46}
                                                style={{
                                                  backgroundColor: '#faad14',
                                                  fontSize: '20px',
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {getInitials(candidateName)}
                                              </Avatar>
                                              <div>
                                                <Text strong style={{ fontSize: 16 }}>
                                                  {candidateName}
                                                </Text>
                                                <br />
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    width: '100%',
                                                  }}
                                                >
                                                  <InfoCircleOutlined style={{ color: '#faad14' }} />
                                                  <Text
                                                    type="secondary"
                                                    style={{ color: '#faad14', fontSize: 13 }}
                                                  >
                                                    Yet to join iParley
                                                  </Text>
                                                </div>
                                              </div>
                                            </div>
                                            {item?.description && (
                                              <div
                                                style={{
                                                  borderTop: '1px solid #f0f0f0',
                                                  marginTop: 16,
                                                  paddingTop: 12,
                                                }}
                                              >
                                                <Text>{item.description}</Text>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    {linkPager('staffingConsultants')}
                                  </Panel>
                                )}
                              </Collapse>
                            </div>
                          </div>
                        </div>
                      )}

                    {currentListing?.attributes?.publicData?.agencyProfile?.agencyType?.includes(
                      'Implementation'
                    ) && (
                        <div id="TabImplementation" className={css.profileSection}>
                          <div className={css.profileCardHeading}>
                            <h3 className={css.profileCardTitle}>
                              <img src={implementNav} alt="Implementation Summary" width={40} />{' '}
                              Implementation Summary
                            </h3>
                          </div>
                          <div className={css.profileCardContent}>
                            <Row gutter={[16, 16]}>
                              {(!isExternalUser || visibilitySettings?.['deliveryModels']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Implementation Delivery Models</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.deliveryModels
                                        )
                                          ? currentListing?.attributes?.publicData?.onBoardingInfo?.deliveryModels?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.deliveryModels || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['deliveryModels']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['deliveryModels']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                        <VerificationTag selfDeclared={true} />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['implementationIndustryFocus']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Implementation Industry Focus</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.implementationIndustryFocus
                                          )
                                            ? currentListing?.attributes?.publicData?.implementationIndustryFocus?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.implementationIndustryFocus || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['implementationIndustryFocus']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['implementationIndustryFocus']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser || visibilitySettings?.['projectTypesDelivered']) && (
                                <Col xs={24} xl={8}>
                                  <div className={css.formRow}>
                                    <div>
                                      <label>Implementation Project Types delivered</label>
                                      <h6>
                                        {Array.isArray(
                                          currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.projectTypesDelivered
                                        )
                                          ? currentListing?.attributes?.publicData?.onBoardingInfo?.projectTypesDelivered?.join(
                                            ', '
                                          )
                                          : currentListing?.attributes?.publicData?.onBoardingInfo
                                            ?.projectTypesDelivered || '-'}
                                      </h6>
                                    </div>
                                    {!isExternalUser && (
                                      <div className={css.formactions}>
                                        <Tag
                                          color={
                                            visibilitySettings?.['projectTypesDelivered']
                                              ? 'green'
                                              : 'default'
                                          }
                                        >
                                          {visibilitySettings?.['projectTypesDelivered']
                                            ? 'Public'
                                            : 'Private'}
                                        </Tag>
                                        <VerificationTag selfDeclared={true} />
                                      </div>
                                    )}
                                  </div>
                                </Col>
                              )}
                              {(!isExternalUser ||
                                visibilitySettings?.['implementationServiceDomains']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Implementation Service Domains</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.implementationServiceDomains
                                          )
                                            ? currentListing?.attributes?.publicData?.implementationServiceDomains?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.implementationServiceDomains || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['implementationServiceDomains']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['implementationServiceDomains']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['implementationGeographiesServed']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Implementation Geographies Served</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData
                                              ?.implementationGeographiesServed
                                          )
                                            ? currentListing?.attributes?.publicData?.implementationGeographiesServed?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData
                                              ?.implementationGeographiesServed || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['implementationGeographiesServed']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['implementationGeographiesServed']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {(!isExternalUser ||
                                visibilitySettings?.['certificationsFrameworks']) && (
                                  <Col xs={24} xl={8}>
                                    <div className={css.formRow}>
                                      <div>
                                        <label>Implementation Certifications or Frameworks</label>
                                        <h6>
                                          {Array.isArray(
                                            currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.certificationsFrameworks
                                          )
                                            ? currentListing?.attributes?.publicData?.onBoardingInfo?.certificationsFrameworks?.join(
                                              ', '
                                            )
                                            : currentListing?.attributes?.publicData?.onBoardingInfo
                                              ?.certificationsFrameworks || '-'}
                                        </h6>
                                      </div>
                                      {!isExternalUser && (
                                        <div className={css.formactions}>
                                          <Tag
                                            color={
                                              visibilitySettings?.['certificationsFrameworks']
                                                ? 'green'
                                                : 'default'
                                            }
                                          >
                                            {visibilitySettings?.['certificationsFrameworks']
                                              ? 'Public'
                                              : 'Private'}
                                          </Tag>
                                          <VerificationTag selfDeclared={true} />
                                        </div>
                                      )}
                                    </div>
                                  </Col>
                                )}
                            </Row>

                            <div className={css.accordianDiv}>
                              <Collapse accordion expandIconPosition="left">
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationRolesOffered']) && (
                                    <Panel header="Implementation Roles Offered" key="1">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationRolesOffered
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationRolesOffered?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <h6>{item?.name}</h6>
                                                <Rate disabled value={item?.rating} />
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationSkillsOffered']) && (
                                    <Panel header="Implementation Skills Offered" key="2">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationSkillsOffered
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationSkillsOffered?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <h6>{item?.name}</h6>
                                                {/* <span>
                                              {item?.skillExp}{' '}
                                              {item?.skillExp == 1 ? 'year' : 'years'}{' '}
                                            </span>{' '} */}
                                                <br />
                                                <Rate disabled value={item?.rating} />
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['linkedCustomers']) && (
                                    <Panel header="Implementation Clients" key="implementationPartners">
                                      <Table
                                        dataSource={linkItems('implementationCustomers')}
                                        columns={createCustomerColumns()}
                                        rowKey={record => record._id}
                                        pagination={partnerTablePaging('implementationCustomers')}
                                        locale={{
                                          emptyText: linkPageFailed('implementationCustomers')
                                            ? 'These links could not be loaded.'
                                            : 'No implementation clients yet',
                                        }}
                                      />
                                    </Panel>
                                  )}
                                {/* A supplier's own sub-suppliers, shown when it switched the list on. */}
                                {showsPreferredList('implementationSubSuppliers') && (
                                  <Panel header={<FormattedMessage id="ListingPage.subSuppliers.implementation" />} key="implementationSubSuppliers">
                                    {linkPageFailure('implementationSubSuppliers')}
                                    <div className={css.profileLists}>
                                      {linkItems('implementationSubSuppliers').map((item, index) => (
                                        <div className={css.profileListBox} key={index}>
                                          <h6>
                                            {item?.label}{' '}
                                            {item?.listingId ? (
                                              <a href={`/l/${item?.listingName}/${item?.listingId}`} target="_blank" rel="noopener noreferrer">
                                                <ExportOutlined />
                                              </a>
                                            ) : null}
                                          </h6>
                                        </div>
                                      ))}
                                    </div>
                                    {linkPager('implementationSubSuppliers')}
                                  </Panel>
                                )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationPartnerships']) && (
                                    <Panel header="Implementation Alliances & Partnerships" key="3">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationPartnerships
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationPartnerships?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <UserOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#d94452',
                                                  }}
                                                />{' '}
                                                {item?.label}
                                                <br />
                                                {item?.id && (
                                                  <a
                                                    href={`/l/${item?.listingId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    <ExportOutlined />
                                                  </a>
                                                )}
                                                Referal: {item?.referal}
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationKeyDeliveryLeaders']) && (
                                    <Panel header="Implementation Key Delivery Leaders" key="5">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationKeyDeliveryLeaders
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationKeyDeliveryLeaders?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox} key={index}>
                                                <div className={css.profileListBox}>
                                                  <List size="small" bordered={false}>
                                                    <List.Item>
                                                      <UserOutlined
                                                        style={{
                                                          fontSize: 18,
                                                          marginRight: 5,
                                                          color: '#d94452',
                                                        }}
                                                      />{' '}
                                                      {item?.firstName && item?.lastName
                                                        ? `${item.firstName} ${item.lastName}`
                                                        : item?.name || '-'}
                                                    </List.Item>
                                                    <List.Item>
                                                      <ContactsOutlined
                                                        style={{
                                                          fontSize: 18,
                                                          marginRight: 5,
                                                          color: '#f5b945',
                                                        }}
                                                      />{' '}
                                                      {item?.role || '-'}
                                                    </List.Item>
                                                    <List.Item>
                                                      <MailOutlined
                                                        style={{
                                                          fontSize: 18,
                                                          marginRight: 5,
                                                          color: '#5e9cea',
                                                        }}
                                                      />{' '}
                                                      {item?.email || '-'}
                                                    </List.Item>
                                                    <List.Item>
                                                      <MobileOutlined
                                                        style={{
                                                          fontSize: 18,
                                                          marginRight: 5,
                                                          color: '#9579da',
                                                        }}
                                                      />{' '}
                                                      {item?.phoneNumber || '-'}
                                                    </List.Item>
                                                  </List>
                                                </div>
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationClientList']) && (
                                    <Panel header="Implementation Client List" key="6">
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationClientList
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationClientList?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox}>
                                                <List size="small" bordered={false}>
                                                  <List.Item>
                                                    <UserOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#d94452',
                                                      }}
                                                    />{' '}
                                                    {item?.clientName || '-'}
                                                  </List.Item>
                                                  <List.Item>
                                                    <FileTextOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#f5b945',
                                                      }}
                                                    />{' '}
                                                    {item?.projectDescription || '-'}
                                                  </List.Item>
                                                </List>
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {(!isExternalUser ||
                                  visibilitySettings?.['implementationCaseStudies']) && (
                                    <Panel
                                      header="Implementation Case Studies or Project Summaries"
                                      key="7"
                                    >
                                      <div className={css.profileLists}>
                                        {Array.isArray(
                                          currentListing.attributes?.publicData
                                            ?.implementationCaseStudies
                                        ) &&
                                          currentListing.attributes?.publicData?.implementationCaseStudies?.map(
                                            (item, index) => (
                                              <div className={css.profileListBox}>
                                                <List size="small" bordered={false}>
                                                  <List.Item>
                                                    <ContactsOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#d94452',
                                                      }}
                                                    />{' '}
                                                    {item?.title}
                                                  </List.Item>
                                                  <List.Item>
                                                    <FileTextOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#f5b945',
                                                      }}
                                                    />{' '}
                                                    {item?.summary}
                                                  </List.Item>
                                                  <List.Item>
                                                    <SettingOutlined
                                                      style={{
                                                        fontSize: 18,
                                                        marginRight: 5,
                                                        color: '#f5b945',
                                                      }}
                                                    />{' '}
                                                    {item?.technologies}
                                                  </List.Item>
                                                </List>
                                              </div>
                                            )
                                          )}
                                      </div>
                                    </Panel>
                                  )}
                                {showsPreferredList('implementationConsultants') && (
                                    <Panel header="Implementation Consultants" key="8">
                                      {linkPageFailure('implementationConsultants')}
                                      <div className={css.profileLists}>
                                        {linkItems('implementationConsultants').map((item, index) => {
                                          let candidate = null;
                                          if ((item?.consultantId || item?.listingId) && candidatesListings?.length) {
                                            candidate = candidatesListings.find(
                                              x => x.id.uuid == item.listingId
                                            );
                                          }

                                          // Get initials for avatar
                                          const getInitials = name => {
                                            if (!name) return 'N/A';
                                            return name
                                              .split(' ')
                                              .map(word => word[0])
                                              .join('')
                                              .toUpperCase()
                                              .substring(0, 2);
                                          };

                                          if (item?.status == 'requested' && item?.listingId) {
                                            return null;
                                          }
                                          // Card for existing candidate (has joined iParley)
                                          if (candidate) {
                                            const candidateName =
                                              candidate.attributes?.title || item?.label || 'N/A';
                                            const candidateHeading =
                                              candidate.attributes?.publicData?.onBoardingInfo
                                                ?.candidateHeading || '';
                                            const candidateCity =
                                              candidate.attributes?.publicData?.cityAddress || '';
                                            const candidateImage =
                                              candidate.images?.[0]?.attributes?.variants?.[
                                                'square-small'
                                              ]?.url || Profile1;
                                            const profileSlug =
                                              item?.listingName || createSlug(candidateName);

                                            return (
                                              <div className={css.profileListBox} key={index}>
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    gap: 16,
                                                    width: '100%',
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'flex-start',
                                                      gap: 16,
                                                      flex: 1,
                                                    }}
                                                  >
                                                    <Avatar
                                                      size={46}
                                                      src={candidateImage}
                                                      style={
                                                        !candidateImage
                                                          ? {
                                                            backgroundColor: '#0e9941',
                                                            fontSize: '20px',
                                                            fontWeight: 600,
                                                          }
                                                          : {}
                                                      }
                                                    >
                                                      {!candidateImage && getInitials(candidateName)}
                                                    </Avatar>
                                                    <div style={{ flex: 1 }}>
                                                      <Text strong style={{ fontSize: 16 }}>
                                                        {candidateName}
                                                      </Text>
                                                      <br />
                                                      {candidateHeading && (
                                                        <>
                                                          <Text type="secondary">
                                                            {candidateHeading}
                                                          </Text>
                                                          <br />
                                                        </>
                                                      )}
                                                      {candidateCity && (
                                                        <div
                                                          style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            width: '100%',
                                                          }}
                                                        >
                                                          <EnvironmentOutlined
                                                            style={{ color: 'var(--marketplaceColor)' }}
                                                          />
                                                          <Text
                                                            type="secondary"
                                                            style={{ fontSize: 13 }}
                                                          >
                                                            {candidateCity}
                                                          </Text>
                                                        </div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <Button
                                                    size="middle"
                                                    onClick={() => {
                                                      const profilePath = createResourceLocatorString(
                                                        'ListingPage',
                                                        routeConfiguration,
                                                        { id: item?.listingId, slug: profileSlug },
                                                        {}
                                                      );
                                                      history.push(profilePath);
                                                    }}
                                                  >
                                                    View Profile
                                                  </Button>
                                                </div>
                                                {item?.description && (
                                                  <div
                                                    style={{
                                                      borderTop: '1px solid #f0f0f0',
                                                      marginTop: 16,
                                                      paddingTop: 12,
                                                    }}
                                                  >
                                                    <Text>{item.description}</Text>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          }

                                          // Card for non-existing candidate (yet to join iParley)
                                          const candidateName = item?.label || 'N/A';

                                          return (
                                            <div className={css.profileListBox} key={index}>
                                              <div
                                                style={{
                                                  display: 'flex',
                                                  alignItems: 'flex-start',
                                                  gap: 16,
                                                  width: '100%',
                                                }}
                                              >
                                                <Avatar
                                                  size={46}
                                                  style={{
                                                    backgroundColor: '#faad14',
                                                    fontSize: '20px',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {getInitials(candidateName)}
                                                </Avatar>
                                                <div>
                                                  <Text strong style={{ fontSize: 16 }}>
                                                    {candidateName}
                                                  </Text>
                                                  <br />
                                                  <div
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 4,
                                                      width: '100%',
                                                    }}
                                                  >
                                                    <InfoCircleOutlined style={{ color: '#faad14' }} />
                                                    <Text
                                                      type="secondary"
                                                      style={{ color: '#faad14', fontSize: 13 }}
                                                    >
                                                      Yet to join iParley
                                                    </Text>
                                                  </div>
                                                </div>
                                              </div>
                                              {item?.description && (
                                                <div
                                                  style={{
                                                    borderTop: '1px solid #f0f0f0',
                                                    marginTop: 16,
                                                    paddingTop: 12,
                                                  }}
                                                >
                                                  <Text>{item.description}</Text>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      {linkPager('implementationConsultants')}
                                    </Panel>
                                  )}
                              </Collapse>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* <div id="Tab2" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={skillNav} alt="Skills" width={40} />Skills offered</h3>

                      </div>
                      <div className={css.profileCardContent}>
                        <div className={css.profileLists}>
                          {Array.isArray(currentListing.attributes?.publicData?.skillsOffered) && currentListing.attributes.publicData.skillsOffered.map((item, index) => {

                            return (
                              <div className={css.profileListBox} key={index}>
                                <h6>{item?.name || '-'}</h6>
                                <Rate disabled value={item?.rating || 0} />
                              </div>
                            );
                          })}


                        </div>
                      </div>
                    </div> */}

                    {/* <div id="Tab8" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={aboutNav} alt="About" width={40} /> About {listingIsAgency ? 'Agency' : 'Company'} </h3>

                      </div>
                      <div className={css.profileCardContent}>

                        <Row gutter={20}>
                          <Col xs={24} sm={24} md={12} lg={6}>
                            <List
                              itemLayout="horizontal"
                              dataSource={aboutdata}
                              renderItem={(item) => (
                                <List.Item>
                                  <List.Item.Meta
                                    avatar={item.avatar}
                                    title={item.title}
                                    description={
                                      (item.title === 'Email' || item.title === 'Type' || item.title === 'Website') ? (
                                        <span className={css.breakall}>{item.description}</span>
                                      ) : (
                                        item.description
                                      )
                                    }
                                  />
                                </List.Item>
                              )}
                            />
                          </Col>
                          <Col xs={24} sm={24} md={12} lg={18}>
                            <h5> <b>{listingIsAgency ? currentListing.attributes?.publicData?.agencyProfile?.name : currentListing.attributes?.publicData?.customerProfile?.name}</b></h5>
                            <Space style={{ fontSize: '16px' }}>
                              <FacebookOutlined />
                              <InstagramOutlined />
                              <XOutlined />
                              <BehanceOutlined />
                            </Space>
                            <div className={css.aboutContainer}>
                              <p>{listingIsAgency ? currentListing.attributes?.publicData?.agencyProfile?.agencyDescription : currentListing.attributes?.publicData?.customerProfile?.customerDescription}</p>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div> */}

                    {/* <div id="Tab3" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={verificationNav} alt="Verification Summary" width={40} /> Verification Summary</h3>

                      </div>
                      <div className={css.profileCardContent}>

                        <div className={`${css.profileLists} ${css.profileListWide}`}>
                          <div className={css.profileListBox}>
                            <div className={css.profileHead}>
                              <h5><Badge dot /> Identity</h5>
                              <Dropdown menu={{ items }}>
                                <a onClick={(e) => e.preventDefault()}>
                                  <Space>
                                    <MoreOutlined />
                                  </Space>
                                </a>
                              </Dropdown>
                            </div>
                            <p><small>(Driving Licence / State ID / Passport)</small></p>
                            <div className={css.profileBody}>
                              <Timeline
                                items={[
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Name</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Address</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={unverified} alt='verified' width="22" title="Not Verified" />,
                                    children: <h6>DOB</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={unverified} alt='verified' width="22" title="Not Verified" />,
                                    children: <h6>Document #, Expiry</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Social Security Number</h6>,
                                  },
                                ]}
                              />
                            </div>
                          </div>

                          <div className={css.profileListBox}>
                            <div className={css.profileHead}>
                              <h5><Badge dot /> Work Authorisation</h5>
                              <Dropdown menu={{ items }}>
                                <a onClick={(e) => e.preventDefault()}>
                                  <Space>
                                    <MoreOutlined />
                                  </Space>
                                </a>
                              </Dropdown>
                            </div>
                            <p><small>(Passport / PR Card /)</small></p>
                            <div className={css.profileBody}>
                              <Timeline
                                items={[
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Name</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Address</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={unverified} alt='verified' width="22" title="Not Verified" />,
                                    children: <h6>DOB</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={unverified} alt='verified' width="22" title="Not Verified" />,
                                    children: <h6>Document #, Expiry</h6>,
                                  },
                                  {
                                    color: '#1da0f2',
                                    dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                    children: <h6>Social Security Number</h6>,
                                  },
                                ]}
                              />
                            </div>
                          </div>

                          {listingIsCandidate ?
                            <>
                              <div className={css.profileListBox}>
                                <div className={css.profileHead}>
                                  <h5><Badge dot /> Education</h5>
                                  <Dropdown menu={{ items }}>
                                    <a onClick={(e) => e.preventDefault()}>
                                      <Space>
                                        <MoreOutlined />
                                      </Space>
                                    </a>
                                  </Dropdown>
                                </div>
                                <div className={css.profileBody}>
                                  <Timeline
                                    items={[
                                      {
                                        color: '#1da0f2',
                                        dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                        children:
                                          <div>
                                            <h6>BSc. CS</h6>
                                            <div>Pass Out Year: 2007</div>
                                          </div>,
                                      },
                                      {
                                        color: '#1da0f2',
                                        dot: <img src={verified} alt='verified' width="22" title="Verified" />,
                                        children:
                                          <div>
                                            <h6>CMMI Certificate</h6>
                                            <div>Issue Date : 02-05-2010</div>
                                            <div>Expiry Date : 02-05-2020</div>
                                          </div>,
                                      },
                                      {
                                        color: '#1da0f2',
                                        dot: <img src={unverified} alt='verified' width="22" title="Not Verified" />,
                                        children:
                                          <div>
                                            <h6>AI Certificate</h6>
                                            <div>Issue Date : 02-05-2020</div>
                                            <div>Expiry Date : NIL</div>
                                          </div>,
                                      },
                                    ]}
                                  />
                                </div>
                              </div>
                              <div id="Tab15" className={css.profileSection}>
                                <div className={css.profileCardHeading}>
                                  <h3 className={css.profileCardTitle}><img src={educationsNav} alt="Recommendations" width={40} /> Recommendations</h3>

                                </div>
                                <div className={css.profileCardContent}>
                                  <div>
                                    <Flex gap="small" wrap>
                                      <Tooltip title="Recommendations">
                                        <span >Recommendations </span>
                                      </Tooltip>
                                    </Flex>
                                  </div>
                                  <Timeline
                                    items={currentListing?.attributes?.publicData?.recommendations?.filter(item => item?.recommendation_approved === true)?.map(item => ({
                                      children: (
                                        <>
                                          {item?.recommendation_approved && (
                                            <>
                                              <h4>{item?.recommendation_from?.id ? <a href={`/l/${item?.recommendation_from?.listingId}`} style={{ color: 'black' }}>{item?.recommendation_from?.label}</a> : <span>{item?.recommendation_from?.label}</span>}</h4>
                                              {item?.recommendation_approved === undefined && <div>Request description: {item?.request_description}</div>}
                                              {item?.recommendation_approved === true && <div>{item?.description}</div>}
                                            </>
                                          )}
                                        </>
                                      ),
                                    }))}
                                  />

                                  <div>
                                    <Flex gap="small" wrap>
                                      <Tooltip title="Recommendations Given">
                                        <span >Recommendations Given</span>
                                      </Tooltip>
                                    </Flex>

                                    <Timeline
                                      items={currentListing?.attributes?.publicData?.recommendationsGiven?.filter(item => item?.recommendation_approved === true)?.map(item => ({
                                        children: (
                                          <>
                                            {item?.recommendation_approved && (
                                              <>
                                                <h4>{item?.recommendation_to?.id ? <a href={`/l/${item?.recommendation_to?.listingId}`} style={{ color: 'black' }}>{item?.recommendation_to?.label}</a> : <span>{item?.recommendation_to?.label}</span>}</h4>
                                                <div>{item?.description && <>Description: {item?.description}</>}</div>
                                                <div>Status: {item?.recommendation_approved === undefined ? 'Pending' : item?.recommendation_approved === true ? 'Approved' : 'Denied'}</div>
                                              </>
                                            )}
                                          </>
                                        ),
                                      }))}
                                    />
                                  </div>
                                </div>
                              </div>
                            </>
                            : null}
                        </div>
                      </div>

                    </div> */}

                    {/* <div id="Tab9" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={vendorshipsNav} alt="Vendorships" width={40} /> Vendorships</h3>

                      </div>
                      <div className={css.profileCardContent}>
                        <div className={css.profileLists}>
                          {currentListing.attributes?.publicData?.vendorships?.map((item, index) => {
                            return (
                              <div className={css.profileListBox}>
                                <h6>{item?.name} {item?.link ? <a href={item?.link} target='_blank'><ExportOutlined /></a> : null}</h6>
                                <p>Referal: {item?.referal}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div> */}

                    {/* <div id="Tab10" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={partnershipsNav} alt="Partnerships" width={40} /> Partnerships</h3>

                      </div>
                      <div className={css.profileCardContent}>
                        <div className={css.profileLists}>
                          {currentListing.attributes?.publicData?.partnerships?.map((item, index) => {
                            return (
                              <div className={css.profileListBox}>
                                <h6>
                                  {item?.label}
                                  {item?.id && <a href={`/u/${item?.id}`} target='_blank'><ExportOutlined /></a>}
                                </h6>
                                <p>Referal: {item?.referal}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div> */}

                    {/* <div id="Tab11" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={servicesNav} alt="Services" width={40} /> Services</h3>

                      </div>
                      <div className={css.profileCardContent}>
                        <div className={`${css.profileLists} ${css.profileListWide}`}>

                          {Array.isArray(currentListing.attributes?.publicData?.servicesOffered) && currentListing.attributes.publicData.servicesOffered.map((item, index) => {
                            // const formatPrice = item?.serviceCost ? formatMoney(intl, item?.serviceCost ? (item?.serviceCost/100) : new Money(0 * 100, 'USD')) : '';

                            // const p = item?.serviceCost ? new Money( item?.serviceCost?.amount, 'USD') :  new Money( 0, 'USD');
                            // const format = formatMoney(intl, p);
                            const serviceCostStr = typeof item?.serviceCost === 'string' ? item.serviceCost : String(item?.serviceCost || '');
                            const format = serviceCostStr ? formatMoney(intl, new Money(Math.round(parseFloat(serviceCostStr.replace(/[^0-9.-]+/g, '') || '0') * 100), "USD")) : "";
                            item.format = format;

                            return (
                              <Badge.Ribbon text={`${format}/hr`}>
                                <div className={css.profileListBoxCard}>
                                  <div className={css.profileHead}>
                                    <h5><Badge dot />{item?.name}</h5>
                                  </div>
                                  <div>
                                    <div><Rate disabled value={item?.rating} /></div>
                                    <div><a href={item?.link} target='_blank'>View website</a></div>
                                    <p>
                                      {item?.description?.length > 70 ?
                                        <>
                                          {item.description.slice(0, 70)}
                                          <a onClick={() => { setCardDetails(item); showModal('showServiceCard') }}>...see more</a>
                                        </>
                                        : item?.description}
                                    </p>
                                  </div>
                                </div>
                              </Badge.Ribbon>
                            );
                          })}

                        </div>
                      </div>
                    </div> */}
                  </>
                ) : null}

                {listingIsCustomer ? (
                  <>
                    <div id="TabBusinessReg" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={registrationNav} alt="Business Registration" width={40} />{' '}
                          Business Registration
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['customerProfileName']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Company Name</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.name || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerProfileName']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerProfileName']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'customerProfileName',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['customerAlternateName']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>DBA/Alternate Name</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerAlternateName || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerAlternateName']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerAlternateName']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'customerAlternateName',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['countryOfFormation']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Country of Formation</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.formationDetails
                                      ?.countryOfFormation || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['countryOfFormation']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['countryOfFormation']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'countryOfFormation',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['stateOfIncorp']) &&
                            currentListing?.attributes?.publicData?.formationDetails
                              ?.countryOfFormation === 'United States' && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>State of Incorporation (Domestic Main Office)</label>
                                    <h6>
                                      {currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.stateOfIncorp || '-'}
                                    </h6>
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['stateOfIncorp']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['stateOfIncorp']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag
                                        isVerified={getFieldVerificationStatus(
                                          effectivePersonaChecks,
                                          'stateOfIncorp',
                                          personaRecords
                                        )}
                                      />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                          {(!isExternalUser || visibilitySettings?.['statesIncorpForeign']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>States incorporated as Foreign Entity</label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.statesIncorpForeign
                                    )
                                      ? currentListing?.attributes?.publicData?.onBoardingInfo?.statesIncorpForeign?.join(
                                        ', '
                                      )
                                      : currentListing?.attributes?.publicData?.onBoardingInfo
                                        ?.statesIncorpForeign || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['statesIncorpForeign']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['statesIncorpForeign']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['customerAddress']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Company Address</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.address?.selectedPlace?.address || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerAddress']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerAddress']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'customerAddress',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['dateOfFormation']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Date of Formation</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.onBoardingInfo
                                      ?.dateOfFormation || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['dateOfFormation']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['dateOfFormation']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'dateOfFormation',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['typeOfBusiness']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Business type</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.typeOfBusiness || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['typeOfBusiness'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['typeOfBusiness']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'typeOfBusiness',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['publicOrPrivateCompany']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Public or Private</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.publicOrPrivateCompany || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['publicOrPrivateCompany']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['publicOrPrivateCompany']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['identificationType']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Tax Identification Type</label>
                                  <h6>{currentListing?.attributes?.publicData?.companyIdentification?.identificationType || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['identificationType'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['identificationType'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'identificationType', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['identificationNumber']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Tax Identification Number</label>
                                  <h6>{currentListing?.attributes?.publicData?.companyIdentification?.identificationNumber || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['identificationNumber'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['identificationNumber'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'identificationNumber', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['taxClassification']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Federal Tax Classification</label>
                                  <h6>{currentListing?.attributes?.publicData?.companyIdentification?.taxClassification || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['taxClassification'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['taxClassification'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'taxClassification', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                        </Row>
                      </div>
                    </div>

                    <div id="TabBusinessInfo" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={infoNav} alt="Business Information" width={40} /> Business
                          Information
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['customerDescription']) && (
                            <Col xs={24}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Company Description</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerDescription || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerDescription']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerDescription']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['customerWebsite']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Website</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerWebsite || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerWebsite']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerWebsite']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'customerWebsite',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['customerLinkedInPage']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>LinkedIn Page</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerLinkedInPage || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerLinkedInPage']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerLinkedInPage']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['employeeCount']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Employee count</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.employeeCount || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['employeeCount'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['employeeCount'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'employeeCount',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['annualRevenue']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Annual Revenue</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.annualRevenue || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['annualRevenue'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['annualRevenue'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'annualRevenue',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['customerMail']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Contact Email Address</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerMail || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerMail'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerMail'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag
                                      isVerified={getFieldVerificationStatus(
                                        effectivePersonaChecks,
                                        'customerMail',
                                        personaRecords
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['subcontractingPolicy']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Subcontracting Policy</label>
                                  <h6>{currentListing?.attributes?.publicData?.subcontractingPolicy || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['subcontractingPolicy'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['subcontractingPolicy'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {(!isExternalUser || visibilitySettings?.['geographiesServed']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Geographies Served</label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.geographiesServed
                                    )
                                      ? currentListing?.attributes?.publicData?.geographiesServed?.join(
                                        ', '
                                      )
                                      : currentListing?.attributes?.publicData?.geographiesServed ||
                                      '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['geographiesServed']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['geographiesServed']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </div>

                    <div id="TabInsurance" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img
                            src={insuranceNav}
                            alt="Ownership and Management Details"
                            width={40}
                          />{' '}
                          Ownership and Management Details
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        {/* <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['insuranceDetailsVisibility']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Insurance Details Visibility</label>
                                  <h6>{currentListing?.attributes?.publicData?.insuranceDetailsVisibility || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['insuranceDetailsVisibility'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['insuranceDetailsVisibility'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['insuranceCertificateUrl']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Insurance Details Certificate</label>
                                  <h6>{currentListing?.attributes?.publicData?.insuranceCertificateUrl ? <a href={currentListing?.attributes?.publicData?.insuranceCertificateUrl} target='_blank'> File </a> : '-'}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['insuranceCertificateUrl'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['insuranceCertificateUrl'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag isVerified={getFieldVerificationStatus(effectivePersonaChecks, 'insuranceCertificateUrl', personaRecords)} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                        </Row> */}
                        <div className={css.accordianDiv}>
                          <Collapse accordion expandIconPosition="left">
                            {(!isExternalUser || visibilitySettings?.['insuranceDetails']) && listingIsAgency && (
                              <Panel header="Insurance Details" key="4">
                                <div className={css.profileLists}>
                                  {Array.isArray(currentListing?.attributes?.publicData?.insuranceDetails) && currentListing?.attributes?.publicData?.insuranceDetails?.map((item, index) => (
                                    <div key={item?.id || index} className={css.profileListBox}>
                                      <SafetyOutlined style={{ fontSize: 18, marginRight: 5, color: "#35ba9b" }} /> <b>{item?.category} </b>
                                      <ul className={css.coverageList}>
                                        {Object.entries(item?.coverageAmounts || {}).map(([coverageType, amount], i) => (
                                          <li key={i}>
                                            <strong>{coverageType}:</strong> $ {amount}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </Panel>
                            )}
                            {companyCredentialsLoaded && (
                              <Panel header="Beneficial Owners" key="1">
                                <div className={css.profileLists}>
                                  {Array.isArray(companyCredentials.beneficialOwners) &&
                                    companyCredentials.beneficialOwners.map(
                                      (item, index) => {
                                        if (item?.ownerType === 'individual') {
                                          return (
                                            <div key={index} className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />
                                                  {item?.firstName || '-'} {item?.middleName || ''}{' '}
                                                  {item?.lastName || ''}
                                                </List.Item>
                                                <List.Item>
                                                  <PercentageOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  {item?.ownershipPercentage + '%' || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <EnvironmentOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#35ba9b',
                                                    }}
                                                  />
                                                  {item?.addressDetail?.selectedPlace?.address || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PhoneOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d56fac',
                                                    }}
                                                  />
                                                  {item?.workLandlineNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }

                                        if (item?.ownerType === 'company') {
                                          return (
                                            <div key={index} className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <BankOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />
                                                  {item?.companyName || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <NumberOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  Reg. No: {item?.registrationNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PercentageOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />
                                                  {item?.ownershipPercentage + '%' || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <EnvironmentOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#35ba9b',
                                                    }}
                                                  />
                                                  {item?.addressDetail?.selectedPlace?.address || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <PhoneOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d56fac',
                                                    }}
                                                  />
                                                  {item?.workLandlineNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }

                                        // fallback if ownerType is missing or unknown
                                        return null;
                                      }
                                    )}
                                </div>
                              </Panel>
                            )}
                            {(!isExternalUser ||
                              visibilitySettings?.['keyManagementPersonnel']) && (
                                <Panel header="Key Management Personnel" key="2">
                                  <div className={css.profileLists}>
                                    {Array.isArray(
                                      currentListing.attributes?.publicData?.keyManagementPersonnel
                                    ) &&
                                      currentListing.attributes?.publicData?.keyManagementPersonnel?.map(
                                        (item, index) => {
                                          return (
                                            <div className={css.profileListBox}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />{' '}
                                                  {item?.firstName && item?.lastName
                                                    ? `${item.firstName} ${item.lastName}`
                                                    : item?.name || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <ContactsOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />{' '}
                                                  {item?.role || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />{' '}
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />{' '}
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>
                                </Panel>
                              )}
                            {(!isExternalUser ||
                              visibilitySettings?.['keyProcurementContacts']) && (
                                <Panel header="Key Procurement Contacts" key="3">
                                  <div className={css.profileLists}>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.keyProcurementContacts
                                    ) &&
                                      currentListing?.attributes?.publicData?.keyProcurementContacts?.map(
                                        (item, index) => {
                                          return (
                                            <div className={css.profileListBox} key={index}>
                                              <List size="small" bordered={false}>
                                                <List.Item>
                                                  <UserOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#d94452',
                                                    }}
                                                  />{' '}
                                                  {item?.name || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <ContactsOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#f5b945',
                                                    }}
                                                  />{' '}
                                                  {item?.title || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MailOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#5e9cea',
                                                    }}
                                                  />{' '}
                                                  {item?.email || '-'}
                                                </List.Item>
                                                <List.Item>
                                                  <MobileOutlined
                                                    style={{
                                                      fontSize: 18,
                                                      marginRight: 5,
                                                      color: '#9579da',
                                                    }}
                                                  />{' '}
                                                  {item?.phoneNumber || '-'}
                                                </List.Item>
                                              </List>
                                            </div>
                                          );
                                        }
                                      )}
                                  </div>
                                </Panel>
                              )}
                            {(!isExternalUser || visibilitySettings?.['authorizedSignatories']) && (
                              <Panel header="Authorized Signatories" key="4">
                                <div className={css.profileLists}>
                                  {Array.isArray(
                                    currentListing.attributes?.publicData?.authorizedSignatories
                                  ) &&
                                    currentListing.attributes?.publicData?.authorizedSignatories?.map(
                                      (item, index) => {
                                        return (
                                          <div className={css.profileListBox}>
                                            <List size="small" bordered={false}>
                                              <List.Item>
                                                <UserOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#d94452',
                                                  }}
                                                />{' '}
                                                {item?.firstName && item?.lastName
                                                  ? `${item.firstName} ${item.lastName}`
                                                  : item?.name || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <ContactsOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#f5b945',
                                                  }}
                                                />{' '}
                                                {item?.role || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <MailOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#5e9cea',
                                                  }}
                                                />{' '}
                                                {item?.email || '-'}
                                              </List.Item>
                                              <List.Item>
                                                <MobileOutlined
                                                  style={{
                                                    fontSize: 18,
                                                    marginRight: 5,
                                                    color: '#9579da',
                                                  }}
                                                />{' '}
                                                {item?.phoneNumber || '-'}
                                              </List.Item>
                                            </List>
                                          </div>
                                        );
                                      }
                                    )}
                                </div>
                              </Panel>
                            )}
                          </Collapse>
                        </div>
                      </div>
                    </div>

                    {!isExternalUser ? (
                      // Customer Verification Summary
                      <div id="Tab3" className={css.profileSection}>
                        <div className={css.profileCardHeading}>
                          <h3 className={css.profileCardTitle}>
                            <img src={verificationNav} alt="Verification Summary" width={40} />{' '}
                            Verification Summary
                          </h3>
                        </div>
                        <div className={css.profileCardContentFull}>
                          <div className={css.profileMediaContainer}>
                            <div className={css.profileMedia}>
                              <div>
                                <img src={identityIcon} alt="identity" />
                              </div>
                              <div className={css.profileMediaBox}>
                                <div className={css.profileMediaText}>
                                  <h6>Business Identity and Registry</h6>
                                  <Progress
                                    type="circle"
                                    percent={businessIdentityProgress}
                                    status={
                                      businessIdentityProgress === 100 ? 'success' : 'exception'
                                    }
                                    size={20}
                                  />
                                </div>
                                <Progress percent={businessIdentityProgress} size="small" />
                              </div>
                            </div>
                          </div>
                          <div className={`${css.profileLists} ${css.profileListWide}`}>
                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot /> Business Identity and Registry
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBBusinessDetails({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Business Identity and Registry', personaChecks['Business Registry Verification (KYB)'], 'business-identity', isOwnListing, personaChecks['Business Registry Verification (KYB)'])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot />
                                    Beneficial Owner Identity
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBBeneficialOwners({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Beneficial Owner Identity', personaChecks['Business Registry Verification (KYB)']?.beneficialOwners || [], 'beneficial-owner', isOwnListing, personaChecks['Business Registry Verification (KYB)']?.beneficialOwners || [])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot />
                                    Authorized Person Identity
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBAuthorizedPersons({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                                {/* <div className={css.requestButtonContainer}>
                                  <Button
                                    icon={isOwnListing ? <ShareAltOutlined /> : <FileSearchOutlined />}
                                    onClick={() => handleRequestDocument('Authorized Person Identity', personaChecks['Business Registry Verification (KYB)']?.authorizedPersons || [], 'authorized-person', isOwnListing, personaChecks['Business Registry Verification (KYB)']?.authorizedPersons || [])}
                                  >
                                    {isOwnListing ? 'Share All' : 'Request All'}
                                  </Button>
                                </div> */}
                              </div>
                            </div>

                            <div className={css.profileListBox}>
                              <div className={css.profileHead}>
                                <div>
                                  <h5>
                                    <Badge dot /> Insurance and Reports
                                  </h5>
                                  <p>
                                    <small> </small>
                                  </p>
                                </div>
                              </div>
                              <div className={css.profileBody}>
                                {isBusinessIdentityVerified ? (
                                  renderKYBInsuranceAndReports({
                                    personaChecks: kybDisplayChecks,
                                    documentType: 'Business Registry Verification (KYB)',
                                    verifyIcon,
                                    unveriffiedIcon,
                                    handleRequestDocument,
                                    css,
                                    isOwnListing,
                                  })
                                ) : (
                                  <p style={{ color: '#888', fontStyle: 'italic' }}>
                                    Verification pending
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}

                    <div id="TabServicesNeed" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={infoNav} alt="Services Need Summary" width={40} /> Services Need
                          Summary
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <Row gutter={[16, 16]}>
                          {(!isExternalUser || visibilitySettings?.['customerType']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Service Needs</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.customerProfile
                                      ?.customerType || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['customerType'] ? 'green' : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['customerType'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['workAuthSponsorship']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Work authorization sponsorship</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.onBoardingInfo
                                      ?.workAuthSponsorship || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['workAuthSponsorship']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['workAuthSponsorship']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['sponsorshipPolicyVisibility']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Sponsorship policy visibility</label>
                                  <h6>{currentListing?.attributes?.publicData?.onBoardingInfo?.sponsorshipPolicyVisibility || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['sponsorshipPolicyVisibility'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['sponsorshipPolicyVisibility'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {(!isExternalUser || visibilitySettings?.['procurementProcessType']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Procurement Process Type</label>
                                  <h6>
                                    {currentListing?.attributes?.publicData?.onBoardingInfo
                                      ?.procurementProcessType || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['procurementProcessType']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['procurementProcessType']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['jobRfpVisibility']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Job posts and RFP posts visibility</label>
                                  <h6>
                                    {currentListing.attributes?.publicData?.onBoardingInfo
                                      ?.jobRfpVisibility ? (
                                      <PostingVisibilityLabel
                                        value={currentListing.attributes.publicData.onBoardingInfo.jobRfpVisibility}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['jobRfpVisibility']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['jobRfpVisibility']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['contractTypesSupported']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Contract types supported</label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing.attributes?.publicData?.onBoardingInfo
                                        ?.contractTypesSupported
                                    )
                                      ? currentListing.attributes?.publicData?.onBoardingInfo?.contractTypesSupported?.join(
                                        ', '
                                      )
                                      : currentListing.attributes?.publicData?.onBoardingInfo
                                        ?.contractTypesSupported || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['contractTypesSupported']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['contractTypesSupported']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {/* {(!isExternalUser || visibilitySettings?.['subcontractedSubmissionsPreference']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Do you allow Subcontracted submissions?</label>
                                  <h6>{currentListing?.attributes?.publicData?.customerProfile?.subcontractedSubmissionsPreference || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['subcontractedSubmissionsPreference'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['subcontractedSubmissionsPreference'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {(!isExternalUser ||
                            visibilitySettings?.['contractModelsImpProjects']) && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>
                                      Contract models accepted for implementation projects
                                    </label>
                                    <h6>
                                      {Array.isArray(
                                        currentListing?.attributes?.publicData?.customerProfile
                                          ?.contractModelsImpProjects
                                      )
                                        ? currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects?.join(
                                          ', '
                                        )
                                        : currentListing?.attributes?.publicData?.customerProfile
                                          ?.contractModelsImpProjects || '-'}
                                    </h6>
                                    {/* <h6>{currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects || "-"}</h6> */}
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['contractModelsImpProjects']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['contractModelsImpProjects']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag selfDeclared={true} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                          {/* {(!isExternalUser || visibilitySettings?.['subContractedWorkPreference']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Do you allow work to be Subcontracted?</label>
                                  <h6>{currentListing?.attributes?.publicData?.customerProfile?.subContractedWorkPreference || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['subContractedWorkPreference'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['subContractedWorkPreference'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {/* {(!isExternalUser || visibilitySettings?.['paymentTerms']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>What are your expected payment terms?</label>
                                  <h6>{currentListing?.attributes?.publicData?.customerProfile?.paymentTerms || "-"}</h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag color={visibilitySettings?.['paymentTerms'] ? 'green' : 'default'}>
                                      {visibilitySettings?.['paymentTerms'] ? 'Public' : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )} */}
                          {(!isExternalUser ||
                            visibilitySettings?.['securityAndComplianceStandards']) && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>Security and compliance standards</label>
                                    <h6>
                                      {Array.isArray(
                                        currentListing?.attributes?.publicData
                                          ?.securityAndComplianceStandards
                                      )
                                        ? currentListing?.attributes?.publicData?.securityAndComplianceStandards?.join(
                                          ', '
                                        )
                                        : currentListing?.attributes?.publicData
                                          ?.securityAndComplianceStandards || '-'}
                                    </h6>
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['securityAndComplianceStandards']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['securityAndComplianceStandards']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag selfDeclared={true} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                          {(!isExternalUser || visibilitySettings?.['procurementSystemUsed']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>
                                    Which procurement or supplier onboarding system does your company
                                    use?
                                  </label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.customerProfile
                                        ?.procurementSystemUsed
                                    )
                                      ? currentListing?.attributes?.publicData?.customerProfile?.procurementSystemUsed?.join(
                                        ', '
                                      )
                                      : currentListing?.attributes?.publicData?.customerProfile
                                        ?.procurementSystemUsed || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['procurementSystemUsed']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['procurementSystemUsed']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser || visibilitySettings?.['projectBudgetRange']) && (
                            <Col xs={24} xl={8}>
                              <div className={css.formRow}>
                                <div>
                                  <label>Estimated Budget Range </label>
                                  <h6>
                                    {Array.isArray(
                                      currentListing?.attributes?.publicData?.customerProfile
                                        ?.projectBudgetRange
                                    )
                                      ? currentListing?.attributes?.publicData?.customerProfile?.projectBudgetRange
                                        ?.map(range => formatBudgetRange(range))
                                        ?.join(', ')
                                      : formatBudgetRange(
                                        currentListing?.attributes?.publicData?.customerProfile
                                          ?.projectBudgetRange
                                      ) || '-'}
                                  </h6>
                                </div>
                                {!isExternalUser && (
                                  <div className={css.formactions}>
                                    <Tag
                                      color={
                                        visibilitySettings?.['projectBudgetRange']
                                          ? 'green'
                                          : 'default'
                                      }
                                    >
                                      {visibilitySettings?.['projectBudgetRange']
                                        ? 'Public'
                                        : 'Private'}
                                    </Tag>
                                    <VerificationTag selfDeclared={true} />
                                  </div>
                                )}
                              </div>
                            </Col>
                          )}
                          {(!isExternalUser ||
                            visibilitySettings?.['expectedContractDuration']) && (
                              <Col xs={24} xl={8}>
                                <div className={css.formRow}>
                                  <div>
                                    <label>Expected Contract Duration </label>
                                    <h6>
                                      {Array.isArray(
                                        currentListing?.attributes?.publicData?.customerProfile
                                          ?.expectedContractDuration
                                      )
                                        ? currentListing?.attributes?.publicData?.customerProfile?.expectedContractDuration
                                          ?.map(duration => formatContractDuration(duration))
                                          ?.join(', ')
                                        : formatContractDuration(
                                          currentListing?.attributes?.publicData?.customerProfile
                                            ?.expectedContractDuration
                                        ) || '-'}
                                    </h6>
                                  </div>
                                  {!isExternalUser && (
                                    <div className={css.formactions}>
                                      <Tag
                                        color={
                                          visibilitySettings?.['expectedContractDuration']
                                            ? 'green'
                                            : 'default'
                                        }
                                      >
                                        {visibilitySettings?.['expectedContractDuration']
                                          ? 'Public'
                                          : 'Private'}
                                      </Tag>
                                      <VerificationTag selfDeclared={true} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                            )}
                        </Row>

                        <Tabs
                          defaultActiveKey="1"
                          type="card"
                          size="large"
                          className={css.profileCardTabs}
                          items={[
                            {
                              label: 'Staffing Services Summary',
                              key: '1',
                              icon: <TeamOutlined />,
                              children: (
                                <>
                                  {currentListing?.attributes?.publicData?.customerProfile?.customerType?.includes(
                                    'Staffing'
                                  ) && (
                                      <div className={css.subSection}>
                                        <h4 className={css.profileCardTitle}>
                                          Staffing Services Summary
                                        </h4>
                                        <div className={css.profileCardContentFull}>
                                          <Row gutter={[16, 16]}>
                                            {(!isExternalUser || visibilitySettings?.['staffingRecentInfo']) && (
                                              <Col xs={24}>
                                                <div className={css.formRow}>
                                                  <div>
                                                    <label>Recent Staffing And Hiring Details</label>
                                                    <h6 style={{ textAlign: 'left' }}>
                                                      {currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffingRecentInfo || '-'}
                                                    </h6>
                                                  </div>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['contractModelsForStaffing']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Contract Models For Staffing</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.contractModelsForStaffing
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.contractModelsForStaffing?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.contractModelsForStaffing || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {/* {visibilitySettings?.['subcontractedSubmissionsPreference'] && (
                                          <Col xs={24} xl={8}>
                                            <div className={css.formRow}>
                                              <label>Subcontracted Submissions Preference</label>
                                              <h6>{currentListing?.attributes?.publicData?.customerProfile?.subcontractedSubmissionsPreference || "-"}</h6>
                                            </div>
                                          </Col>
                                        )} */}
                                            {(!isExternalUser || visibilitySettings?.['staffingPaymentTerms']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Staffing Payment Terms</label>
                                                  <h6>
                                                    {currentListing?.attributes?.publicData
                                                      ?.customerProfile?.staffingPaymentTerms || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.[
                                              'staffingProcurementSystemUsed'
                                            ]) && (
                                                <Col xs={24} xl={8}>
                                                  <div className={css.formRow}>
                                                    <label>Staffing Procurement System Used</label>
                                                    <h6>
                                                      {Array.isArray(
                                                        currentListing?.attributes?.publicData
                                                          ?.customerProfile
                                                          ?.staffingProcurementSystemUsed
                                                      )
                                                        ? currentListing?.attributes?.publicData?.customerProfile?.staffingProcurementSystemUsed?.join(
                                                          ', '
                                                        )
                                                        : currentListing?.attributes?.publicData
                                                          ?.customerProfile
                                                          ?.staffingProcurementSystemUsed || '-'}
                                                    </h6>
                                                  </div>
                                                </Col>
                                              )}
                                            {(!isExternalUser || visibilitySettings?.['staffRoleNeeds']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Staffing Role Needs</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffRoleNeeds
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.staffRoleNeeds?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffRoleNeeds || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['staffingServiceDomains']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Staffing Service Domains</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffingServiceDomains
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.staffingServiceDomains?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffingServiceDomains ||
                                                      '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['staffingTechFocSpec']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>
                                                    Staffing Technology Focus/Specializations
                                                  </label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffingTechFocSpec
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.staffingTechFocSpec?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile?.staffingTechFocSpec ||
                                                      '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                          </Row>

                                          <div className={css.accordianDiv}>
                                            <Collapse accordion expandIconPosition="left">
                                              {/* {(!isExternalUser || visibilitySettings?.['keyManagementPersonnel']) && (
                                            <Panel header="Key Management Personnel" key="1">
                                              <div className={css.profileLists}>
                                                {Array.isArray(currentListing?.attributes?.publicData?.keyManagementPersonnel) && currentListing?.attributes?.publicData?.keyManagementPersonnel?.map((item, index) => {
                                                  return (
                                                    <div className={css.profileListBox} key={index}>
                                                      <List size="small" bordered={false}>
                                                        <List.Item><UserOutlined style={{ fontSize: 18, marginRight: 5, color: "#d94452" }} /> {item?.name || "-"}</List.Item>
                                                        <List.Item><ContactsOutlined style={{ fontSize: 18, marginRight: 5, color: "#f5b945" }} /> {item?.role || "-"}</List.Item>
                                                        <List.Item><MailOutlined style={{ fontSize: 18, marginRight: 5, color: "#5e9cea" }} /> {item?.email || "-"}</List.Item>
                                                        <List.Item><MobileOutlined style={{ fontSize: 18, marginRight: 5, color: "#9579da" }} /> {item?.phoneNumber || "-"}</List.Item>
                                                      </List>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </Panel>
                                          )} */}
                                              {/* {(!isExternalUser || visibilitySettings?.['keyProcurementContacts']) && (
                                            <Panel header="Key Procurement Contacts" key="2">
                                              <div className={css.profileLists}>
                                                {Array.isArray(currentListing?.attributes?.publicData?.keyProcurementContacts) && currentListing?.attributes?.publicData?.keyProcurementContacts?.map((item, index) => {
                                                  return (
                                                    <div className={css.profileListBox} key={index}>
                                                      <List size="small" bordered={false}>
                                                        <List.Item><UserOutlined style={{ fontSize: 18, marginRight: 5, color: "#d94452" }} /> {item?.name || "-"}</List.Item>
                                                        <List.Item><ContactsOutlined style={{ fontSize: 18, marginRight: 5, color: "#f5b945" }} /> {item?.title || "-"}</List.Item>
                                                        <List.Item><MailOutlined style={{ fontSize: 18, marginRight: 5, color: "#5e9cea" }} /> {item?.email || "-"}</List.Item>
                                                        <List.Item><MobileOutlined style={{ fontSize: 18, marginRight: 5, color: "#9579da" }} /> {item?.phoneNumber || "-"}</List.Item>
                                                      </List>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            </Panel>
                                          )} */}
                                              {(!isExternalUser ||
                                                visibilitySettings?.['talentAcquisitionContact']) && (
                                                  <Panel header="Talent Acquisition Contacts" key="3">
                                                    <div className={css.profileLists}>
                                                      {Array.isArray(
                                                        currentListing?.attributes?.publicData
                                                          ?.talentAcquisitionContact
                                                      ) &&
                                                        currentListing?.attributes?.publicData?.talentAcquisitionContact?.map(
                                                          (item, index) => {
                                                            return (
                                                              <div
                                                                className={css.profileListBox}
                                                                key={index}
                                                              >
                                                                <List size="small" bordered={false}>
                                                                  <List.Item>
                                                                    <UserOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#d94452',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.name || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <ContactsOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#f5b945',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.title || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <MailOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#5e9cea',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.email || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <MobileOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#9579da',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.phoneNumber || '-'}
                                                                  </List.Item>
                                                                </List>
                                                              </div>
                                                            );
                                                          }
                                                        )}
                                                    </div>
                                                  </Panel>
                                                )}
                                              {(!isExternalUser ||
                                                visibilitySettings?.['hiringManagers']) && (
                                                  <Panel header="Hiring Managers" key="4">
                                                    <div className={css.profileLists}>
                                                      {Array.isArray(
                                                        currentListing?.attributes?.publicData
                                                          ?.hiringManagers
                                                      ) &&
                                                        currentListing?.attributes?.publicData?.hiringManagers?.map(
                                                          (item, index) => {
                                                            return (
                                                              <div
                                                                className={css.profileListBox}
                                                                key={index}
                                                              >
                                                                <List size="small" bordered={false}>
                                                                  <List.Item>
                                                                    <UserOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#d94452',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.name || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <AuditOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#35ba9b',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.department || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <ContactsOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#f5b945',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.role || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <MailOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#5e9cea',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.email || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <MobileOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#9579da',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.phoneNumber || '-'}
                                                                  </List.Item>
                                                                </List>
                                                              </div>
                                                            );
                                                          }
                                                        )}
                                                    </div>
                                                  </Panel>
                                                )}
                                              {showsPreferredList('staffingConsultants') && (
                                                  <Panel
                                                    header="Preferred Staffing Consultants"
                                                    key="5"
                                                  >
                                                    {linkPageFailure('staffingConsultants')}
                                                    <div className={css.profileLists}>
                                                      {linkItems('staffingConsultants').map(
                                                        (item, index) => {
                                                          let candidate = null;
                                                          if (
                                                            (item.consultantId || item?.listingId) &&
                                                            candidatesListings?.length
                                                          ) {
                                                            candidate = candidatesListings.find(
                                                              x => x.id.uuid == item.listingId
                                                            );
                                                          }

                                                          // Get initials for avatar
                                                          const getInitials = name => {
                                                            if (!name) return 'N/A';
                                                            return name
                                                              .split(' ')
                                                              .map(word => word[0])
                                                              .join('')
                                                              .toUpperCase()
                                                              .substring(0, 2);
                                                          };

                                                          if (item?.status == 'requested' && item?.listingId) {
                                                            return null;
                                                          }
                                                          // Card for existing candidate (has joined iParley)
                                                          if (candidate) {
                                                            const candidateName =
                                                              candidate.attributes?.title ||
                                                              item?.label ||
                                                              'N/A';
                                                            const candidateHeading =
                                                              candidate.attributes?.publicData
                                                                ?.onBoardingInfo?.candidateHeading ||
                                                              '';
                                                            const candidateCity =
                                                              candidate.attributes?.publicData
                                                                ?.cityAddress || '';
                                                            const candidateImage =
                                                              candidate.images?.[0]?.attributes
                                                                ?.variants?.['square-small']?.url ||
                                                              Profile1;
                                                            const profileSlug =
                                                              item?.listingName ||
                                                              createSlug(candidateName);

                                                            return (
                                                              <div
                                                                className={css.profileListBox}
                                                                key={index}
                                                              >
                                                                <div
                                                                  style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start',
                                                                    gap: 16,
                                                                    width: '100%',
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      display: 'flex',
                                                                      alignItems: 'flex-start',
                                                                      gap: 16,
                                                                      flex: 1,
                                                                    }}
                                                                  >
                                                                    <Avatar
                                                                      size={46}
                                                                      src={candidateImage}
                                                                      style={
                                                                        !candidateImage
                                                                          ? {
                                                                            backgroundColor:
                                                                              '#0e9941',
                                                                            fontSize: '20px',
                                                                            fontWeight: 600,
                                                                          }
                                                                          : {}
                                                                      }
                                                                    >
                                                                      {!candidateImage &&
                                                                        getInitials(candidateName)}
                                                                    </Avatar>
                                                                    <div style={{ flex: 1 }}>
                                                                      <Text
                                                                        strong
                                                                        style={{ fontSize: 16 }}
                                                                      >
                                                                        {candidateName}
                                                                      </Text>
                                                                      <br />
                                                                      {candidateHeading && (
                                                                        <>
                                                                          <Text type="secondary">
                                                                            {candidateHeading}
                                                                          </Text>
                                                                          <br />
                                                                        </>
                                                                      )}
                                                                      {candidateCity && (
                                                                        <div
                                                                          style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 4,
                                                                            width: '100%',
                                                                          }}
                                                                        >
                                                                          <EnvironmentOutlined
                                                                            style={{
                                                                              color:
                                                                                'var(--marketplaceColor)',
                                                                            }}
                                                                          />
                                                                          <Text
                                                                            type="secondary"
                                                                            style={{ fontSize: 13 }}
                                                                          >
                                                                            {candidateCity}
                                                                          </Text>
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                  <Button
                                                                    size="middle"
                                                                    onClick={() => {
                                                                      const profilePath = createResourceLocatorString(
                                                                        'ListingPage',
                                                                        routeConfiguration,
                                                                        {
                                                                          id: item?.listingId,
                                                                          slug: profileSlug,
                                                                        },
                                                                        {}
                                                                      );
                                                                      history.push(profilePath);
                                                                    }}
                                                                  >
                                                                    View Profile
                                                                  </Button>
                                                                </div>
                                                                {item?.description && (
                                                                  <div
                                                                    style={{
                                                                      borderTop: '1px solid #f0f0f0',
                                                                      marginTop: 16,
                                                                      paddingTop: 12,
                                                                    }}
                                                                  >
                                                                    <Text>{item.description}</Text>
                                                                  </div>
                                                                )}
                                                              </div>
                                                            );
                                                          }

                                                          // Card for non-existing candidate (yet to join iParley)
                                                          const candidateName = item?.label || 'N/A';

                                                          return (
                                                            <div
                                                              className={css.profileListBox}
                                                              key={index}
                                                            >
                                                              <div
                                                                style={{
                                                                  display: 'flex',
                                                                  alignItems: 'flex-start',
                                                                  gap: 16,
                                                                  width: '100%',
                                                                }}
                                                              >
                                                                <Avatar
                                                                  size={46}
                                                                  style={{
                                                                    backgroundColor: '#faad14',
                                                                    fontSize: '20px',
                                                                    fontWeight: 600,
                                                                  }}
                                                                >
                                                                  {getInitials(candidateName)}
                                                                </Avatar>
                                                                <div>
                                                                  <Text strong style={{ fontSize: 16 }}>
                                                                    {candidateName}
                                                                  </Text>
                                                                  <br />
                                                                  <div
                                                                    style={{
                                                                      display: 'flex',
                                                                      alignItems: 'center',
                                                                      gap: 4,
                                                                      width: '100%',
                                                                    }}
                                                                  >
                                                                    <InfoCircleOutlined
                                                                      style={{ color: '#faad14' }}
                                                                    />
                                                                    <Text
                                                                      type="secondary"
                                                                      style={{
                                                                        color: '#faad14',
                                                                        fontSize: 13,
                                                                      }}
                                                                    >
                                                                      Yet to join iParley
                                                                    </Text>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                              {item?.description && (
                                                                <div
                                                                  style={{
                                                                    borderTop: '1px solid #f0f0f0',
                                                                    marginTop: 16,
                                                                    paddingTop: 12,
                                                                  }}
                                                                >
                                                                  <Text>{item.description}</Text>
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                    {linkPager('staffingConsultants')}
                                                  </Panel>
                                                )}
                                              {showsPreferredList('staffingVendorships') && (
                                                  <Panel header="Preferred Staffing Suppliers" key="6">
                                                    {linkPageFailure('staffingVendors')}
                                                    <div className={css.profileLists}>
                                                      {linkItems('staffingVendors').map(
                                                          (item, index) => (
                                                            <div
                                                              className={css.profileListBox}
                                                              key={index}
                                                            >
                                                              <h6>
                                                                {item?.label}{' '}
                                                                {item?.listingId ? (
                                                                  <a
                                                                    href={`/l/${item?.listingName}/${item?.listingId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                  >
                                                                    <ExportOutlined />
                                                                  </a>
                                                                ) : null}
                                                              </h6>
                                                              <p>Description: {item?.description}</p>
                                                            </div>
                                                          )
                                                        )}
                                                    </div>
                                                    {linkPager('staffingVendors')}
                                                  </Panel>
                                                )}
                                            </Collapse>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                </>
                              ),
                            },
                            {
                              label: 'Implementation Services Summary',
                              key: '2',
                              icon: <ToolOutlined />,
                              children: (
                                <>
                                  {currentListing?.attributes?.publicData?.customerProfile?.customerType?.includes(
                                    'Implementation'
                                  ) && (
                                      <div className={css.subSection}>
                                        <h4 className={css.profileCardTitle}>
                                          Implementation Services Summary
                                        </h4>
                                        <div className={css.profileCardContentFull}>
                                          <Row gutter={[16, 16]}>
                                            {(!isExternalUser || visibilitySettings?.['implementationRecentInfo']) && (
                                              <Col xs={24}>
                                                <div className={css.formRow}>
                                                  <div>
                                                    <label>
                                                      Recent Project Implementation Details
                                                    </label>
                                                    <h6 style={{ textAlign: 'left' }}>
                                                      {currentListing?.attributes?.publicData
                                                        ?.customerProfile?.implementationRecentInfo ||
                                                        '-'}
                                                    </h6>
                                                  </div>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['contractModelsImpProjects']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>
                                                    Contract Models For Implementation Projects
                                                  </label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.contractModelsImpProjects
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.contractModelsImpProjects || '-'}
                                                  </h6>
                                                  {/* <h6>{currentListing?.attributes?.publicData?.customerProfile?.contractModelsImpProjects || "-"}</h6> */}
                                                </div>
                                              </Col>
                                            )}
                                            {/* {visibilitySettings?.['subContractedWorkPreference'] && (
                                          <Col xs={24} xl={8}>
                                            <div className={css.formRow}>
                                              <label>Subcontracted Work Preference</label>
                                              <h6>{currentListing?.attributes?.publicData?.customerProfile?.subContractedWorkPreference || "-"}</h6>
                                            </div>
                                          </Col>
                                        )} */}
                                            {(!isExternalUser || visibilitySettings?.['implementationPaymentTerms']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Implementation Payment Terms</label>
                                                  <h6>
                                                    {currentListing?.attributes?.publicData
                                                      ?.customerProfile?.implementationPaymentTerms ||
                                                      '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.[
                                              'implementationProcurementSystemUsed'
                                            ]) && (
                                                <Col xs={24} xl={8}>
                                                  <div className={css.formRow}>
                                                    <label>
                                                      Implementation Procurement System Used
                                                    </label>
                                                    <h6>
                                                      {Array.isArray(
                                                        currentListing?.attributes?.publicData
                                                          ?.customerProfile
                                                          ?.implementationProcurementSystemUsed
                                                      )
                                                        ? currentListing?.attributes?.publicData?.customerProfile?.implementationProcurementSystemUsed?.join(
                                                          ', '
                                                        )
                                                        : currentListing?.attributes?.publicData
                                                          ?.customerProfile
                                                          ?.implementationProcurementSystemUsed ||
                                                        '-'}
                                                    </h6>
                                                  </div>
                                                </Col>
                                              )}
                                            {(!isExternalUser || visibilitySettings?.['projectBudgetRange']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Project Budget Range</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.projectBudgetRange
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.projectBudgetRange
                                                        ?.map(range => formatBudgetRange(range))
                                                        ?.join(', ')
                                                      : formatBudgetRange(
                                                        currentListing?.attributes?.publicData
                                                          ?.customerProfile?.projectBudgetRange
                                                      ) || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['expectedContractDuration']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Expected Contract Duration</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.expectedContractDuration
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.expectedContractDuration
                                                        ?.map(duration =>
                                                          formatContractDuration(duration)
                                                        )
                                                        ?.join(', ')
                                                      : formatContractDuration(
                                                        currentListing?.attributes?.publicData
                                                          ?.customerProfile
                                                          ?.expectedContractDuration
                                                      ) || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['implementationServiceDomains']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Implementation Service Domains</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.implementationServiceDomains
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.implementationServiceDomains?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.implementationServiceDomains || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['implementationTechFocSpec']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>
                                                    Implementation Technology Focus/Specializations
                                                  </label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.implementationTechFocSpec
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.implementationTechFocSpec?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.implementationTechFocSpec || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                            {(!isExternalUser || visibilitySettings?.['implementationRoleNeeds']) && (
                                              <Col xs={24} xl={8}>
                                                <div className={css.formRow}>
                                                  <label>Implementation Role Needs</label>
                                                  <h6>
                                                    {Array.isArray(
                                                      currentListing?.attributes?.publicData
                                                        ?.customerProfile?.implementationRoleNeeds
                                                    )
                                                      ? currentListing?.attributes?.publicData?.customerProfile?.implementationRoleNeeds?.join(
                                                        ', '
                                                      )
                                                      : currentListing?.attributes?.publicData
                                                        ?.customerProfile
                                                        ?.implementationRoleNeeds || '-'}
                                                  </h6>
                                                </div>
                                              </Col>
                                            )}
                                          </Row>

                                          <div className={css.accordianDiv}>
                                            <Collapse accordion expandIconPosition="left">
                                              {(!isExternalUser ||
                                                visibilitySettings?.['technicalStakeholders']) && (
                                                  <Panel header="Technical Stakeholders" key="1">
                                                    <div className={css.profileLists}>
                                                      {Array.isArray(
                                                        currentListing.attributes?.publicData
                                                          ?.technicalStakeholders
                                                      ) &&
                                                        currentListing.attributes?.publicData?.technicalStakeholders?.map(
                                                          (item, index) => {
                                                            return (
                                                              <div
                                                                key={index}
                                                                className={css.profileListBox}
                                                              >
                                                                <List size="small" bordered={false}>
                                                                  <List.Item>
                                                                    <UserOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#d94452',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.name || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <IdcardOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#f5b945',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.title || '-'}
                                                                  </List.Item>
                                                                  <List.Item>
                                                                    <ContactsOutlined
                                                                      style={{
                                                                        fontSize: 18,
                                                                        marginRight: 5,
                                                                        color: '#35ba9b',
                                                                      }}
                                                                    />{' '}
                                                                    {item?.Role || '-'}
                                                                  </List.Item>
                                                                </List>
                                                              </div>
                                                            );
                                                          }
                                                        )}
                                                    </div>
                                                  </Panel>
                                                )}
                                              {(!isExternalUser ||
                                                visibilitySettings?.[
                                                'implementationConsultants'
                                                ]) && (
                                                  <Panel
                                                    header="Preferred Implementation Consultants"
                                                    key="2"
                                                  >
                                                    {linkPageFailure('implementationConsultants')}
                                                    <div className={css.profileLists}>
                                                      {linkItems('implementationConsultants').map(
                                                        (item, index) => {
                                                          let candidate = null;
                                                          if (
                                                            (item.consultantId || item?.listingId) &&
                                                            candidatesListings?.length
                                                          ) {
                                                            candidate = candidatesListings.find(
                                                              x => x.id.uuid == item.listingId
                                                            );
                                                          }

                                                          // Get initials for avatar
                                                          const getInitials = name => {
                                                            if (!name) return 'N/A';
                                                            return name
                                                              .split(' ')
                                                              .map(word => word[0])
                                                              .join('')
                                                              .toUpperCase()
                                                              .substring(0, 2);
                                                          };

                                                          if (item?.status == 'requested' && item?.listingId) {
                                                            return null;
                                                          }
                                                          // Card for existing candidate (has joined iParley)
                                                          if (candidate) {
                                                            const candidateName =
                                                              candidate.attributes?.title ||
                                                              item?.label ||
                                                              'N/A';
                                                            const candidateHeading =
                                                              candidate.attributes?.publicData
                                                                ?.onBoardingInfo?.candidateHeading ||
                                                              '';
                                                            const candidateCity =
                                                              candidate.attributes?.publicData
                                                                ?.cityAddress || '';
                                                            const candidateImage =
                                                              candidate.images?.[0]?.attributes
                                                                ?.variants?.['square-small']?.url ||
                                                              Profile1;
                                                            const profileSlug =
                                                              item?.listingName ||
                                                              createSlug(candidateName);

                                                            return (
                                                              <div
                                                                className={css.profileListBox}
                                                                key={index}
                                                              >
                                                                <div
                                                                  style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'flex-start',
                                                                    gap: 16,
                                                                    width: '100%',
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      display: 'flex',
                                                                      alignItems: 'flex-start',
                                                                      gap: 16,
                                                                      flex: 1,
                                                                    }}
                                                                  >
                                                                    <Avatar
                                                                      size={46}
                                                                      src={candidateImage}
                                                                      style={
                                                                        !candidateImage
                                                                          ? {
                                                                            backgroundColor:
                                                                              '#0e9941',
                                                                            fontSize: '20px',
                                                                            fontWeight: 600,
                                                                          }
                                                                          : {}
                                                                      }
                                                                    >
                                                                      {!candidateImage &&
                                                                        getInitials(candidateName)}
                                                                    </Avatar>
                                                                    <div style={{ flex: 1 }}>
                                                                      <Text
                                                                        strong
                                                                        style={{ fontSize: 16 }}
                                                                      >
                                                                        {candidateName}
                                                                      </Text>
                                                                      <br />
                                                                      {candidateHeading && (
                                                                        <>
                                                                          <Text type="secondary">
                                                                            {candidateHeading}
                                                                          </Text>
                                                                          <br />
                                                                        </>
                                                                      )}
                                                                      {candidateCity && (
                                                                        <div
                                                                          style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 4,
                                                                            width: '100%',
                                                                          }}
                                                                        >
                                                                          <EnvironmentOutlined
                                                                            style={{
                                                                              color:
                                                                                'var(--marketplaceColor)',
                                                                            }}
                                                                          />
                                                                          <Text
                                                                            type="secondary"
                                                                            style={{ fontSize: 13 }}
                                                                          >
                                                                            {candidateCity}
                                                                          </Text>
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                  <Button
                                                                    size="middle"
                                                                    onClick={() => {
                                                                      const profilePath = createResourceLocatorString(
                                                                        'ListingPage',
                                                                        routeConfiguration,
                                                                        {
                                                                          id: item?.listingId,
                                                                          slug: profileSlug,
                                                                        },
                                                                        {}
                                                                      );
                                                                      history.push(profilePath);
                                                                    }}
                                                                  >
                                                                    View Profile
                                                                  </Button>
                                                                </div>
                                                                {item?.description && (
                                                                  <div
                                                                    style={{
                                                                      borderTop: '1px solid #f0f0f0',
                                                                      marginTop: 16,
                                                                      paddingTop: 12,
                                                                    }}
                                                                  >
                                                                    <Text>{item.description}</Text>
                                                                  </div>
                                                                )}
                                                              </div>
                                                            );
                                                          }

                                                          // Card for non-existing candidate (yet to join iParley)
                                                          const candidateName = item?.label || 'N/A';

                                                          return (
                                                            <div
                                                              className={css.profileListBox}
                                                              key={index}
                                                            >
                                                              <div
                                                                style={{
                                                                  display: 'flex',
                                                                  alignItems: 'flex-start',
                                                                  gap: 16,
                                                                  width: '100%',
                                                                }}
                                                              >
                                                                <Avatar
                                                                  size={46}
                                                                  style={{
                                                                    backgroundColor: '#faad14',
                                                                    fontSize: '20px',
                                                                    fontWeight: 600,
                                                                  }}
                                                                >
                                                                  {getInitials(candidateName)}
                                                                </Avatar>
                                                                <div>
                                                                  <Text strong style={{ fontSize: 16 }}>
                                                                    {candidateName}
                                                                  </Text>
                                                                  <br />
                                                                  <div
                                                                    style={{
                                                                      display: 'flex',
                                                                      alignItems: 'center',
                                                                      gap: 4,
                                                                      width: '100%',
                                                                    }}
                                                                  >
                                                                    <InfoCircleOutlined
                                                                      style={{ color: '#faad14' }}
                                                                    />
                                                                    <Text
                                                                      type="secondary"
                                                                      style={{
                                                                        color: '#faad14',
                                                                        fontSize: 13,
                                                                      }}
                                                                    >
                                                                      Yet to join iParley
                                                                    </Text>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                              {item?.description && (
                                                                <div
                                                                  style={{
                                                                    borderTop: '1px solid #f0f0f0',
                                                                    marginTop: 16,
                                                                    paddingTop: 12,
                                                                  }}
                                                                >
                                                                  <Text>{item.description}</Text>
                                                                </div>
                                                              )}
                                                            </div>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                    {linkPager('implementationConsultants')}
                                                  </Panel>
                                                )}
                                              {(!isExternalUser ||
                                                visibilitySettings?.[
                                                'implementationVendorships'
                                                ]) && (
                                                  <Panel
                                                    header="Preferred Implementation Suppliers"
                                                    key="3"
                                                  >
                                                    {linkPageFailure('implementationVendors')}
                                                    <div className={css.profileLists}>
                                                      {linkItems('implementationVendors').map(
                                                          (item, index) => (
                                                            <div
                                                              className={css.profileListBox}
                                                              key={index}
                                                            >
                                                              <h6>
                                                                {item?.label}{' '}
                                                                {item?.listingId ? (
                                                                  <a
                                                                    href={`/l/${item?.listingName}/${item?.listingId}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                  >
                                                                    <ExportOutlined />
                                                                  </a>
                                                                ) : null}
                                                              </h6>
                                                              <p>Description: {item?.description}</p>
                                                            </div>
                                                          )
                                                        )}
                                                    </div>
                                                    {linkPager('implementationVendors')}
                                                  </Panel>
                                                )}
                                            </Collapse>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                </>
                              ),
                            },
                          ]}
                        />
                      </div>
                    </div>
                  </>
                ) : null}

                {listingIsCandidate && (
                  <>
                    <div id="Tab2" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={skillNav} alt="Skills" width={40} /> Skills
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <div className={css.profileLists}>
                          {currentListing?.attributes?.publicData?.skills ? (
                            currentListing?.attributes?.publicData?.skills?.map((item, index) => {
                              return (
                                <div className={css.profileListBox}>
                                  <h6>{item?.name}</h6>
                                  <span>
                                    {item?.skillExp} {item?.skillExp == 1 ? 'year' : 'years'}
                                  </span>{' '}
                                  <br />
                                  <Rate disabled value={item?.rating === '' ? 1 : item?.rating} />
                                </div>
                              );
                            })
                          ) : (
                            <></>
                          )}

                          {/* <div className={css.profileListBoxBlur}>
                            <div className={css.lockedCard}>
                              <Skeleton title={false} paragraph={{ rows: 2 }} />
                              <Rate disabled />
                              <div className={css.lockOverlay}>
                                <LockOutlined style={{ fontSize: 40 }} />
                                <a href="#" className={css.loginText}><strong>LOGIN</strong> TO UNLOCK</a>
                              </div>
                            </div>
                          </div> */}
                        </div>
                      </div>
                    </div>
                    <div id="Tab6" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={certificationNav} alt="Certifications & Awards" width={40} />{' '}
                          Certifications & Awards
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <List
                          itemLayout="horizontal"
                          dataSource={
                            currentListing?.attributes?.publicData?.certificationsAndAwards || []
                          }
                          renderItem={item => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<TrophyOutlined />}
                                title={
                                  item?.link ? (
                                    <a href={`${item?.link}`} target="_blank">
                                      {item.title}
                                    </a>
                                  ) : (
                                    <span>{item.title}</span>
                                  )
                                }
                                description={item?.description || ''}
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    </div>
                    <div id="Tab18" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}>
                          <img src={projectsNav} alt="Projects" width={40} /> Projects
                        </h3>
                      </div>
                      <div className={css.profileCardContent}>
                        <div className={css.profileLists}>
                          {currentListing?.attributes?.publicData?.linkedProjects &&
                            currentListing?.attributes?.publicData?.linkedProjects.length > 0 ? (
                            currentListing?.attributes?.publicData?.linkedProjects?.map(
                              (item, index) => {
                                return (
                                  <div key={index} className={css.profileListBox}>
                                    <h6>{item?.name || item?.label || item?.value}</h6>
                                    {item?.rating && (
                                      <div>
                                        <Rate disabled value={item?.rating} />
                                      </div>
                                    )}
                                    {item?.link && (
                                      <div style={{ marginTop: '4px' }}>
                                        <a
                                          href={item?.link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          View website
                                        </a>
                                      </div>
                                    )}
                                    {item?.technologiesUsed && (
                                      <div style={{ marginTop: '6px' }}>
                                        <Tag
                                          className={css.tagEllipsis}
                                          title={item?.technologiesUsed}
                                        >
                                          {item?.technologiesUsed}
                                        </Tag>
                                      </div>
                                    )}
                                    {item?.description && (
                                      <p
                                        style={{
                                          marginTop: '6px',
                                          fontSize: '12px',
                                          color: '#666',
                                        }}
                                      >
                                        {item?.description}
                                      </p>
                                    )}
                                  </div>
                                );
                              }
                            )
                          ) : (
                            <div style={{ padding: '12px', color: '#999', fontSize: '13px' }}>
                              No projects added yet.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div id="Tab15" className={css.profileSection}>
                      <div className={css.profileCardHeading}>
                        <h3 className={css.profileCardTitle}><img src={educationsNav} alt="Recommendations" width={40} /> Recommendations</h3>

                      </div>
                      <div className={css.profileCardContent}>
                        <div>
                          <Flex gap="small" wrap>
                            <Tooltip title="Recommendations">
                              <span >Recommendations </span>
                            </Tooltip>
                          </Flex>
                        </div>
                        <Timeline
                          items={recommendationsReceived.map(item => ({
                            key: item.id,
                            children: (
                              <>
                                <h4>{item.giver?.listingId ? <a href={`/l/${item.giver.listingId}`} style={{ color: 'black' }}>{item.giver.name}</a> : <span>{item.giver?.name}</span>}</h4>
                                {item.wording && <div>{item.wording}</div>}
                              </>
                            ),
                          }))}
                        />

                        <div>
                          <Flex gap="small" wrap>
                            <Tooltip title="Recommendations Given">
                              <span >Recommendations Given</span>
                            </Tooltip>
                          </Flex>
                        </div>
                        <Timeline
                          items={recommendationsGiven.map(item => ({
                            key: item.id,
                            children: (
                              <>
                                <h4>{item.recipient?.listingId ? <a href={`/l/${item.recipient.listingId}`} style={{ color: 'black' }}>{item.recipient.name}</a> : <span>{item.recipient?.name}</span>}</h4>
                                <div>{item.wording && <>Description: {item.wording}</>}</div>
                              </>
                            ),
                          }))}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              {mainUserId !== currentListing?.attributes?.publicData?.adminId &&
                (currentUserIsCustomer || enableMessageButton) && (
                  <FloatButton.Group
                    trigger="click"
                    type="primary"
                    description="Message User"
                    style={{ insetInlineEnd: 30 }}
                    icon={<CommentOutlined />}
                    onClick={handleContactUserClick}
                  />
                )}

              {/* Info Guide Block */}
              {!isExternalUser && (
                <div className={css.infoGuideBlock}>
                  <h4 className={css.infoGuideTitle}>
                    <InfoCircleOutlined />
                    Tag & Chip Guide
                  </h4>

                  <Space direction="vertical" size={8} className={css.infoGuideContent}>
                    <div className={css.infoGuideSection}>Verification Tags:</div>

                    <div className={css.infoGuideRow}>
                      <VerificationTag isVerified={true} />
                      <span>
                        Information verified through official documentation or third-party
                        verification services.
                      </span>
                    </div>

                    <div className={css.infoGuideRow}>
                      <VerificationTag isVerified={false} />
                      <span>
                        Information not yet verified, may require additional documentation.
                      </span>
                    </div>

                    <div className={css.infoGuideRow}>
                      <VerificationTag selfDeclared={true} />
                      <span>
                        Self-reported information without independent verification.
                      </span>
                    </div>

                    <Divider className={css.infoGuideDivider} />

                    <div className={css.infoGuideSection}>Visibility Chips:</div>

                    <div className={css.infoGuideRow}>
                      <VisibilityChip isPublic={true} />
                      <span>Information visible to all users who view your profile.</span>
                    </div>

                    <div className={css.infoGuideRow}>
                      <VisibilityChip isPublic={false} />
                      <span>
                        Information hidden from public view, only visible to logged in
                        iParley users.
                      </span>
                    </div>
                  </Space>
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal
          title={modalTitle}
          closable={{ 'aria-label': 'Custom Close Button' }}
          open={isModalOpen}
          //width={activeCase === 'modalResume' ? 1000 : undefined}
          onCancel={handleCancel}
          footer={null}
          styles={{ body: { maxHeight: '600px', overflowY: 'auto' } }}
          disabled={true}
        >
          {content}
        </Modal>

        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
        </div>
        {/* <Modal
          id="ListingPage.inquiry"
          contentClassName={css.inquiryModalContent}
          isOpen={isAuthenticated && inquiryModalOpen}
          onClose={() => setInquiryModalOpen(false)}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <InquiryForm
            className={css.inquiryForm}
            submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
            listingTitle={title}
            authorDisplayName={authorDisplayName}
            sendInquiryError={sendInquiryError}
            onSubmit={onSubmitInquiry}
            inProgress={sendInquiryInProgress}
          />
        </Modal> */}
        <Modal
          title={`Message ${currentListing?.attributes?.title}`}
          open={showMessageModal}
          onOk={handleMessageSend}
          onCancel={() => {
            setShowMessageModal(false);
          }}
          footer={
            <Button type="primary" onClick={handleMessageSend}>
              Send Message
            </Button>
          }
        >
          <input
            type="textarea"
            placeholder="Type your message here..."
            className="filter-input"
            style={{
              width: '100%',
              height: 100,
              padding: 8,
              borderRadius: 4,
              border: '1px solid #d9d9d9',
            }}
            onChange={e => setToSendMessage(e.target.value)}
            value={toSendMessage || ''}
            defaultValue={toSendMessage || ''}
          />
        </Modal>

        {/* Request/Share Document Modal */}
        <Modal
          title={
            isShareMode
              ? `Share Credentials: ${selectedDocumentName}`
              : `Request Document: ${selectedDocumentName}`
          }
          open={showRequestDocumentModal}
          onCancel={handleRequestDocumentCancel}
          footer={[
            // <Button key="close" onClick={handleRequestDocumentCancel}>
            //   Close
            // </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={handleRequestDocumentSubmit}
              disabled={
                !dialogCanSubmit({ fields: selectedVerifiedFields, files: tickedFiles }) ||
                !consentToRequest ||
                (!responseByMail && !responseByPhone) ||
                (isShareMode && !shareRecipientName) ||
                (isShareMode && responseByMail && !shareEmail) ||
                (isShareMode && responseByPhone && !sharePhone)
              }
            >
              {isShareMode ? 'Share Credentials' : 'Submit Request'}
            </Button>,
          ]}
          width={600}
        >
          <div className={css.requestDocumentModalContent}>
            <h4 className={css.requestDocumentModalSectionTitle}>
              {isShareMode ? 'Select Credentials to Share' : 'Select Verified Fields to Request'}
            </h4>
            <div className={css.requestVerifiedFieldsContainer}>
              {availableFields && availableFields.length > 0 ? (
                availableFields.map(field => {
                  // Handle both object format {fieldName, displayName} and string format
                  const fieldKey = typeof field === 'object' ? field.fieldName : field;
                  const fieldDisplay = typeof field === 'object' ? field.displayName : field;

                  return (
                    <div
                      key={fieldKey}
                      className={`${css.requestFieldCheckboxItem} ${css.requestFieldCheckboxItemVerified}`}
                    >
                      <input
                        type="checkbox"
                        id={`field-${fieldKey}`}
                        checked={selectedVerifiedFields.includes(fieldKey)}
                        onChange={() => handleVerifiedFieldToggle(fieldKey)}
                        className={css.requestFieldCheckbox}
                      />
                      <label htmlFor={`field-${fieldKey}`} className={css.requestFieldLabel}>
                        {fieldDisplay}
                        <img
                          src={verified}
                          alt="verified"
                          width="16"
                          className={css.requestVerifiedIcon}
                        />
                      </label>
                    </div>
                  );
                })
              ) : offeredFiles.length === 0 ? (
                <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                  No verified fields available to share.
                </p>
              ) : null}
            </div>
            <CompanyFileRows
              files={offeredFiles}
              ticked={tickedFiles}
              onToggle={kind => setTickedFiles(ticked => toggleCompanyFile(ticked, kind))}
              css={css}
            />

            {/* Show Recipient Name Field in Share mode */}
            {isShareMode && (
              <div
                className={css.shareInputContainer}
                style={{ marginTop: '24px', marginBottom: '16px' }}
              >
                <label htmlFor="share-recipient-name" className={css.shareInputLabel}>
                  Recipient Name *
                </label>
                <input
                  type="text"
                  id="share-recipient-name"
                  value={shareRecipientName}
                  onChange={e => setShareRecipientName(e.target.value)}
                  placeholder="Enter recipient's full name..."
                  className={css.shareInput}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>
            )}

            <h4 className={css.requestDocumentModalSectionTitleWithMargin}>
              {isShareMode
                ? 'How do you want to share the credentials?'
                : 'How do you want to recieve the credential presentation?'}
            </h4>
            <div className={css.requestResponseMethodContainer}>
              <div className={css.requestResponseMethodItem}>
                <input
                  type="checkbox"
                  id="response-by-mail"
                  checked={responseByMail}
                  onChange={e => {
                    setResponseByMail(e.target.checked);
                    if (!e.target.checked) setShareEmail('');
                  }}
                  className={css.requestResponseMethodCheckbox}
                />
                <label htmlFor="response-by-mail" className={css.requestResponseMethodLabel}>
                  By Email
                </label>
              </div>

              <div className={css.requestResponseMethodItem}>
                <input
                  type="checkbox"
                  id="response-by-phone"
                  checked={responseByPhone}
                  onChange={e => {
                    setResponseByPhone(e.target.checked);
                    if (!e.target.checked) setSharePhone('');
                  }}
                  className={css.requestResponseMethodCheckbox}
                />
                <label htmlFor="response-by-phone" className={css.requestResponseMethodLabel}>
                  By SMS
                </label>
                <NoSmsCreditsHint smsBalance={smsBalance} />
              </div>
            </div>

            {/* Show Email Input Field when Email is selected in Share mode */}
            {isShareMode && responseByMail && (
              <div className={css.shareInputContainer} style={{ marginTop: '16px' }}>
                <label htmlFor="share-email" className={css.shareInputLabel}>
                  Recipient Email Address *
                </label>
                <input
                  type="email"
                  id="share-email"
                  value={shareEmail}
                  onChange={e => setShareEmail(e.target.value)}
                  placeholder="Enter email address..."
                  className={css.shareInput}
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>
            )}

            {/* Show Phone Input Field when Phone is selected in Share mode */}
            {isShareMode && responseByPhone && (
              <div className={css.shareInputContainer} style={{ marginTop: '16px' }}>
                <label htmlFor="share-phone" className={css.shareInputLabel}>
                  Recipient Phone Number *
                </label>
                <SharePhoneInput
                  id="share-phone"
                  value={sharePhone}
                  onChange={setSharePhone}
                  placeholder="Enter phone number..."
                  required
                />
              </div>
            )}

            {/* Consent Section */}
            <div className={css.requestConsentContainer}>
              <div className={css.requestConsentItem}>
                <input
                  type="checkbox"
                  id="consent-to-request"
                  checked={consentToRequest}
                  onChange={e => setConsentToRequest(e.target.checked)}
                  className={css.requestConsentCheckbox}
                />
                <label htmlFor="consent-to-request" className={css.requestConsentLabel}>
                  {isShareMode
                    ? 'I agree to share the selected credentials with the recipient. I understand that the shared information will be sent via the selected method and confirm that I have the authority to share this information.'
                    : 'I agree that by requesting this credential presentation, I confirm that I have a legitimate business purpose to verify the information and will handle the received data in accordance with applicable privacy laws and regulations.'}
                </label>
              </div>
            </div>
          </div>
        </Modal>

        {/* Credential Access Request Popup - shown to profile owner when they click the email link */}
        <Modal
          title="Credential Access Request"
          open={showCredentialAccessModal}
          onCancel={handleCloseCredentialAccessModal}
          footer={credentialAccessEmailFailedId ? [
            <Button key="close" onClick={handleCloseCredentialAccessModal}>
              <FormattedMessage id="CredentialApproval.close" />
            </Button>,
          ] : CredentialRequestActions({
            isCompanyAdmin: !!isOwnListing,
            loading: credentialAccessLoading,
            shareDisabled:
              !approvalConsentChecked ||
              !approvalWouldShare({
                request: credentialAccessRequest,
                tickedFiles: credentialAccessTickedFiles,
              }),
            onDecline: handleRejectCredentialAccess,
            onShare: handleApproveCredentialAccess,
            onClose: handleCloseCredentialAccessModal,
          })}
          width={550}
          maskClosable={false}
        >
          {credentialAccessRequest && (
            <CredentialRequestDetails request={credentialAccessRequest}>
              <CredentialRequestFileChoices
                files={requestedCompanyFiles(credentialAccessRequest)}
                isCompanyAdmin={!!isOwnListing}
                ticked={credentialAccessTickedFiles}
                onChange={setCredentialAccessTickedFiles}
              />
            </CredentialRequestDetails>
          )}
          {/* Only the company admin can share, and once access is approved the consent is given. */}
          {credentialAccessRequest && !!isOwnListing && !credentialAccessEmailFailedId && (
            <ApprovalShareConsent
              checked={approvalConsentChecked}
              onChange={setApprovalConsentChecked}
              disabled={credentialAccessLoading}
              requesterLabel={approvalRequesterLabel(credentialAccessRequest)}
            />
          )}
          {credentialAccessEmailFailedId && (
            <ApprovalEmailResend
              requestId={credentialAccessEmailFailedId}
              onResend={onResendCredentialAccessEmail}
              onSent={() => {
                handleCloseCredentialAccessModal();
                message.success('Credentials shared successfully! The requester has been notified via email.');
              }}
            />
          )}
        </Modal>

        <Modal
          title={
            listingIsAgency && listingAgencyType === 'Staffing and Implementation Services'
              ? 'Invite to a Job/RFP'
              : listingIsAgency && listingAgencyType === 'Staffing Services'
                ? 'Invite to a Job'
                : listingIsAgency && listingAgencyType === 'Implementation Service'
                  ? 'Invite to an RFP'
                  : 'Invite to a Job'
          }
          open={isInviteModalOpen}
          onCancel={() => setInviteModalOpen(false)}
          footer={[
            <Button key="back" onClick={() => setInviteModalOpen(false)}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={!selectedJobForInvitation}
              loading={inviteSending}
              onClick={async () => {
                setInviteSending(true);
                try {
                  const selectedListing = jobRfpListingsOfCus?.find(
                    listing => listing.id.uuid === selectedJobForInvitation
                  );

                  const isJobPosting =
                    selectedListing?.attributes?.publicData?.listingType === 'job_listing' || false;
                  const isRFPPosting =
                    selectedListing?.attributes?.publicData?.listingType === 'rfp_listing' || false;

                  await onSendInvitation(
                    selectedJobForInvitation,
                    selectedListing?.attributes?.title,
                    toEmail,
                    toUserId,
                    invitedUserName,
                    toUserType,
                    toUserProfileListingId,
                    isJobPosting,
                    isRFPPosting
                  );

                  setInvitedTo([...invitedTo, selectedJobForInvitation]);

                  setInviteModalOpen(false);
                  setSelectedJobForInvitation(null);
                } finally {
                  setInviteSending(false);
                }
              }}
            >
              Invite
            </Button>,
          ]}
        >
          <div style={{ marginBottom: '24px' }}>
            {/* Already Invited Section */}
            {jobRfpListingsOfCus?.filter(
              listing =>
                invitedTo.includes(listing.id.uuid) ||
                listing?.attributes?.publicData?.invitedUsersToPosting?.find(
                  invited => invited?.toUserId === currentListingAdmin?.id?.uuid
                )
            ).length > 0 && (
                <div
                  style={{
                    backgroundColor: '#f6f8fa',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    border: '1px solid #e1e4e8',
                  }}
                >
                  <p
                    style={{
                      margin: '0 0 8px 0',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#57606a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Already Invited To
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#24292f',
                      lineHeight: '1.5',
                    }}
                  >
                    {jobRfpListingsOfCus
                      ?.filter(
                        listing =>
                          invitedTo.includes(listing.id.uuid) ||
                          listing?.attributes?.publicData?.invitedUsersToPosting?.find(
                            invited => invited?.toUserId === currentListingAdmin?.id?.uuid
                          )
                      )
                      .map(listing => listing.attributes.title)
                      .join(', ')}
                  </p>
                </div>
              )}

            {jobRfpListingsOfCus?.filter(listing => hasCurrentListingAdminAppliedTo(listing)).length > 0 && (
              <div
                style={{
                  backgroundColor: '#f6f8fa',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  border: '1px solid #e1e4e8',
                }}
              >
                <p
                  style={{
                    margin: '0 0 8px 0',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#57606a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Already Applied To
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#24292f',
                    lineHeight: '1.5',
                  }}
                >
                  {jobRfpListingsOfCus
                    ?.filter(listing => hasCurrentListingAdminAppliedTo(listing))
                    .map(listing => listing.attributes.title)
                    .join(', ')}
                </p>
              </div>
            )}

            {/* Info message for dual-type agencies */}
            {listingIsAgency && listingAgencyType === 'Staffing and Implementation Services' && (
              <div
                style={{
                  backgroundColor: '#fffbeb',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  border: '1px solid #fde68a',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#92400e',
                    lineHeight: '1.5',
                  }}
                >
                  You can invite this supplier to either a job or an RFP, but only one at a time.
                </p>
              </div>
            )}

            {/* Jobs Selection Section */}
            {(listingIsCandidate ||
              (listingIsAgency &&
                (listingAgencyType === 'Staffing Services' ||
                  listingAgencyType === 'Staffing and Implementation Services'))) && (
                <div
                  style={{
                    marginBottom:
                      listingIsAgency && listingAgencyType === 'Staffing and Implementation Services'
                        ? '20px'
                        : '0',
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#24292f',
                    }}
                  >
                    {listingIsAgency && listingAgencyType === 'Staffing and Implementation Services'
                      ? 'Select a job to invite this supplier to:'
                      : listingIsAgency && listingAgencyType === 'Staffing Services'
                        ? 'Select a job to invite this supplier to:'
                        : 'Select a job to invite this candidate to:'}
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={
                      selectedJobForInvitation &&
                        jobRfpListingsOfCus?.find(l => l.id.uuid === selectedJobForInvitation)?.attributes?.publicData?.listingType === 'job_listing'
                        ? selectedJobForInvitation
                        : undefined
                    }
                    placeholder="Select a Job"
                    onChange={value => setSelectedJobForInvitation(value)}
                    options={
                      jobRfpListingsOfCus
                        ?.filter(
                          listing =>
                            !invitedTo.includes(listing.id.uuid) &&
                            !listing?.attributes?.publicData?.invitedUsersToPosting?.find(
                              invited => invited?.toUserId === currentListingAdmin?.id?.uuid
                            ) &&
                            !hasCurrentListingAdminAppliedTo(listing) &&
                            listing?.attributes?.publicData?.listingType === 'job_listing' &&
                            listing?.attributes?.state !== 'closed'
                        )
                        ?.map(listing => ({
                          value: listing.id.uuid,
                          label: listing.attributes.title,
                        })) || []
                    }
                  />
                </div>
              )}

            {/* Message when agency cannot be invited to Jobs */}
            {listingIsAgency && listingAgencyType === 'Implementation Service' && (
              <div
                style={{
                  backgroundColor: '#f0f4f8',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  border: '1px solid #d0d7de',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#57606a',
                    lineHeight: '1.5',
                  }}
                >
                  This supplier is an Implementation Service provider and cannot be invited to Jobs. They can only be invited to RFPs.
                </p>
              </div>
            )}

            {/* RFPs Selection Section */}
            {listingIsAgency &&
              (listingAgencyType === 'Implementation Service' ||
                listingAgencyType === 'Staffing and Implementation Services') && (
                <div>
                  <label
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#24292f',
                    }}
                  >
                    {listingAgencyType === 'Staffing and Implementation Services'
                      ? 'Select an RFP to invite this supplier to:'
                      : 'Select an RFP to invite this supplier to:'}
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    value={
                      selectedJobForInvitation &&
                        jobRfpListingsOfCus?.find(l => l.id.uuid === selectedJobForInvitation)?.attributes?.publicData?.listingType === 'rfp_listing'
                        ? selectedJobForInvitation
                        : undefined
                    }
                    placeholder="Select an RFP"
                    onChange={value => setSelectedJobForInvitation(value)}
                    options={
                      jobRfpListingsOfCus
                        ?.filter(
                          listing =>
                            !invitedTo.includes(listing.id.uuid) &&
                            !listing?.attributes?.publicData?.invitedUsersToPosting?.find(
                              invited => invited?.toUserId === currentListingAdmin?.id?.uuid
                            ) &&
                            !hasCurrentListingAdminAppliedTo(listing) &&
                            listing?.attributes?.publicData?.listingType === 'rfp_listing' &&
                            listing?.attributes?.state !== 'closed'
                        )
                        ?.map(listing => ({
                          value: listing.id.uuid,
                          label: listing.attributes.title,
                        })) || []
                    }
                  />
                </div>
              )}

            {/* Message when agency cannot be invited to RFPs */}
            {listingIsAgency && listingAgencyType === 'Staffing Services' && (
              <div
                style={{
                  backgroundColor: '#f0f4f8',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginTop: '20px',
                  border: '1px solid #d0d7de',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#57606a',
                    lineHeight: '1.5',
                  }}
                >
                  This supplier is a Staffing Provider and cannot be invited to RFPs. They can only be invited to Jobs.
                </p>
              </div>
            )}
          </div>
        </Modal>

        {/* Share Profile OTP Verification Modal */}
        <Modal
          title="Verify Secure Verification Code"
          open={showShareOtpModal}
          onCancel={handleShareOtpCancel}
          footer={[
            <Button key="resend" onClick={handleResendShareOtp} disabled={shareOtpLoading}>
              Resend Secure Verification Code
            </Button>,
            <Button key="cancel" onClick={handleShareOtpCancel} disabled={shareOtpLoading}>
              Cancel
            </Button>,
            <Button style={{ marginTop: '10px' }}
              key="verify"
              type="primary"
              onClick={handleShareOtpSubmit}
              loading={shareOtpLoading}
              disabled={!shareOtp || shareOtp.length !== 6}
            >
              Verify Secure Verification Code
            </Button>,
          ]}
          width={500}
          centered
          destroyOnClose={true}
          maskClosable={false}
        >
          <div style={{ padding: '12px 0' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Please enter the 6-digit Secure Verification Code sent to your email to continue.
            </p>

            <label
              style={{
                display: 'block',
                fontWeight: 600,
                marginBottom: '8px',
                color: '#333',
              }}
            >
              Enter Secure Verification Code
            </label>
            <Input
              placeholder="Enter 6-digit Secure Verification Code"
              value={shareOtp}
              onChange={e => setShareOtp(e.target.value)}
              maxLength={6}
              size="large"
              disabled={shareOtpLoading}
              style={{
                fontSize: '16px',
                letterSpacing: '4px',
                textAlign: 'center',
              }}
            />

            {shareOtpError && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  borderLeft: '3px solid #dc2626',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                {shareOtpError}
              </div>
            )}

            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f0f7ff',
                borderLeft: '3px solid #0e9941',
                borderRadius: '4px',
              }}
            >
              <p
                style={{
                  fontSize: '13px',
                  color: '#666',
                  margin: 0,
                  lineHeight: '1.5',
                }}
              >
                <strong>Note:</strong> The Secure Verification Code is valid for 10 minutes. This link can only be used
                once.
              </p>
            </div>
          </div>
        </Modal>

        {/* Request Access Modal */}
        <Modal
          title={requestAccessType ==='vault' ? 'Request Credential Vault Access' : 'Request Profile Access'}
          open={showRequestAccessModal}
          onCancel={handleRequestAccessCancel}
          footer={[
            <Button
              key="cancel"
              onClick={handleRequestAccessCancel}
              disabled={requestAccessLoading}
            >
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={handleRequestAccessSubmit}
              loading={requestAccessLoading}
            >
              Submit Request
            </Button>,
          ]}
          width={550}
          centered
          destroyOnClose={true}
        >
          <div style={{ padding: '12px 0' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
              {requestAccessType === 'vault'
                ? 'Request access to view this candidate\'s verified credential vault. The profile owner will be notified and can choose to share their credentials with you.'
                : 'Request access to view the complete profile. The profile owner will be notified and can approve or deny your request.'
              }
            </p>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#333',
                  }}
                >
                  First Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Input
                  placeholder="Enter your first name"
                  value={requestAccessData.firstName}
                  onChange={e => handleRequestAccessChange('firstName', e.target.value)}
                  size="large"
                  disabled={requestAccessLoading}
                />
              </Col>

              <Col span={12}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#333',
                  }}
                >
                  Last Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Input
                  placeholder="Enter your last name"
                  value={requestAccessData.lastName}
                  onChange={e => handleRequestAccessChange('lastName', e.target.value)}
                  size="large"
                  disabled={requestAccessLoading}
                />
              </Col>

              <Col span={24}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#333',
                  }}
                >
                  Email <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={requestAccessData.email}
                  onChange={e => handleRequestAccessChange('email', e.target.value)}
                  size="large"
                  disabled={requestAccessLoading}
                />
              </Col>

              <Col span={24}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#333',
                  }}
                >
                  Company Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <Input
                  placeholder="Enter your company name"
                  value={requestAccessData.companyName}
                  onChange={e => handleRequestAccessChange('companyName', e.target.value)}
                  size="large"
                  disabled={requestAccessLoading}
                />
              </Col>

              <Col span={24}>
                <label
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    marginBottom: '6px',
                    color: '#333',
                  }}
                >
                  Message (Optional)
                </label>
                <Input.TextArea
                  placeholder="Why would you like to view this profile?"
                  value={requestAccessData.message}
                  onChange={e => handleRequestAccessChange('message', e.target.value)}
                  rows={3}
                  maxLength={500}
                  showCount
                  disabled={requestAccessLoading}
                />
              </Col>
            </Row>

            {requestAccessError && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#fef2f2',
                  borderLeft: '3px solid #dc2626',
                  borderRadius: '4px',
                  color: '#dc2626',
                  fontSize: '13px',
                }}
              >
                {requestAccessError}
              </div>
            )}

            <div
              style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f0f7ff',
                borderLeft: '3px solid #0e9941',
                borderRadius: '4px',
              }}
            >
              <p
                style={{
                  fontSize: '13px',
                  color: '#666',
                  margin: 0,
                  lineHeight: '1.5',
                }}
              >
                <strong>Privacy Notice:</strong>{' '}
                {requestAccessType === 'vault'
                  ? 'Your information will only be shared with the profile owner. No credential data is shared without their explicit approval.'
                  : 'Your information will only be shared with the profile owner for verification purposes.'
                }
              </p>
            </div>
          </div>
        </Modal>
        <LoginPopupModal
          isOpen={isLoginModalOpen}
          onClose={() => { setIsLoginModalOpen(false); setLoginModalShowRoleSelect(true); }}
          from={`${location.pathname}${location.search}${location.hash}`}
          showRoleSelect={loginModalShowRoleSelect}
          agencyOnly={loginModalAgencyOnly}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    messageTxnId,
    applyTxnId,
    applied,
    applying,
    mainUserId,
    profileListing,
    candidatesListings,
    enableMessageButton,
    jobRfpListingsOfCus,
    currentListingAdmin,
    postingForward,
    requestRecords,
    loading,
    authorEmail,
    integrityCheckStatus,
    hasConsentForJob,
    fetchJobConsentInProgress,
    fetchJobConsentError,
  } = state.ListingPage;
  const {
    currentUser,
    allApplyTransactions,
    allApplyTransactionsInProgress,
    subscriptionsOfUser,
    personaRecords,
    currentUserPersonaRecords,
    messagePacksOfUser,
    profileListing: userProfileListingFromState,
    smsBalance,
  } = state.user;

  // Use profileListing from state.user as it's properly populated
  const userProfileListing = userProfileListingFromState;

  const { acceptedConsultantsExist, listing: listingLinkPages } = state.linkedContacts;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    messageTxnId,
    applyTxnId,
    applied,
    applying,
    mainUserId,
    profileListing,
    candidatesListings,
    enableMessageButton,
    jobRfpListingsOfCus,
    currentListingAdmin,
    postingForward,
    requestRecords,
    loading,
    listingLinkPages,
    acceptedConsultantsExist,
    allApplyTransactions,
    allApplyTransactionsInProgress,
    subscriptionsOfUser,
    subscriptionPrices: state.stripe.prices,
    subscriptionError: state.stripe.subscriptionError,
    authorEmail,
    hasConsentForJob,
    fetchJobConsentInProgress,
    fetchJobConsentError,
    personaRecords,
    currentUserPersonaRecords,
    messagePacksOfUser,
    userProfileListing,
    smsBalance,
    integrityCheckStatus,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onInitiateApplyTransaction: (
    listingId,
    acceptingInvitation,
    consultantId,
    consultantListingId,
    appliedByAgency,
    identityDocuments,
    workAuthorizationDocuments,
    applicationResume,
    status,
    consentId
  ) =>
    dispatch(
      initiateApplyTransaction(
        listingId,
        acceptingInvitation,
        consultantId,
        consultantListingId,
        appliedByAgency,
        identityDocuments,
        workAuthorizationDocuments,
        applicationResume,
        status,
        null,
        null,
        consentId
      )
    ),
  onInitiateManualTransaction: (message, listingId, applying, messaging, acceptingInvitation) =>
    dispatch(
      initiateManualTransaction(message, listingId, applying, messaging, acceptingInvitation)
    ),
  onSendInvitation: (
    listingId,
    listingName,
    toEmail,
    toUserId,
    invitedUserName,
    toUserType,
    toUserProfileListingId,
    isJobPosting,
    isRFPPosting
  ) =>
    dispatch(
      sendInvitationToUser(
        listingId,
        listingName,
        toEmail,
        toUserId,
        invitedUserName,
        toUserType,
        toUserProfileListingId,
        isJobPosting,
        isRFPPosting
      )
    ),
  onRequestDocumentPresentation: data => dispatch(requestDocumentPresentation(data)),
  onSearchAcceptedConsultants: q => dispatch(searchAcceptedConsultants(q)),
  onFetchListingLinkPage: params => dispatch(fetchListingLinkPage(params)),
  onFetchAllApplyTxns: () => dispatch(getAllApplyTransactions()),
  onRequestShareOtp: token => dispatch(requestShareOtpDuck(token)),
  onVerifyShareOtp: (token, otp) => dispatch(verifyShareOtpDuck(token, otp)),
  onGetSharedProfile: (token, options) => dispatch(getSharedProfileDuck(token, options)),
  onFetchShareVisitorPersonaRecords: (listingId, shareVisit) =>
    dispatch(fetchShareVisitorPersonaRecords(listingId, shareVisit)),
  onPayForRFP: (priceId, quantity, type, rfpPageLink) =>
    dispatch(payForRFPDuck(priceId, quantity, type, rfpPageLink)),
  getSubscriptionPrices: () => dispatch(handleGetSubscriptionPrice()),
  onSaveRFPPayment: sessionId => dispatch(saveRFPPayments(sessionId)),
  onRequestProfileAccess: requestData => dispatch(requestProfileAccessDuck(requestData)),
  onShareCredential: shareData => dispatch(shareCredentialDuck(shareData)),
  onRequestCredentialAccess: requestData => dispatch(requestCredentialAccessDuck(requestData)),
  onGetCredentialAccessRequests: params => dispatch(getCredentialAccessRequestsDuck(params)),
  onApproveCredentialAccess: (requestId, files, consentId) =>
    dispatch(approveCredentialAccessDuck(requestId, files, consentId)),
  onRejectCredentialAccess: requestId => dispatch(rejectCredentialAccessDuck(requestId)),
  onResendCredentialAccessEmail: requestId => dispatch(resendCredentialAccessEmailDuck(requestId)),
  onFetchSmsBalance: () => dispatch(fetchSmsBalance()),
  fetchMessagePacksOfUser: () => dispatch(getMessagePacksOfUser()),
  fetchProfileListing: () => dispatch(getUserProfileListing()),
  onUploadBlobFile: data => dispatch(uploadBlobFile(data)),
  onUploadResumeBlobFile: data => dispatch(uploadResumeBlobFile(data)),
  onFetchJobConsent: (consentType, jobListingId) => dispatch(fetchJobConsent(consentType, jobListingId)),
  onUpdateTransactionDocuments: (transactionId, metadata, options) =>
    dispatch(updateTransactionDocuments(transactionId, metadata, options)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
